import React, { useState, useEffect, useRef, useMemo, memo, useCallback } from 'react';
import { Volume2, Play, Pause, SkipBack, SkipForward, Clock, Car, TrendingUp, Radio, X, Cloud, Calendar, Newspaper, Plus, Send, Mic, DollarSign, RefreshCw, ChevronDown, MapPin, Zap, Settings, CheckCircle, Lightbulb, FileText, Music, Trash2, Bookmark } from 'lucide-react';
import { generateCustomInnerLoopPodcast } from './customPodcastHandler';
import { getStoredUserName } from './Azurechatbot';
import PodcastFirestoreService from './services/podcastFirestoreService';
  import { getFunctions, httpsCallable } from 'firebase/functions';
import { serverTimestamp, setDoc, updateDoc } from 'firebase/firestore';
import { StockCard, SkeletonStockCard } from './StockCard';
import GreetingCard from './GreetingCard';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, getDoc, onSnapshot} from 'firebase/firestore';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { 
  extractYouTubeId, 
  containsYouTubeUrl, 
  getYouTubeVideoAnalysis,
  generatePodcastContext,
  extractAndRemoveVideoUrl
} from './youtubeGeneration';
import SwipeableNewsCards from './SwipeableNewsCards';
import { collection, getDocs } from 'firebase/firestore';
import { getTrafficData, formatTrafficForPodcast, getSavedRoutes, saveRoute, getCurrentLocation, geocodeAddress } from './trafficHandler';
import DailyBriefCatchUp from './DailyCatchUp';
import OnboardingModal from './InnerloopOnboardingModal';
import DailyBriefTranscriptView from './DailyBriefTranscriptView';
import StockStation, { StockCardWithStation } from './StockRadioStation';
import SavedDailyBriefsSection from './InnerloopPodcastSavedDailyBriefsSection';
import LocalNewsCard from './YourLocalNewsCard';
import CompaniesLiveStation from './CompaniesLiveStation';
import SelectedNewsStation from './LiveNewsStation';
import { useLocation, useNavigate } from 'react-router-dom';
import { getDefaultSourceConfig, refreshNewsViaBackendSimple } from './backendAPI';
import { SecureAPI } from '../config.js';
import { 
  AZURE_OPENAI_ENDPOINT, 
  AZURE_OPENAI_API_KEY as AZURE_OPENAI_KEY, 
  AZURE_OPENAI_DEPLOYMENT as AZURE_DEPLOYMENT_NAME 
} from '../azureConfigHelper';
// ====================
// AZURE CONFIGURATION
// ====================


const loadPDFJS = async () => {
  if (window.pdfjsLib) return;
  
  const script = document.createElement('script');
  script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
  script.async = true;
  
  return new Promise((resolve, reject) => {
    script.onload = () => {
      if (window.pdfjsLib) {
        window.pdfjsLib.GlobalWorkerOptions.workerSrc = 
          'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
        resolve();
      }
    };
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

const extractTextFromFile = async (file) => {
  try {
    if (file.type === 'text/plain') {
      return await file.text();
    } 
    else if (file.name.endsWith('.docx') || file.type.includes('wordprocessingml')) {
      const mammoth = await import('mammoth');
      const arrayBuffer = await file.arrayBuffer();
      const result = await mammoth.extractRawText({ arrayBuffer });
      return result.value;
    }
    else if (file.type === 'application/pdf' || file.name.endsWith('.pdf')) {
      if (!window.pdfjsLib) {
        await loadPDFJS();
      }

      const arrayBuffer = await file.arrayBuffer();
      const pdf = await window.pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      
      let fullText = '';
      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const textContent = await page.getTextContent();
        const pageText = textContent.items.map(item => item.str).join(' ');
        fullText += pageText + '\n\n';
      }
      
      return fullText.trim();
    }
    else {
      alert('Please upload a .txt, .docx, or .pdf file');
      return null;
    }
  } catch (error) {
    console.error('Error reading file:', error);
    alert('Failed to read file. Please try again or try a different file format.');
    return null;
  }
};

/**
 * ✅ Console logging throttle utility
 * Prevents spam and phone overheating
 */
class ConsoleThrottle {
  constructor(intervalMs = 600000) { // 10 minutes default
    this.intervalMs = intervalMs;
    this.lastLogs = {};
  }

  /**
   * Check if we should log this key
   */
  shouldLog(key) {
    const now = Date.now();
    const lastTime = this.lastLogs[key] || 0;
    
    if (now - lastTime >= this.intervalMs) {
      this.lastLogs[key] = now;
      return true;
    }
    return false;
  }

  /**
   * Log with throttling (logs every 10 minutes per key)
   */
  log(key, ...args) {
    if (this.shouldLog(key)) {
      console.log(`[${key}]`, ...args);
    }
  }

  /**
   * Always log errors (don't throttle)
   */
  error(key, ...args) {
    console.error(`[${key}]`, ...args);
  }

  /**
   * Warn with throttling
   */
  warn(key, ...args) {
    if (this.shouldLog(key)) {
      console.warn(`[${key}]`, ...args);
    }
  }

  /**
   * Force log (bypasses throttle)
   */
  forceLog(key, ...args) {
    console.log(`[FORCE ${key}]`, ...args);
  }
}

// ✅ Create global instance
const throttledLog = new ConsoleThrottle(600000); // 10 minutes

export default function InnerLoopPodcast({ 
  onPodcastGenerated,
  combineAudioSegments,
  autoPlayPodcast,      // ✅ KEEP THIS (new approach)
  onPodcastStarted      // ✅ KEEP THIS (new approach)
}) {
  // ============================================
  // STATE DECLARATIONS (ORGANIZED BY CATEGORY)
  // ============================================
  const [msuNewsData, setMsuNewsData] = useState(null);
  const [missingSourceRetries, setMissingSourceRetries] = useState({});
const [isBackgroundRetrying, setIsBackgroundRetrying] = useState(false);
  const [shimmerTimeout, setShimmerTimeout] = useState(false);
  const [clickedSource, setClickedSource] = useState(null);
const [isSaved, setIsSaved] = useState(false);
const [isSaving, setIsSaving] = useState(false);
const [showOnboarding, setShowOnboarding] = useState(false);
const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(false);
const [isFirstTimeUser, setIsFirstTimeUser] = useState(false);
const [selectedGuest, setSelectedGuest] = useState(null); // null = random (50% chance), 'none' = disabled
// 2. ADD NEW STATE VARIABLES (in your state declarations section)
const [userTimezone, setUserTimezone] = useState(null); // ✅ Starts as null
const [isDailyBriefGenerating, setIsDailyBriefGenerating] = useState(false);
const [timezoneLoading, setTimezoneLoading] = useState(true); // ✅ Track loading state
  // Service & Core State
  const [firestoreService, setfirestoreService] = useState(null);
  const [userName, setUserName] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showRouteSetupFromDailyBrief, setShowRouteSetupFromDailyBrief] = useState(false);
  // Data State
  const [newsData, setNewsData] = useState([]);
  const [stockData, setStockData] = useState([]);
  const [localNewsData, setLocalNewsData] = useState(null);
  const [localNewsHistory, setLocalNewsHistory] = useState([]);
  const [weatherData, setWeatherData] = useState(null);
  
  // Podcast Playback State
  const [isPodcastPlaying, setIsPodcastPlaying] = useState(true);
  const [isNewsPlaying, setIsNewsPlaying] = useState(false);
  const [currentPodcast, setCurrentPodcast] = useState(null);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [currentAudioTime, setCurrentAudioTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [activeLineIndex, setActiveLineIndex] = useState(0);
  const [showMapView, setShowMapView] = useState(false);
const [selectedRouteForMap, setSelectedRouteForMap] = useState(null);
const [personalizedNewsData, setPersonalizedNewsData] = useState({});
const [isPersonalizing, setIsPersonalizing] = useState(false);
const [userPreferences, setUserPreferences] = useState(null);
  // Podcast Generation State
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationStage, setGenerationStage] = useState('');
  const [savedPodcasts, setSavedPodcasts] = useState([]);
  const [showSelectedNewsStation, setShowSelectedNewsStation] = useState(false);
const [selectedNewsStationData, setSelectedNewsStationData] = useState(null);
const [isUserScrolling, setIsUserScrolling] = useState(false);
const scrollTimeoutRef = useRef(null);

  // Daily Brief State
  const [dailyBriefConfig, setDailyBriefConfig] = useState(null);
  const [dailyBriefTime, setDailyBriefTime] = useState('07:00');
  const [dailyBriefModules, setDailyBriefModules] = useState(['news', 'weather', 'stocks']);
  const [dailyBriefTrafficRoute, setDailyBriefTrafficRoute] = useState(null);
  const [dailyBriefPodcast, setDailyBriefPodcast] = useState(null);
  const [isDailyBriefReady, setIsDailyBriefReady] = useState(false);
  const [isDailyBriefExpanded, setIsDailyBriefExpanded] = useState(false);
  const [isGeneratingDailyBrief, setIsGeneratingDailyBrief] = useState(false);
  
  // UI State - Modals & Views
  const [showBriefingBuilder, setShowBriefingBuilder] = useState(false);
  const [showModuleDropdown, setShowModuleDropdown] = useState(false);
  const [showSavedArticles, setShowSavedArticles] = useState(false);
  const [showSavedPodcasts, setShowSavedPodcasts] = useState(false);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const [showTranscriptView, setShowTranscriptView] = useState(false);
  const [showChapters, setShowChapters] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showDailyBriefSetup, setShowDailyBriefSetup] = useState(false);
  const [showRouteSetup, setShowRouteSetup] = useState(false);
  const [showTrafficRouteSetup, setShowTrafficRouteSetup] = useState(false);
  const [showRouteSelector, setShowRouteSelector] = useState(false);
  const [showCreatePodcastModal, setShowCreatePodcastModal] = useState(false);
  const [showHostEditor, setShowHostEditor] = useState(false);
  const [showNewsTypeSelector, setShowNewsTypeSelector] = useState(false);
  
  // UI State - Loading & Animation
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isRefreshingNews, setIsRefreshingNews] = useState(false);
  const [isLoadingLocalNews, setIsLoadingLocalNews] = useState(false);
  const [animateRewind, setAnimateRewind] = useState(false);
  const [animateFastForward, setAnimateFastForward] = useState(false);
  const [animatePlus, setAnimatePlus] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [gradientAngle, setGradientAngle] = useState(245);
  const [blurPosition, setBlurPosition] = useState({ x: -10, y: 20 });
  const [blurOpacity, setBlurOpacity] = useState(0);
  
  // Module Connection State
  const [connectedModules, setConnectedModules] = useState([]);
  const [draggedModule, setDraggedModule] = useState(null);
  const [touchPosition, setTouchPosition] = useState(null);
  const [dragDistance, setDragDistance] = useState(0);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  
  // News & Articles State
  const [promptText, setPromptText] = useState('');
  const [selectedNewsSource, setSelectedNewsSource] = useState(null);
  const [selectedNewsCategory, setSelectedNewsCategory] = useState('news');
  const [selectedNewsType, setSelectedNewsType] = useState('general');
  const [activeStoryIndex, setActiveStoryIndex] = useState({});
  const [lastRefreshTime, setLastRefreshTime] = useState(null);
  const [shownArticles, setShownArticles] = useState(new Set());
  const [shownLocalArticles, setShownLocalArticles] = useState(new Set());
  const [savedArticles, setSavedArticles] = useState([]);
  const [loadedNewsCards, setLoadedNewsCards] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // In-App Browser State
  const [inAppBrowserUrl, setInAppBrowserUrl] = useState(null);
  const [iframeError, setIframeError] = useState(false);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  
  // File Upload State
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadedFileContent, setUploadedFileContent] = useState(null);
  const [uploadedFileType, setUploadedFileType] = useState(null);
  const [youtubeVideoId, setYoutubeVideoId] = useState(null);
  const [youtubeVideoTitle, setYoutubeVideoTitle] = useState(null);
  const [youtubeVideoAnalysis, setYoutubeVideoAnalysis] = useState(null);
  const [newsLoadState, setNewsLoadState] = useState('checking'); // 'checking' | 'loading' | 'loaded' | 'error'

  // Settings State
  const [selectedHost, setSelectedHost] = useState('Host 1');
  const [selectedHosts, setSelectedHosts] = useState(['Host 1', 'Host 2']);
  const [selectedNewsFeeds, setSelectedNewsFeeds] = useState(['cnn', 'bbc', 'abc', 'nbc', 'fox', 'techcrunch']);
const [selectedStocks, setSelectedStocks] = useState(['AAPL', 'NVDA', 'SPX', 'TSLA']);
  const [selectedCategories, setSelectedCategories] = useState({
    news: ['cnn', 'bbc', 'abc'],
    sports: ['espn'],
    tech: ['techcrunch'],
    business: [],
    entertainment: []
  });
  const [introMusicEnabled, setIntroMusicEnabled] = useState(true);
    const navigate = useNavigate();
  const location = useLocation();
  // Route State
  const [savedRoutes, setSavedRoutes] = useState([]);
  const [selectedRouteForPodcast, setSelectedRouteForPodcast] = useState(null);
  const [newRouteName, setNewRouteName] = useState('');
  const [newRouteOrigin, setNewRouteOrigin] = useState('');
  const [newRouteDestination, setNewRouteDestination] = useState('');
  const [newRouteOriginCoords, setNewRouteOriginCoords] = useState('');
  const [newRouteDestinationCoords, setNewRouteDestinationCoords] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [returnToDailyBrief, setReturnToDailyBrief] = useState(false);
const [isLoadingDailyBrief, setIsLoadingDailyBrief] = useState(true);
const [talkingStyle, setTalkingStyle] = useState('casual'); // 'casual' or 'semi-formal'
const [showStyleSelector, setShowStyleSelector] = useState(false);
const [editingRouteId, setEditingRouteId] = useState(null);
const [savedDailyBriefs, setSavedDailyBriefs] = useState([]);
const [showCompanyStation, setShowCompanyStation] = useState(false);
const [selectedCompany, setSelectedCompany] = useState(null);
const [badgeOpacity, setBadgeOpacity] = useState(1);
const [showBadge, setShowBadge] = useState(false);
const [backgroundRetryCount, setBackgroundRetryCount] = useState(0);
const backgroundRetryTimerRef = useRef(null);
const transcriptEndRef = useRef(null);

const [activeTab, setActiveTab] = useState('latest'); // 'latest', 'foryou', 'saved'
const [showSavePrompt, setShowSavePrompt] = useState(false);
const [autoSaveEnabled, setAutoSaveEnabled] = useState(
  localStorage.getItem('preptune_auto_save') === 'true'
);

const [dontAskAgain, setDontAskAgain] = useState(
  localStorage.getItem('preptune_dont_ask_save') === 'true'
);
// ============================================
// SMART GEO-LOCATION WITH CACHING & SHIMMER
// ============================================

// Add these state variables
const [isCheckingLocation, setIsCheckingLocation] = useState(true);
const [showLocalCompany, setShowLocalCompany] = useState(false);
const [userLocation, setUserLocation] = useState(null);

// Cache configuration
const LOCATION_CACHE_KEY = 'msu_location_cache';
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

// Get cached location data
const getCachedLocation = () => {
  try {
    const cached = localStorage.getItem(LOCATION_CACHE_KEY);
    if (!cached) return null;
    
    const data = JSON.parse(cached);
    const now = Date.now();
    
    // Check if cache is still valid
    if (now - data.timestamp < CACHE_DURATION) {
      console.log('✅ Using cached location data');
      console.log(`⏰ Cache age: ${Math.floor((now - data.timestamp) / 1000 / 60)} minutes`);
      return data;
    } else {
      console.log('⏰ Cache expired, fetching fresh location');
      localStorage.removeItem(LOCATION_CACHE_KEY);
      return null;
    }
  } catch (error) {
    console.warn('⚠️ Cache read error:', error);
    return null;
  }
};

// Save location to cache
const setCachedLocation = (latitude, longitude, isInMSUArea) => {
  try {
    const data = {
      latitude,
      longitude,
      isInMSUArea,
      timestamp: Date.now()
    };
    localStorage.setItem(LOCATION_CACHE_KEY, JSON.stringify(data));
    console.log('💾 Location cached for 30 minutes');
  } catch (error) {
    console.warn('⚠️ Cache write error:', error);
  }
};

// Montclair State University Coverage Area
const MSU_COVERAGE = {
  name: 'Montclair State University Area',
  bounds: {
    north: 40.8750,
    south: 40.8550,
    east: -74.1850,
    west: -74.2100
  },
  center: {
    lat: 40.8682,
    lng: -74.1977
  },
  radiusMiles: 2.5
};

const getDistanceInMiles = (lat1, lon1, lat2, lon2) => {
  const R = 3959;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};

const isInMSUArea = (latitude, longitude) => {
  const inBounds = 
    latitude >= MSU_COVERAGE.bounds.south && 
    latitude <= MSU_COVERAGE.bounds.north && 
    longitude >= MSU_COVERAGE.bounds.west && 
    longitude <= MSU_COVERAGE.bounds.east;
  
  const distance = getDistanceInMiles(
    latitude, 
    longitude, 
    MSU_COVERAGE.center.lat, 
    MSU_COVERAGE.center.lng
  );
  const inRadius = distance <= MSU_COVERAGE.radiusMiles;
  
  return inBounds || inRadius;
};

// ✅ MAIN GEO-LOCATION EFFECT WITH CACHING
useEffect(() => {
  const checkLocation = async () => {
    setIsCheckingLocation(true);
    
    // ✅ STEP 1: Check cache first
    const cached = getCachedLocation();
    
    if (cached) {
      // ✅ Use cached data immediately
      setUserLocation({ latitude: cached.latitude, longitude: cached.longitude });
      setShowLocalCompany(cached.isInMSUArea); // ← TRIGGERS CARD REFRESH
      
      // ✅ Still show shimmer for smooth UX (minimum 500ms)
      await new Promise(resolve => setTimeout(resolve, 500));
      setIsCheckingLocation(false); // ← STOPS SHIMMER, SHOWS CARDS
      
      console.log('═══════════════════════════════════════');
      console.log('📍 MSU STATION (CACHED DATA)');
      console.log('═══════════════════════════════════════');
      console.log(`📍 Location: ${cached.latitude.toFixed(6)}, ${cached.longitude.toFixed(6)}`);
      console.log(`${cached.isInMSUArea ? '✅' : '❌'} MSU Area: ${cached.isInMSUArea ? 'YES' : 'NO'}`);
      console.log('═══════════════════════════════════════');
      return;
    }
    
    // ✅ STEP 2: No cache - fetch fresh location
    if ('geolocation' in navigator) {
      const startTime = Date.now();
      
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          console.log(`✅ Location acquired: ${latitude.toFixed(6)}, ${longitude.toFixed(6)}`);
          
          setUserLocation({ latitude, longitude });
          
          const distance = getDistanceInMiles(
            latitude, 
            longitude, 
            MSU_COVERAGE.center.lat, 
            MSU_COVERAGE.center.lng
          );
          
          const inMSUArea = isInMSUArea(latitude, longitude);
          
          // ✅ THIS LINE TRIGGERS INSTANT CARD REFRESH (no page reload!)
          setShowLocalCompany(inMSUArea);
          
          // ✅ Cache the result
          setCachedLocation(latitude, longitude, inMSUArea);
          
          // ✅ Ensure minimum 500ms shimmer (smooth UX)
          const elapsed = Date.now() - startTime;
          const remaining = Math.max(0, 500 - elapsed);
          
          if (remaining > 0) {
            await new Promise(resolve => setTimeout(resolve, remaining));
          }
          
          setIsCheckingLocation(false); // ← STOPS SHIMMER, SHOWS CARDS WITH MSU
          
          // ✅ Detailed logging
          console.log('═══════════════════════════════════════');
          console.log('📍 MSU STATION GEO-DETECTION (FRESH)');
          console.log('═══════════════════════════════════════');
          console.log(`📍 User Location: ${latitude.toFixed(6)}, ${longitude.toFixed(6)}`);
          console.log(`🏫 MSU Campus Center: ${MSU_COVERAGE.center.lat}, ${MSU_COVERAGE.center.lng}`);
          console.log(`📏 Distance from MSU: ${distance.toFixed(2)} miles`);
          console.log(`📦 Bounds Check: ${
            latitude >= MSU_COVERAGE.bounds.south && 
            latitude <= MSU_COVERAGE.bounds.north && 
            longitude >= MSU_COVERAGE.bounds.west && 
            longitude <= MSU_COVERAGE.bounds.east ? '✅ Inside' : '❌ Outside'
          }`);
          console.log(`🎯 Radius Check (${MSU_COVERAGE.radiusMiles} mi): ${distance <= MSU_COVERAGE.radiusMiles ? '✅ Inside' : '❌ Outside'}`);
          console.log('───────────────────────────────────────');
          
          if (inMSUArea) {
            console.log('✅ MSU STUDENT/FACULTY DETECTED!');
            console.log('🎓 Showing Montclair State University station');
            console.log('📰 Local MSU news & campus updates available');
            console.log('🔄 Company cards auto-refreshed instantly (no page reload!)');
          } else {
            console.log('❌ Not in MSU area');
            console.log(`💡 User is ${distance.toFixed(1)} miles from campus`);
            console.log('🌍 Showing standard company stations only');
          }
          console.log('═══════════════════════════════════════');
        },
        async (error) => {
          console.warn('⚠️ Geo-location error:', error.message);
          console.log('🔒 Location permission denied or unavailable');
          
          // ✅ Show shimmer for minimum 500ms even on error
          const elapsed = Date.now() - startTime;
          const remaining = Math.max(0, 500 - elapsed);
          
          if (remaining > 0) {
            await new Promise(resolve => setTimeout(resolve, remaining));
          }
          
          setShowLocalCompany(false);
          setIsCheckingLocation(false);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0 // Don't use browser cache, we have our own
        }
      );
    } else {
      console.warn('⚠️ Geolocation not supported');
      
      // ✅ Show shimmer for minimum 500ms
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setShowLocalCompany(false);
      setIsCheckingLocation(false);
    }
  };
  
  checkLocation();
}, []);
// ============================================
// FETCH MSU NEWS WHEN IN MSU AREA
// ============================================
useEffect(() => {
  const fetchMSUNews = async () => {
    if (!showLocalCompany || !userLocation) return;
    
    console.log('🎓 Fetching MSU news for station card...');
    
    try {
      const MSU_BACKEND_URL = 'https://us-central1-fir-7c9c9.cloudfunctions.net/getMSUNews';
      const cacheBuster = Date.now();
      
      const response = await fetch(`${MSU_BACKEND_URL}?forceRefresh=true&t=${cacheBuster}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.success && data.results && data.results.length > 0) {
          console.log(`✅ Got ${data.results.length} MSU news items for card`);
          console.log(`📊 Sources: Montclarion=${data.sources?.montclarion || 0}, Yahoo=${data.sources?.yahoo || 0}`);
          
          // ✅ STORE IN STATE so the station card can display it
          // You'll need to add this state variable:
          // const [msuNewsData, setMsuNewsData] = useState(null);
          setMsuNewsData(data.results.slice(0, 5));
          
        } else {
          console.log('⚠️ MSU API returned no results');
          setMsuNewsData([]);
        }
      } else {
        console.log(`⚠️ MSU API returned ${response.status}`);
        setMsuNewsData([]);
      }
      
    } catch (error) {
      console.error('❌ MSU news fetch failed:', error.message);
      setMsuNewsData([]);
    }
  };
  
  fetchMSUNews();
}, [showLocalCompany, userLocation]);
/**
 * ✅ Article Analysis Cache
 * Prevents duplicate API calls for same articles
 */
class ArticleAnalysisCache {
  constructor() {
    this.cache = new Map();
    this.pendingRequests = new Map();
  }

  generateKey(storyTitle, storyDescription, storyId = null, storyUrl = null) {
    if (storyId) return `article_id_${storyId}`;
    if (storyUrl) return `article_url_${storyUrl}`;
    
    const content = `${storyTitle}|${storyDescription}`;
    let hash = 0;
    for (let i = 0; i < content.length; i++) {
      hash = ((hash << 5) - hash) + content.charCodeAt(i);
      hash = hash & hash;
    }
    return `article_hash_${Math.abs(hash)}`;
  }

  get(key) {
    return this.cache.get(key) || null;
  }

  set(key, analysis) {
    this.cache.set(key, analysis);
    return analysis;
  }

  has(key) {
    return this.cache.has(key);
  }

  getPendingRequest(key) {
    return this.pendingRequests.get(key) || null;
  }

  setPendingRequest(key, promise) {
    this.pendingRequests.set(key, promise);
  }

  clearPendingRequest(key) {
    this.pendingRequests.delete(key);
  }

  getStats() {
    return {
      cacheSize: this.cache.size,
      pendingRequests: this.pendingRequests.size
    };
  }
}

// ✅ Create global cache instance
const articleCache = new ArticleAnalysisCache();
// ✅ FUNCTION 1: analyzeArticleWithAI - FULLY UPDATED
const analyzeArticleWithAI = async (storyTitle, storyDescription, storyId = null, storyUrl = null) => {
  try {
    // ✅ Check cache first
    const cacheKey = articleCache.generateKey(storyTitle, storyDescription, storyId, storyUrl);
    
    const cachedAnalysis = articleCache.get(cacheKey);
    if (cachedAnalysis) {
      throttledLog.log('cache_hit', `Reusing cached analysis for: ${storyTitle.substring(0, 30)}`);
      return cachedAnalysis;
    }

    // ✅ Check if already pending
    const pendingRequest = articleCache.getPendingRequest(cacheKey);
    if (pendingRequest) {
      return await pendingRequest;
    }

    // ✅ Create new analysis promise
    const analysisPromise = (async () => {
      const maxTitleLength = 150;
      const maxDescLength = 200;
      const truncatedTitle = storyTitle?.substring(0, maxTitleLength) || '';
      const truncatedDesc = storyDescription?.substring(0, maxDescLength) || storyTitle?.substring(0, maxDescLength) || '';
      
      const data = await SecureAPI.callAzureOpenAI([
        {
          role: 'system',
          content: `Extract key information from news articles. Return ONLY valid JSON with this exact structure:
{"topics":["topic1","topic2"],"people":["person1"],"places":["place1"],"organizations":["org1"],"themes":["theme1"]}

Keep arrays short (max 3 items each). Use lowercase. No explanations.`
        },
        {
          role: 'user',
          content: `Title: "${truncatedTitle}"\nDescription: "${truncatedDesc}"`
        }
      ], {
        temperature: 0,
        max_tokens: 200,
        response_format: { type: "json_object" }
      });

      if (!data.choices?.[0]?.message?.content) {
        return { topics: [], people: [], places: [], organizations: [], themes: [] };
      }

      let jsonContent = data.choices[0].message.content.trim();
      jsonContent = jsonContent.replace(/```json\s*/g, '').replace(/```\s*/g, '').trim();
      
      if (!jsonContent.endsWith('}')) {
        const lastCommaIndex = jsonContent.lastIndexOf(',');
        if (lastCommaIndex > 0) {
          jsonContent = jsonContent.substring(0, lastCommaIndex) + '}';
        } else {
          jsonContent += '}';
        }
      }
      
      try {
        const parsed = JSON.parse(jsonContent);
        const result = {
          topics: parsed.topics || [],
          people: parsed.people || [],
          places: parsed.places || [],
          organizations: parsed.organizations || [],
          themes: parsed.themes || []
        };

        // ✅ CACHE IT
        articleCache.set(cacheKey, result);
        return result;

      } catch (parseError) {
        const fallback = { 
          topics: ['news'], 
          people: [], 
          places: [], 
          organizations: [], 
          themes: ['current events']
        };
        articleCache.set(cacheKey, fallback);
        return fallback;
      }
      
    })();

    articleCache.setPendingRequest(cacheKey, analysisPromise);
    const result = await analysisPromise;
    articleCache.clearPendingRequest(cacheKey);

    return result;

  } catch (error) {
    console.error('❌ AI analysis failed:', error);
    
    if (error.message?.includes('content_filter')) {
      return { 
        topics: ['news'], 
        people: [], 
        places: [], 
        organizations: [], 
        themes: ['current events'],
        filtered: true 
      };
    }
    
    return { topics: [], people: [], places: [], organizations: [], themes: [] };
  }
};

// ✅ FUNCTION 2: summarizeTitleWithAI - FULLY UPDATED
const summarizeTitleWithAI = async (originalTitle) => {
  if (!originalTitle || originalTitle.length < 10) return originalTitle;
  
  try {
    console.log('🤖 Summarizing title:', originalTitle);
    
    // ✅ USE SECUREAPI - No more fetch()
    const data = await SecureAPI.callAzureOpenAI([
      {
        role: 'system',
        content: 'Summarize the topic into exactly 3-4 words. Be concise and descriptive. Return ONLY the summary, no quotes or extra punctuation.'
      },
      {
        role: 'user',
        content: originalTitle
      }
    ], {
      temperature: 0.3,
      max_tokens: 15
    });

    const summary = data.choices?.[0]?.message?.content?.trim() || originalTitle;
    
    console.log('✅ Title summarized:', summary);
    return summary;
    
  } catch (error) {
    console.error('❌ Error summarizing title:', error);
    // Fallback: take first 4 words
    const words = originalTitle.split(' ').slice(0, 4);
    return words.join(' ');
  }
};

 // ✅ KEY FIX: Apply speed whenever it changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackSpeed;
      console.log('🎚️ Playback speed changed to:', playbackSpeed);
    }
  }, [playbackSpeed]);

  const changeSpeed = () => {
    const speeds = [0.95, 1.0, 1.15, 1.25, 1.5];
    const currentIndex = speeds.indexOf(playbackSpeed);
    const nextSpeed = speeds[(currentIndex + 1) % speeds.length];
    setPlaybackSpeed(nextSpeed);
  };
// FUNCTION 2: Build User Preferences
const buildUserPreferences = async () => {
  if (!firestoreService?.isReady()) return null;

  try {
    const db = getFirestore();
    const feedbackRef = collection(db, 'users', firestoreService.currentUser.uid, 'newsFeedback');
    const snapshot = await getDocs(feedbackRef);
    
    const preferences = {
      topics: {},
      people: {},
      places: {},
      organizations: {},
      themes: {}
    };

    for (const doc of snapshot.docs) {
      const data = doc.data();
      const analysis = data.aiAnalysis || {};
      // ✅ COUNT BOTH LIKES AND DISLIKES
      const weight = data.feedback === 'up' ? 1 : (data.feedback === 'down' ? -1 : 0);

      if (weight === 0) continue;

      ['topics', 'people', 'places', 'organizations', 'themes'].forEach(category => {
        if (analysis[category]) {
          analysis[category].forEach(item => {
            const key = item.toLowerCase();
            if (!preferences[category][key]) {
              preferences[category][key] = { likes: 0, dislikes: 0, confidence: 0 };
            }
            
            // ✅ NOW COUNTS BOTH
            if (weight > 0) {
              preferences[category][key].likes++;
            } else {
              preferences[category][key].dislikes++;
            }
          });
        }
      });
    }

    // ✅ CALCULATE SCORES (includes negative scores for dislikes)
    Object.keys(preferences).forEach(category => {
      Object.keys(preferences[category]).forEach(item => {
        const { likes, dislikes } = preferences[category][item];
        const total = likes + dislikes;
        
        if (total >= 3) {
          const score = ((likes - dislikes) / total) * 100;
          const confidence = Math.min(total * 10, 100);
          
          preferences[category][item].score = score;
          preferences[category][item].confidence = confidence;
        }
      });
    });

    console.log('✅ User preferences built (includes dislikes)');
    return preferences;
  } catch (error) {
    console.error('❌ Failed to build preferences:', error);
    return null;
  }
};

// FUNCTION 3: Score Article
const scoreArticle = (storyAnalysis, userPrefs) => {
  if (!userPrefs) return 50;

  let totalScore = 0;
  let matchCount = 0;
  let dislikeCount = 0;
  let dislikeScore = 0;

  ['topics', 'people', 'places', 'organizations', 'themes'].forEach(category => {
    if (storyAnalysis[category] && userPrefs[category]) {
      storyAnalysis[category].forEach(item => {
        const key = item.toLowerCase();
        const pref = userPrefs[category][key];
        
        if (pref && pref.confidence >= 30) {
          // ✅ IF DISLIKED (negative score), track it
          if (pref.score < 0) {
            dislikeScore += Math.abs(pref.score);
            dislikeCount++;
          } else {
            // ✅ IF LIKED (positive score), add to total
            totalScore += pref.score * (pref.confidence / 100);
            matchCount++;
          }
        }
      });
    }
  });

  // ✅ FILTER OUT: If article has disliked topics with high confidence
  if (dislikeCount > 0 && dislikeScore > 50) {
    console.log(`🚫 Article filtered out (dislike score: ${dislikeScore})`);
    return -1; // ← Special flag: HIDE THIS ARTICLE
  }

  if (matchCount === 0) return 50;
  const finalScore = (totalScore / matchCount);
  return Math.max(0, Math.min(100, 50 + finalScore));
};

// FUNCTION 4: Personalize News
// ============================================
// SMART ARTICLE ROTATION - CHECK EACH ONE
// Keep only articles WITHOUT dislikes (up to 3)
// ============================================

const personalizeNewsData = async (rawNewsData) => {
  if (!rawNewsData || Object.keys(rawNewsData).length === 0) {
    return rawNewsData;
  }

  setIsPersonalizing(true);

  try {
    const prefs = await buildUserPreferences();
    setUserPreferences(prefs);

    if (!prefs) {
      setIsPersonalizing(false);
      return rawNewsData;
    }

    const personalized = {};

    for (const [sourceId, sourceData] of Object.entries(rawNewsData)) {
      const stories = sourceData.stories || [];
      
      const scoredStories = await Promise.all(
        stories.map(async (story) => {
          const analysis = await analyzeArticleWithAI(story.title, story.description);
          const score = scoreArticle(analysis, prefs);
          
          return {
            ...story,
            aiAnalysis: analysis,
            preferenceScore: score
          };
        })
      );

      // ✅ SMART ROTATION - CHECK EACH ARTICLE
      const keepers = [];
      const disliked = [];

      // Step 1: Check each article one by one
      for (const story of scoredStories) {
        if (story.preferenceScore === -1) {
          // ✅ This one is disliked - save it for later
          disliked.push(story);
          console.log(`   ❌ ${story.title.substring(0, 40)}... (disliked)`);
        } else {
          // ✅ This one is GOOD - keep it
          keepers.push(story);
          console.log(`   ✅ ${story.title.substring(0, 40)}... (liked)`);
          
          // Stop when we have 3 good ones
          if (keepers.length >= 3) {
            console.log(`   🛑 Got 3 good articles, stopping check`);
            break;
          }
        }
      }

      // Step 2: If we don't have 3 good ones, add disliked ones
      let finalStories = keepers;
      
      if (finalStories.length < 3 && disliked.length > 0) {
        const needed = 3 - finalStories.length;
        const addFromDisliked = disliked.slice(0, needed);
        
        finalStories = [...finalStories, ...addFromDisliked];
        
        console.log(`📍 ${sourceId}: Added ${addFromDisliked.length} disliked articles to reach 3`);
      }

      // Step 3: Sort by score (good ones first, disliked at bottom)
      const sorted = finalStories.sort((a, b) => b.preferenceScore - a.preferenceScore);

      personalized[sourceId] = {
        ...sourceData,
        stories: sorted,
        originalCount: stories.length,
        goodCount: keepers.length,
        dislikedCount: disliked.length,
        finalCount: sorted.length
      };
      
      console.log(`📊 ${sourceId}: ${keepers.length} good + ${Math.min(disliked.length, 3 - keepers.length)} disliked = ${sorted.length} total`);
    }

    setIsPersonalizing(false);
    return personalized;
  } catch (error) {
    console.error('❌ Personalization failed:', error);
    setIsPersonalizing(false);
    return rawNewsData;
  }
};
// Check if current podcast is saved
useEffect(() => {
  if (currentPodcast?.audioUrl) {
    const alreadySaved = savedPodcasts.some(p => p.audioUrl === currentPodcast.audioUrl);
    setIsSaved(alreadySaved);
    console.log('🔍 Checked saved status:', alreadySaved ? 'SAVED' : 'NOT SAVED');
  }
}, [currentPodcast?.audioUrl, savedPodcasts]);
useEffect(() => {
  const totalSaved = savedArticles.length + savedPodcasts.length;
  
  if (totalSaved > 0) {
    // Wait 1 second for Firestore to load, then show badge
    const showTimer = setTimeout(() => {
      setShowBadge(true);
      setBadgeOpacity(1);
    }, 1000);

    // Then fade after 2 more seconds (3 seconds total from save)
    const fadeTimer = setTimeout(() => {
      setBadgeOpacity(0);
    }, 3000);

    return () => {
      clearTimeout(showTimer);
      clearTimeout(fadeTimer);
    };
  }
}, [savedArticles.length, savedPodcasts.length]); // Dependency array tracks when items are added/removed

  // Chapters State
  const [chapters, setChapters] = useState([]);
const handleSavePodcast = async () => {
  if (!currentPodcast || isSaving) return;
  
  setIsSaving(true);
  
  try {
    if (!firestoreService?.isReady()) {
      alert('Service not ready. Please wait a moment and try again.');
      setIsSaving(false);
      return;
    }

    console.log('💾 Saving podcast...');
    
    const podcastToSave = {
      ...currentPodcast,
      savedAt: Date.now(),
      savedManually: true,
      id: currentPodcast.id || `podcast_${Date.now()}` // ✅ Ensure ID exists
    };
    
    // ✅ CRITICAL: Set isSaved FIRST before updating savedPodcasts
    setIsSaved(true);
    
    // ✅ Optimistic UI update - show immediately
    setSavedPodcasts(prev => [podcastToSave, ...prev]);
    setShowSavePrompt(false);
    setIsSaving(false);
    
    console.log('✅ Podcast marked as saved locally');
    
    // ✅ Save to Firestore in background
    firestoreService.savePodcast(podcastToSave).catch(error => {
      console.error('❌ Failed to save podcast:', error);
      // Revert optimistic update on error
      setSavedPodcasts(prev => prev.filter(p => p.audioUrl !== currentPodcast.audioUrl));
      setIsSaved(false);
      alert('Failed to save podcast. Please try again.');
    });
    
  } catch (error) {
    console.error('❌ Failed to save podcast:', error);
    alert('Failed to save podcast. Please try again.');
    setIsSaving(false);
  }
};
  // ============================================
  // REFS
  // ============================================
  const generateButtonRef = useRef(null);
  const moduleRefs = useRef({});
  const svgRef = useRef(null);
  const dropdownRef = useRef(null);
  const audioRef = useRef(null);
  const transcriptRef = useRef(null);
  const lineRefs = useRef({});
  const chaptersRef = useRef(null);
  const chapterRefs = useRef({});
  const fileInputRef = useRef(null);
const refreshLockRef = useRef(false);

const [isMapAnimating, setIsMapAnimating] = useState(false);
const mapContainer = useRef(null);
const map = useRef(null);

  // Check if this is a first-time user
useEffect(() => {
  const hasCompleted = localStorage.getItem('hasCompletedUserOnboarding');
  
  if (!hasCompleted && firestoreService?.isReady()) {
    console.log('🆕 First-time user detected - showing onboarding');
    setIsFirstTimeUser(true);
    setShowOnboarding(true);
  } else if (hasCompleted) {
    setHasCompletedOnboarding(true);
  }
}, [firestoreService?.isReady()]);
// ✅ Handle podcast playback from ChatMode
useEffect(() => {
  if (!autoPlayPodcast) return;
  
  // Case 1: Already generated podcast - just play it
  if (autoPlayPodcast.audioUrl && !autoPlayPodcast.shouldGenerate) {
    console.log('🎙️ Auto-playing pre-generated podcast from chat');
    
    const podcastData = {
      audioUrl: autoPlayPodcast.audioUrl,
      dialogue: autoPlayPodcast.dialogue,
      title: autoPlayPodcast.title,
      duration: autoPlayPodcast.duration,
      metadata: autoPlayPodcast.metadata,
      type: autoPlayPodcast.type || 'chat-generated'
    };
    
    setCurrentPodcast(podcastData);
    
    const audio = audioRef.current;
    if (audio) {
      audio.src = autoPlayPodcast.audioUrl;
      
      const handleCanPlay = () => {
        // ✅ Auto-open transcript
        if (autoPlayPodcast.showTranscript) {
          setShowTranscriptView(true);
        }
        
        // ✅ Auto-play
        if (autoPlayPodcast.autoPlay) {
          setIsPodcastPlaying(true);
          audio.play().catch(err => {
            console.error('❌ Autoplay blocked:', err);
            setIsPodcastPlaying(false);
            // Keep transcript open even if autoplay fails
          });
        }
        
        audio.removeEventListener('canplay', handleCanPlay);
      };
      
      audio.addEventListener('canplay', handleCanPlay);
      audio.load();
    }
    
    // Clear the request
    if (onPodcastStarted) {
      onPodcastStarted();
    }
  }
}, [autoPlayPodcast, audioRef, onPodcastStarted]);

// Card visibility states
const [showNewsCard, setShowNewsCard] = useState(true);
const [showStocksCard, setShowStocksCard] = useState(true);
const [showCryptoCard, setShowCryptoCard] = useState(false);

const COMPANIES_WATCHLIST = [
  // ✅ MONTCLAIR STATE UNIVERSITY STATION
  {
    ticker: 'TheMontclarion',
    name: 'Montclair State University',
    logos: [
      'https://themontclarion.org/wp-content/uploads/2023/11/logo-2.png',  // Add MSU logo
      'https://yt3.googleusercontent.com/4SqpOCD6Ctz0Y8c0sZLAhnuZeLMG4w641bglUOy2R13x4NhOgh-nqoKSFg2rT5kUHR8ZucL8=s900-c-k-c0x00ffffff-no-rj',
      'https://via.placeholder.com/64/E03A3E/ffffff?text=MSU',  // MSU red
    ],
    color: '#000000ff',  // MSU official red
    isLocal: true,
    location: MSU_COVERAGE
  },

  {
    ticker: 'TSLA',
    name: 'Tesla Inc.',
    logos: [
      'https://financialmodelingprep.com/image-stock/TSLA.png',
      'https://companiesmarketcap.com/img/company-logos/64/TSLA.webp',
      'https://assets.parqet.com/logos/symbol/TSLA'
    ],
    color: '#CC0000'
  },
   {
    ticker: 'AAPL',
    name: 'Apple Inc.',
    logos: [
      'https://cdn-icons-png.freepik.com/512/0/747.png',
      'https://companiesmarketcap.com/img/company-logos/64/AAPL.webp',
      'https://assets.parqet.com/logos/symbol/AAPL'
    ],
    color: '#000000ff'
  },
  {
    ticker: 'META',
    name: 'Meta Platforms',
    logos: [
      'https://financialmodelingprep.com/image-stock/META.png',
      'https://companiesmarketcap.com/img/company-logos/64/META.webp',
      'https://assets.parqet.com/logos/symbol/META'
    ],
    color: '#0081FB'
  },
  {
    ticker: 'GOOGL',
    name: 'Alphabet Inc.',
    logos: [
      'https://financialmodelingprep.com/image-stock/GOOGL.png',
      'https://companiesmarketcap.com/img/company-logos/64/GOOGL.webp',
      'https://assets.parqet.com/logos/symbol/GOOGL'
    ],
    color: '#000000'
  },
  {
    ticker: 'MSFT',
    name: 'Microsoft Corp.',
    logos: [
      'https://financialmodelingprep.com/image-stock/MSFT.png',
      'https://companiesmarketcap.com/img/company-logos/64/MSFT.webp',
      'https://assets.parqet.com/logos/symbol/MSFT'
    ],
    color: '#76B900'
  },
  {
    ticker: 'AMZN',
    name: 'Amazon.com Inc.',
    logos: [
      'https://upload.wikimedia.org/wikipedia/commons/d/de/Amazon_icon.png',
      'https://companiesmarketcap.com/img/company-logos/64/AMZN.webp',
      'https://images.seeklogo.com/logo-png/40/2/amazon-icon-logo-png_seeklogo-405254.png'
    ],
    color: '#FF9900'
  },
  {
    ticker: 'OPENAI',
    name: 'OpenAI',
    logos: [
      'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTBdcgkdcDy5z4PYGx_kDJB1AvvC_x1pCBbbQ&s',
      'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcS2LmgGUNPfLs9NmqfycQ0lLiBFplJXOi70EA&s',
      'https://avatars.githubusercontent.com/u/14957082'
    ],
    color: '#000000'
  },
    {
    ticker: 'NVDA',
    name: 'NVIDIA Corp.',
    logos: [
      'https://financialmodelingprep.com/image-stock/NVDA.png',
      'https://companiesmarketcap.com/img/company-logos/64/NVDA.webp',
      'https://assets.parqet.com/logos/symbol/NVDA'
    ],
    color: '#76B900'
  },
  {
    ticker: 'XAI',
    name: 'xAI (Grok)',
    logos: [
      'https://pbs.twimg.com/profile_images/1683325380441128960/yRsRRjGO_400x400.jpg',
      'https://avatars.githubusercontent.com/u/132372032',
      'https://abs.twimg.com/sticky/default_profile_images/default_profile_400x400.png'
    ],
    color: '#000000'
  }
];

const SavedContent = ({ 
  savedArticles, 
  savedPodcasts, 
  savedDailyBriefs,
  onRemoveArticle, 
  onRemovePodcast, 
  onPlayPodcast,
  onPlayDailyBrief,
  setInAppBrowserUrl,
  setSelectedNewsSource,
  handlePlaySavedPodcast,
  removeSavedPodcast
}) => {
  const customScrollStyle = {
    display: 'flex', 
    gap: '12px', 
    overflowX: 'auto', 
    paddingLeft: '16px', 
    paddingRight: '16px', 
    paddingBottom: '8px',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    WebkitOverflowScrolling: 'touch',
    scrollbarColor: 'transparent transparent'
  };

  // ✅ Unique background images for podcasts
  const backgroundImages = [
    '/image/studycard7.png',
    '/image/studycard12.png',
    '/image/studycard13.png',
    '/image/studycard14.png',
    '/image/studycard5.png',
    '/image/studycard15.png',
    '/image/studycard10.png',
    '/image/studycard17.png',
    '/image/studycard11.png',
  ];

  if (savedArticles.length === 0 && savedPodcasts.filter(p => p.type !== 'daily-brief-catchup').length === 0 && savedDailyBriefs.length === 0) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '60px 20px',
        color: '#9ca3af'
      }}>
        <svg width="48" height="48" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ margin: '0 auto 16px' }}>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
        </svg>
        <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#6b7280', marginBottom: '8px' }}>
          No saved items yet
        </h3>
        <p style={{ fontSize: '13px', color: '#9ca3af', lineHeight: '1.5' }}>
          Save articles and podcasts to access them here anytime
        </p>
      </div>
    );
  }

  return (
    <>
{/*
      <SavedDailyBriefsSection
        savedDailyBriefs={savedDailyBriefs}
        onPlayBrief={onPlayDailyBrief}
      />
*/}
      {/* Saved Podcasts Section - FILTERED to exclude daily briefs */}
      {savedPodcasts.filter(p => p.type !== 'daily-brief-catchup').length > 0 && (
        <>
          <h2 style={{ 
            fontSize: '13px', 
            fontWeight: '600', 
            color: '#1f2937', 
            margin: '24px 0 16px 0', 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px' 
          }}>
            <Radio size={16} color="#3b82f6" />
            Saved Podcasts ({savedPodcasts.filter(p => p.type !== 'daily-brief-catchup').length})
          </h2>
          
          <div style={{ marginLeft: '-16px', marginRight: '-16px', marginBottom: '32px' }}>
            <div style={customScrollStyle} className="custom-scroll">
    {savedPodcasts
  .filter(podcast => podcast.type !== 'daily-brief-catchup')
  .sort((a, b) => (b.savedAt || 0) - (a.savedAt || 0)) // ✅ Sort by newest first
  .map((podcast, index) => {
                  // ✅ Get unique background image (cycles through array)
                  const bgImage = backgroundImages[index % backgroundImages.length];
                  
return (
  <div key={`saved-podcast-${podcast.id}-${index}`} style={{ 
    flexShrink: 0,
    width: '200px',
    background: 'rgba(255, 255, 255, 0.31)',
    backdropFilter: 'blur(20px)',
    borderRadius: '16px',
    padding: '5px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
    border: '1px solid rgba(255, 255, 255, 0.93)',
    cursor: 'pointer'
  }}>
    <div style={{
      background: 'white',
      borderRadius: '12px',
      overflow: 'hidden',
      border: '1px solid rgba(213, 213, 213, 0.5)',
      position: 'relative'
    }}>
      {/* ✅ Podcast Background Image with Date Overlay */}
      <div style={{
        backgroundImage: `url(${bgImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        height: '130px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative'
      }}>
        {/* Date Overlay */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(180deg, rgba(0, 0, 0, 0.15) 0%, rgba(0, 0, 0, 0.13) 50%, rgba(0, 0, 0, 0.18) 100%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            fontSize: '30px',
            fontWeight: '700',
            color: 'white',
            marginBottom: '4px',
            textTransform: 'uppercase',
            textShadow: '0 2px 8px rgba(0,0,0,0.3)'
          }}>
            {new Date(podcast.savedAt).toLocaleDateString('en-US', { weekday: 'short' })}
          </div>
          <div style={{
            fontSize: '11px',
            fontWeight: '600',
            color: 'rgba(255,255,255,0.9)',
            textShadow: '0 1px 4px rgba(0,0,0,0.3)'
          }}>
            {new Date(podcast.savedAt).toLocaleDateString('en-US', { 
              month: 'long',
              day: 'numeric',
              year: 'numeric'
            })}
          </div>
        </div>

        {/* ✅ Clickable Saved Badge (removes podcast) */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            removeSavedPodcast(podcast.id);
          }}
          style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            width: '32px',
            height: '32px',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: '50%',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            cursor: 'pointer',
            transition: 'all 0.2s',
            padding: 0,
            zIndex: 10
          }}
        >
          <svg 
            width="16" 
            height="16" 
            fill="#0e5092ff" 
            viewBox="0 0 24 24" 
            stroke="#0e5092ff"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
          </svg>
        </button>
      </div>

      <div style={{ padding: '12px' }}>
        <div style={{ 
          fontSize: '13px', 
          fontWeight: '600', 
          color: '#1f2937', 
          marginBottom: '10px', 
          lineHeight: '1.4', 
          minHeight: '38px', 
          display: '-webkit-box', 
          WebkitLineClamp: 2, 
          WebkitBoxOrient: 'vertical', 
          overflow: 'hidden',
          textAlign: 'left'
        }}>
          {podcast.title}
        </div>

        <div style={{ 
          fontSize: '9px', 
          color: '#9ca3af', 
          marginBottom: '10px', 
          display: 'flex', 
          alignItems: 'center', 
          gap: '4px' 
        }}>
        </div>

        {/* ✅ Only Play Button - No Remove Button */}
        <button
          aria-label="Play podcast" 
          onClick={() => handlePlaySavedPodcast(podcast)}
          style={{
            width: '100%',
            padding: '8px',
            background: '#000000ff',
            color: 'white',
            border: 'none',
            borderRadius: '20px',
            fontSize: '10px',
            fontWeight: '500',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            transition: 'all 0.2s'
          }}
        >
          Play Podcast
        </button>
      </div>
    </div>
  </div>
);
                })}
            </div>
          </div>
        </>
      )}

      {/* Saved Articles Section - unchanged */}
      {savedArticles.length > 0 && (
        <>
          <h2 style={{ 
            fontSize: '13px', 
            fontWeight: '600', 
            color: '#1f2937', 
            margin: '24px 0 16px 0', 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px' 
          }}>
            <svg width="16" height="16" fill="#dfebffff" viewBox="0 0 24 24" stroke="#5596ffff">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
            </svg>
            Saved Articles ({savedArticles.length})
          </h2>
          
          <div style={{ marginLeft: '-16px', marginRight: '-16px', marginBottom: '32px' }}>
            <div style={customScrollStyle} className="custom-scroll">
{savedArticles
  .sort((a, b) => (b.savedAt || 0) - (a.savedAt || 0)) // ✅ Sort by newest first
  .map((article, idx) => (
                    <div key={idx} style={{ 
                  flexShrink: 0,
                  width: '200px',
                  background: 'rgba(255, 255, 255, 0.31)',
                  backdropFilter: 'blur(20px)',
                  borderRadius: '16px',
                  padding: '5px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.93)',
                  transition: 'transform 0.3s ease',
                  cursor: 'pointer'
                }}>
                  <div style={{
                    background: 'white',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    border: '1px solid rgba(213, 213, 213, 0.5)',
                    position: 'relative'
                  }}>
                    <div style={{ position: 'relative' }}>
                      <img 
                        src={article.image || 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=800&q=80'} 
                        alt={article.title} 
                        style={{ width: '100%', height: '130px', objectFit: 'cover' }} 
                      />
                      
                      <div style={{ 
                        position: 'absolute', 
                        top: '10px', 
                        left: '10px', 
                        background: 'rgba(255, 255, 255, 0.95)', 
                        backdropFilter: 'blur(10px)', 
                        padding: '4px 10px', 
                        borderRadius: '6px', 
                        fontSize: '10px', 
                        fontWeight: '700', 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '4px', 
                        color: article.sourceColor 
                      }}>
                        <img src={article.sourceLogo} alt={article.sourceName} style={{ height: '12px', objectFit: 'contain' }} />
                      </div>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemoveArticle(article.id); 
                        }}
                        style={{
                          position: 'absolute',
                          top: '10px',
                          right: '10px',
                          width: '32px',
                          height: '32px',
                              background: 'rgba(255, 255, 255, 0.95)',
                          backdropFilter: 'blur(10px)',
                          borderRadius: '50%',
                          border: 'none',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          padding: 0
                        }}
                      >
                        <svg 
                          width="16" 
                          height="16" 
                          fill="#0e5092ff" 
                          viewBox="0 0 24 24" 
                          stroke="#0e5092ff"
                          strokeWidth={2}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                        </svg>
                      </button>
                    </div>

                    <div style={{ padding: '12px' }}>
                      <div style={{ 
                        fontSize: '13px', 
                        fontWeight: '600', 
                        color: '#1f2937', 
                        marginBottom: '10px', 
                        lineHeight: '1.4', 
                        minHeight: '38px', 
                        display: '-webkit-box', 
                        WebkitLineClamp: 2, 
                        WebkitBoxOrient: 'vertical', 
                        overflow: 'hidden',
                        textAlign: 'left'
                      }}>
                        {article.title}
                      </div>

                      <div style={{ 
                        fontSize: '9px', 
                        color: '#9ca3af', 
                        marginBottom: '10px', 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '4px' 
                      }}>
                        Saved {article.savedAt 
                          ? new Date(article.savedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                          : 'Recently'
                        }
                      </div>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const mockSourceData = {
                            name: article.sourceName,
                            logo: article.sourceLogo,
                            color: article.sourceColor,
                            stories: [{
                              title: article.title,
                              time: article.savedAt 
                                ? `Saved ${new Date(article.savedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                                : 'Recently',
                              image: article.image,
                              description: article.description,
                              source: article.source
                            }]
                          };
                          setSelectedNewsSource({ sourceId: 'saved', sourceData: mockSourceData });
                        }}
                        style={{
                          width: '100%',
                          padding: '8px',
                          background: 'white',
                          color: '#272727',
                          border: '1px solid #e5e7eb',
                          borderRadius: '20px',
                          fontSize: '10px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#f9fafb';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'white';
                        }}
                      >
                        Read More
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </>
  );
};
const handleOpenCompanyStation = (company) => {
  console.log(`🎙️ Opening station: ${company.name}`);
  
  if (audioRef.current) {
    audioRef.current.pause();
    setIsPodcastPlaying(false);
  }
  setCurrentPodcast(null);
  setShowTranscriptView(false);
  
  setSelectedCompany(company);
  setShowCompanyStation(true);
  
  // ✅ Auto-refresh for Montclarion/MSU
  if (company.ticker === 'TheMontclarion' || company.isLocal) {
    console.log('🎓 Montclarion detected - auto-refresh news');
    setTimeout(() => {
      handleRefreshNews();
    }, 500);
  }
};

const CompanyStationCard = ({ company, onClick }) => (
  <div style={{ flexShrink: 0, width: '200px' }}>
    <div style={{ 
      background: 'rgba(255, 255, 255, 0.31)', 
      backdropFilter: 'blur(20px)', 
      borderRadius: '16px', 
      padding: '5px', 
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)', 
      border: '1px solid rgba(255, 255, 255, 0.93)', 
      transition: 'transform 0.3s ease', 
      cursor: 'pointer' 
    }}>
      <div style={{ 
        background: 'white', 
        borderRadius: '12px', 
        padding: '16px', 
        border: '1px solid rgba(213, 213, 213, 0.5)' 
      }}>
        <div style={{ 
          marginBottom: '16px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '12px'
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            background: '#f9fafb',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '12px',
            border: '2px solid #e5e7eb'
          }}>
            <img 
              src={company.logos[0]}
              alt={company.name}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain'
              }}
              data-fallback-index="0"
              data-ticker={company.ticker}
              data-color={company.color}
              onError={(e) => {
                const currentIndex = parseInt(e.target.getAttribute('data-fallback-index'));
                const ticker = e.target.getAttribute('data-ticker');
                const color = e.target.getAttribute('data-color');
                
                // Find the company data
                const companyData = COMPANIES_WATCHLIST.find(c => c.ticker === ticker);
                if (!companyData) return;
                
                const nextIndex = currentIndex + 1;
                
                // Try next fallback
                if (nextIndex < companyData.logos.length) {
                  e.target.src = companyData.logos[nextIndex];
                  e.target.setAttribute('data-fallback-index', nextIndex);
                } else {
                  // All fallbacks failed - show ticker letter
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = `<div style="font-size: 24px; font-weight: 700; color: ${color}">${ticker.charAt(0)}</div>`;
                }
              }}
            />
          </div>
          
          <div style={{ textAlign: 'center' }}>
            <div style={{ 
              fontSize: '16px', 
              fontWeight: '700', 
              color: '#1f2937',
              marginBottom: '2px'
            }}>
              {company.ticker}
            </div>
            <div style={{ 
              fontSize: '10px', 
              color: '#6b7280',
              lineHeight: '1.3'
            }}>
              {company.name}
            </div>
          </div>
        </div>

        <div style={{
          padding: '12px',
          marginBottom: '12px',
        }}>
          <div style={{
            fontSize: '10px',
            color: '#6b7280',
            marginBottom: '4px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            fontWeight: '600'
          }}>
            Live Station
          </div>
          <div style={{
            fontSize: '12px',
            color: '#1f2937',
            fontWeight: '500'
          }}>
            Latest news & updates
          </div>
        </div>

        <button
          onClick={onClick}
          style={{ 
            width: '100%', 
            padding: '10px', 
            background: company.color || '#070707ff', 
            color: 'white', 
            border: 'none', 
            borderRadius: '20px', 
            fontSize: '11px', 
            fontWeight: '600', 
            cursor: 'pointer', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            gap: '6px',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <Radio size={14} />
          Tune In
        </button>
      </div>
    </div>
  </div>
);
// Crypto state
const [cryptoData, setCryptoData] = useState([]);
const [selectedCryptos, setSelectedCryptos] = useState(['bitcoin', 'ethereum', 'binancecoin', 'cardano', 'solana', 'ripple', 'polkadot', 'dogecoin']);

const handlePlayDailyBrief = async (brief) => {
  console.log('🎙️ handlePlayDailyBrief called with:', brief);
  
  if (!brief?.audioUrl) {
    alert('Audio not ready yet');
    return;
  }

  if (!brief.dialogue || brief.dialogue.length === 0) {
    alert('Brief data is incomplete. Please try again.');
    return;
  }

  try {
    // ✅ CRITICAL: Stop and clear any existing audio first
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.removeAttribute('src');
      audioRef.current.load(); // This clears the buffer
    }
    
    // Reset all playback states
    setIsPodcastPlaying(false);
    setActiveLineIndex(0);
    setCurrentAudioTime(0);
    setAudioDuration(0);
    
    console.log('✅ Audio cleared, setting up new brief...');
    
    // Mark as listened
    if (firestoreService && brief.id) {
      await firestoreService.markDailyBriefCatchUpAsListened(brief.id);
      setSavedDailyBriefs(prev => 
        prev.map(b => b.id === brief.id 
          ? { ...b, listened: true, playCount: (b.playCount || 0) + 1 } 
          : b
        )
      );
    }
    
    // ✅ CRITICAL: Build moduleData from brief with proper coordinate handling
    let moduleData = {};
    
    if (brief.moduleData) {
      moduleData = { ...brief.moduleData };
      
      // ✅ FIX: Ensure traffic coordinates are properly formatted
      if (moduleData.traffic) {
        console.log('📍 Processing traffic data:', moduleData.traffic);
        
        // Ensure coordinates are in the correct format
        if (typeof moduleData.traffic.originCoords === 'string') {
          const [lat, lon] = moduleData.traffic.originCoords.split(',').map(s => parseFloat(s.trim()));
          moduleData.traffic.originLat = lat;
          moduleData.traffic.originLon = lon;
        }
        
        if (typeof moduleData.traffic.destCoords === 'string') {
          const [lat, lon] = moduleData.traffic.destCoords.split(',').map(s => parseFloat(s.trim()));
          moduleData.traffic.destLat = lat;
          moduleData.traffic.destLon = lon;
        }
        
        console.log('✅ Traffic coordinates processed:', {
          origin: `${moduleData.traffic.originLat},${moduleData.traffic.originLon}`,
          dest: `${moduleData.traffic.destLat},${moduleData.traffic.destLon}`
        });
      }
    }
    
    // Set up new podcast data with processed moduleData
    const podcastData = {
      audioUrl: brief.audioUrl,
      dialogue: brief.dialogue,
      title: brief.title || `Daily Brief - ${brief.generatedFor}`,
      duration: brief.duration || 0,
      chapters: brief.chapters || [],
      type: 'daily-brief-catchup',
      id: brief.id,
      generatedAt: brief.generatedAt,
      listened: true,
      coverImage: brief.coverImage,
      moduleData: moduleData // ✅ Include processed moduleData
    };
    
    console.log('📦 Setting new podcast with moduleData:', podcastData);
    
    // ✅ Set podcast AFTER clearing old audio
    setCurrentPodcast(podcastData);
    
    // ✅ Show transcript view
    setShowTranscriptView(true);
    
    // Wait a tick for state to update
    setTimeout(() => {
      if (audioRef.current) {
        const audio = audioRef.current;
        
        console.log('🎵 Loading new audio:', brief.audioUrl);
        audio.src = brief.audioUrl;
        
        const handleCanPlay = () => {
          console.log('✅ Audio ready, duration:', audio.duration);
          
          if (audio.duration && !isNaN(audio.duration) && isFinite(audio.duration)) {
            setAudioDuration(audio.duration);
          }
          
          // Auto-play
          setIsPodcastPlaying(true);
          audio.play()
            .then(() => console.log('✅ Playing'))
            .catch(err => {
              console.error('❌ Autoplay blocked:', err);
              setIsPodcastPlaying(false);
            });
          
          audio.removeEventListener('canplay', handleCanPlay);
        };
        
        audio.addEventListener('canplay', handleCanPlay, { once: true });
        audio.load();
      }
    }, 100);
    
  } catch (error) {
    console.error('❌ Error in handlePlayDailyBrief:', error);
    alert('Failed to play Daily Brief. Please try again.');
  }
};

const initializeMap = async (route) => {
  if (map.current) return; // Map already initialized

    mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN ||'pk.eyJ1IjoiZnJhbnpvMTAwMSIsImEiOiJjbTlkN2p3bGcwemg0MmxxM3dmenMzcHZtIn0.OawlpbrFzFwbmlgIOr2AIw';

  // Create map
  map.current = new mapboxgl.Map({
    container: mapContainer.current,
    style: 'mapbox://styles/mapbox/standard',
    center: [route.originLon, route.originLat],
    zoom: 15,
    pitch: 45,
    bearing: 0
  });

  // Add navigation controls
  map.current.addControl(new mapboxgl.NavigationControl());

  // Wait for map to load
  map.current.on('load', async () => {
    // Add origin marker (green)
    new mapboxgl.Marker({ color: '#10b981' })
      .setLngLat([route.originLon, route.originLat])
      .setPopup(new mapboxgl.Popup().setHTML(`<strong>Start</strong><br>${route.origin}`))
      .addTo(map.current);

    // Add destination marker (red)
    new mapboxgl.Marker({ color: '#ef4444' })
      .setLngLat([route.destLon, route.destLat])
      .setPopup(new mapboxgl.Popup().setHTML(`<strong>Destination</strong><br>${route.destination}`))
      .addTo(map.current);

    // Fetch route from Mapbox Directions API
    const directionsUrl = `https://api.mapbox.com/directions/v5/mapbox/driving/${route.originLon},${route.originLat};${route.destLon},${route.destLat}?geometries=geojson&access_token=${mapboxgl.accessToken}`;
    
    const response = await fetch(directionsUrl);
    const data = await response.json();
    
    if (data.routes && data.routes[0]) {
      const routeGeoJSON = data.routes[0].geometry;

      // Add route source
      map.current.addSource('route', {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: routeGeoJSON
        }
      });

      // Add route layer
      map.current.addLayer({
        id: 'route',
        type: 'line',
        source: 'route',
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': '#3b82f6',
          'line-width': 6,
          'line-opacity': 0.8
        }
      });

      // Add route outline
      map.current.addLayer({
        id: 'route-outline',
        type: 'line',
        source: 'route',
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': '#1e40af',
          'line-width': 8,
          'line-opacity': 0.4
        }
      });

      // Start animation after 1 second
      setTimeout(() => {
        animateMapRoute(route);
      }, 1000);
    }
  });
};

const animateMapRoute = async (route) => {
  setIsMapAnimating(true);

  const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));
  
  const flyTo = (options) => {
    return new Promise((resolve) => {
      map.current.flyTo({
        ...options,
        essential: true
      });
      setTimeout(resolve, options.duration);
    });
  };

  const calculateBearing = (lat1, lon1, lat2, lon2) => {
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const y = Math.sin(dLon) * Math.cos(lat2 * Math.PI / 180);
    const x = Math.cos(lat1 * Math.PI / 180) * Math.sin(lat2 * Math.PI / 180) -
              Math.sin(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.cos(dLon);
    const bearing = Math.atan2(y, x) * 180 / Math.PI;
    return (bearing + 360) % 360;
  };

  // Step 1: Zoom in on origin
  await flyTo({
    center: [route.originLon, route.originLat],
    zoom: 17,
    pitch: 60,
    bearing: 0,
    duration: 2000
  });

  await sleep(1000);

  // Step 2: Zoom out to see full route
  const bounds = new mapboxgl.LngLatBounds()
    .extend([route.originLon, route.originLat])
    .extend([route.destLon, route.destLat]);

  map.current.fitBounds(bounds, {
    padding: { top: 100, bottom: 100, left: 100, right: 100 },
    pitch: 45,
    bearing: calculateBearing(
      route.originLat, 
      route.originLon, 
      route.destLat, 
      route.destLon
    ),
    duration: 3000
  });

  await sleep(4000);

  // Step 3: Zoom in on destination
  await flyTo({
    center: [route.destLon, route.destLat],
    zoom: 17,
    pitch: 60,
    bearing: calculateBearing(
      route.originLat, 
      route.originLon, 
      route.destLat, 
      route.destLon
    ),
    duration: 2000
  });

  await sleep(1500);

  // Step 4: Final overview
  map.current.fitBounds(bounds, {
    padding: { top: 80, bottom: 80, left: 80, right: 80 },
    pitch: 30,
    bearing: 0,
    duration: 2000
  });

  setIsMapAnimating(false);
};

const closeMapView = () => {
  if (map.current) {
    map.current.remove();
    map.current = null;
  }
  setShowMapView(false);
  setSelectedRouteForMap(null);
  setIsMapAnimating(false);
};

// Initialize map when route is selected
useEffect(() => {
  if (showMapView && selectedRouteForMap && mapContainer.current) {
    initializeMap(selectedRouteForMap);
  }
}, [showMapView, selectedRouteForMap]);
const [dialogueTimestamps, setDialogueTimestamps] = useState([]);


const fetchCryptoData = async () => {
  try {
    console.log('💰 Fetching crypto data from CoinGecko...');
    
    const cryptoIds = selectedCryptos.join(',');
    const response = await fetch(
      `https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&ids=${cryptoIds}&order=market_cap_desc&sparkline=false&price_change_percentage=24h`,
      {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      }
    );
    
    if (!response.ok) {
      console.error(`❌ CoinGecko API returned HTTP ${response.status}`);
      return;
    }
    
    const data = await response.json();
    
    if (data && data.length > 0) {
      const formattedData = data.map(coin => ({
        id: coin.id,
        symbol: coin.symbol.toUpperCase(),
        name: coin.name,
        price: coin.current_price,
        percent: coin.price_change_percentage_24h?.toFixed(2) || '0.00',
        marketCap: coin.market_cap,
        image: coin.image
      }));
      
      setCryptoData(formattedData);
      console.log(`✅ Crypto data loaded: ${formattedData.length} coins`);
    }
  } catch (error) {
    console.error('❌ Error fetching crypto data:', error);
  }
};

// Add useEffect to fetch crypto data
useEffect(() => {
  if (showCryptoCard) {
    fetchCryptoData();
    const interval = setInterval(fetchCryptoData, 60000); // Update every minute
    return () => clearInterval(interval);
  }
}, [selectedCryptos, showCryptoCard]);
useEffect(() => {
  const handleGlobalBriefPlay = (event) => {
    const briefData = event.detail;
    console.log('🎙️ Received play event for Daily Brief:', briefData.title);
    
    setCurrentPodcast({
      audioUrl: briefData.audioUrl,
      dialogue: briefData.dialogue,
      title: briefData.title,
      duration: briefData.duration,
      chapters: briefData.chapters || [],
      type: 'daily-brief-catchup',
      id: briefData.id
    });
    
    setShowTranscriptView(true);
    setActiveLineIndex(0);
    
    if (audioRef.current) {
      const audio = audioRef.current;
      audio.src = briefData.audioUrl;
      audio.load();
      audio.play()
        .then(() => {
          setIsPodcastPlaying(true);
          console.log('✅ Daily Brief playing');
        })
        .catch(err => {
          console.error('❌ Autoplay failed:', err);
        });
    }
  };
  
  window.addEventListener('playDailyBrief', handleGlobalBriefPlay);
  
  return () => {
    window.removeEventListener('playDailyBrief', handleGlobalBriefPlay);
  };
}, []);

useEffect(() => {
  const audio = audioRef.current;
  if (!audio) return;

  const handleEnded = async () => {
    console.log('🎵 Audio ended');
    setIsPodcastPlaying(false);
    
    // ✅ CRITICAL: Use isSaved state instead of checking array
    if (isSaved) {
      console.log('✅ Podcast already saved - auto-closing player in 2 seconds');
      
      // ✅ Auto-close everything after 2 seconds if already saved
      setTimeout(() => {
        setShowTranscriptView(false);
        setCurrentPodcast(null);
      }, 2000);
      
    } else {
      console.log('❓ Podcast not saved - showing save prompt');
      
      // ✅ Show save prompt modal
      setTimeout(() => {
        setShowSavePrompt(true);
      }, 500);
    }
  };

  audio.addEventListener('ended', handleEnded);
  
  return () => {
    audio.removeEventListener('ended', handleEnded);
  };
}, [isSaved]); // ✅ CHANGE THIS LINE - was [savedPodcasts, currentPodcast, showTranscriptView]
// Initialize service - IMPROVED VERSION with fallback
useEffect(() => {
  const initService = async () => {
    try {
      console.log('🔄 Initializing Firestore service...');
      
      // ✅ CRITICAL FIX: Wait for Firebase Auth to be ready
      const auth = getAuth();
      
      // Wait for auth state to resolve
      await new Promise((resolve) => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
          unsubscribe();
          resolve(user);
        });
      });
      
      const currentUser = auth.currentUser;
      
      if (!currentUser) {
        console.error('❌ No user signed in - redirecting to login');
        alert('Please sign in to access podcast features');
        // Navigate back to chat or login
        return;
      }
      
      console.log('✅ User authenticated:', currentUser.email);
      
      // Now initialize or reuse service
      if (!window.firestoreService) {
        console.warn('⚠️ window.firestoreService not found, creating new instance...');
        
        const FirestorePersistentStorageService = (await import('./services/FirestorePersistentStorageService')).default;
        window.firestoreService = new FirestorePersistentStorageService();
      }
      
      // Wait for service to be ready
      console.log('⏳ Waiting for service to be ready...');
      await window.firestoreService.waitForReady(15000);
      
      setfirestoreService(window.firestoreService);
      console.log('✅ Podcast service initialized and ready');
      
      // Verify user is set
      const userId = window.firestoreService.userId || window.firestoreService.currentUser?.uid;
      if (!userId) {
        console.error('❌ Service initialized but no userId');
      } else {
        console.log('✅ User ID confirmed:', userId);
      }
      
    } catch (error) {
      console.error('❌ Failed to initialize service:', error);
      alert('Failed to initialize storage service. Please refresh the page.');
    }
  };
  
  initService();
}, []);

// ✅ STEP 1: Source Configuration with better feeds
// ✅ STEP 1: Source Configuration organized by categories
const newsSources = {
  news: [
    { id: 'cnn', name: 'CNN', favicon: 'https://www.cnn.com/favicon.ico' },
    { id: 'bbc', name: 'BBC', favicon: 'https://images.icon-icons.com/70/PNG/512/bbc_news_14062.png' },
    { id: 'abc', name: 'ABC', favicon: 'https://abc.com/favicon.ico' },
    { id: 'nbc', name: 'NBC', favicon: 'https://www.nbc.com/favicon.ico' },
    { id: 'fox', name: 'FOX', favicon: 'https://www.foxnews.com/favicon.ico' },
    { id: 'reuters', name: 'Reuters', favicon: 'https://www.reuters.com/favicon.ico' },
    { id: 'guardian', name: 'Guardian', favicon: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRKdlviWRPLIlr0pTbbcHomBEKDoIo0br44wg&s' },
    { id: 'ap', name: 'AP News', favicon: 'https://apnews.com/favicon.ico' }
  ],
  sports: [
    { id: 'espn', name: 'ESPN', favicon: 'https://www.espn.com/favicon.ico' },
    { id: 'yahoo-sports', name: 'Yahoo Sports', favicon: 'https://sports.yahoo.com/favicon.ico' }
  ],
  tech: [
    { id: 'techcrunch', name: 'TechCrunch', favicon: 'https://techcrunch.com/wp-content/uploads/2015/02/cropped-cropped-favicon-gradient.png' }
  ],
  business: [
    { id: 'cnbc', name: 'CNBC', favicon: 'https://www.cnbc.com/favicon.ico' }
  ],
  entertainment: [
    { id: 'variety', name: 'Variety', favicon: 'https://www.nasdaq.com/sites/acquia.prod/files/2020/09/18/variety.jpg' }
  ]
};

// ✅ Source metadata for RSS feeds and colors

const newsSourceMetadata = {
  cnn: { 
    name: 'CNN',  // ✅ Added name
    color: '#cc0000', 
    logo: 'https://www.cnn.com/favicon.ico',
    feeds: ['http://rss.cnn.com/rss/edition.rss', 'http://rss.cnn.com/rss/cnn_topstories.rss']
  },
  bbc: { 
    name: 'BBC',  // ✅ Added name
    color: '#000000', 
    logo: 'https://images.icon-icons.com/70/PNG/512/bbc_news_14062.png',
    feeds: ['http://feeds.bbci.co.uk/news/rss.xml']
  },
  abc: { 
    name: 'ABC',  // ✅ Added name
    color: '#0000ff', 
    logo: 'https://abc.com/favicon.ico',
    feeds: ['https://abcnews.go.com/abcnews/topstories']
  },
  nbc: { 
    name: 'NBC',  // ✅ Added name
    color: '#800080', 
    logo: 'https://www.nbc.com/favicon.ico',
    feeds: ['https://feeds.nbcnews.com/nbcnews/public/news']
  },
  fox: { 
    name: 'FOX',  // ✅ Added name
    color: '#003366', 
    logo: 'https://www.foxnews.com/favicon.ico',
    feeds: ['https://moxie.foxnews.com/google-publisher/latest.xml']
  },
  techcrunch: { 
    name: 'TechCrunch',  // ✅ Added name
    color: '#0a9e32', 
    logo: 'https://techcrunch.com/wp-content/uploads/2015/02/cropped-cropped-favicon-gradient.png',
    feeds: ['https://techcrunch.com/feed/']
  },
  espn: { 
    name: 'ESPN',  // ✅ Added name
    color: '#ff0000', 
    logo: 'https://www.espn.com/favicon.ico',
    feeds: ['https://www.espn.com/espn/rss/news']
  },
  reuters: { 
    name: 'Reuters',  // ✅ Added name
    color: '#ff8000', 
    logo: 'https://www.reuters.com/favicon.ico',
    feeds: ['https://www.reutersagency.com/feed/']
  },
  guardian: { 
    name: 'Guardian',  // ✅ Added name
    color: '#052962', 
    logo: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRKdlviWRPLIlr0pTbbcHomBEKDoIo0br44wg&s',
    feeds: ['https://www.theguardian.com/world/rss']
  },
  ap: { 
    name: 'AP News',  // ✅ Added name
    color: '#d71920', 
    logo: 'https://apnews.com/favicon.ico',
    feeds: ['https://rsshub.app/apnews/topics/apf-topnews']
  },
  cnbc: {
    name: 'CNBC',  // ✅ CRITICAL: This was missing!
    color: '#005ead',
    logo: 'https://www.cnbc.com/favicon.ico',
    feeds: ['https://www.cnbc.com/id/100003114/device/rss/rss.html']
  },
  variety: {
    name: 'Variety',  // ✅ Added name
    color: '#000000',
    logo: 'https://www.nasdaq.com/sites/acquia.prod/files/2020/09/18/variety.jpg',
    feeds: ['https://variety.com/feed/']
  },
  'yahoo-sports': {
    name: 'Yahoo Sports',  // ✅ Added name
    color: '#7e1fff',
    logo: 'https://sports.yahoo.com/favicon.ico',
    feeds: ['https://sports.yahoo.com/rss/']
  }
};
// ✅ STEP 2: Smart Placeholder Images (category-based)
const getSmartPlaceholder = (title, sourceName) => {
  const text = (title + ' ' + sourceName).toLowerCase();
  
  const categories = [
    {
      keywords: ['ai', 'tech', 'software', 'app', 'digital', 'crypto', 'blockchain', 'startup'],
      image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&q=80'
    },
    {
      keywords: ['business', 'finance', 'market', 'stock', 'economy', 'investment', 'trade'],
      image: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=800&q=80'
    },
    {
      keywords: ['sports?', 'football', 'basketball', 'game', 'nba', 'nfl', 'soccer', 'baseball'],
      image: 'https://images.unsplash.com/photo-1461896836934-ffe607ba8211?w=800&q=80'
    },
    {
      keywords: ['movie', 'music', 'entertainment', 'celebrity', 'film', 'actor', 'singer'],
      image: 'https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?w=800&q=80'
    },
    {
      keywords: ['health', 'medical', 'doctor', 'hospital', 'medicine', 'wellness'],
      image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&q=80'
    },
    {
      keywords: ['politics', 'election', 'government', 'congress', 'senate', 'president'],
      image: 'https://images.unsplash.com/photo-1529107386315-e1a2ed48a620?w=800&q=80'
    },
    {
      keywords: ['climate', 'environment', 'weather', 'nature', 'earth', 'energy'],
      image: 'https://images.unsplash.com/photo-1569163139394-de4798aa62b5?w=800&q=80'
    }
  ];
  
  for (const category of categories) {
    for (const keyword of category.keywords) {
      if (text.includes(keyword)) {
        return category.image;
      }
    }
  }
  
  // Default: News/World
  return 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=800&q=80';
};

// ✅ STEP 3: HTML Entity Decoder
const decodeHtmlEntities = (text) => {
  if (!text) return '';
  return text
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&apos;/g, "'")
    .replace(/&#x27;/g, "'")
    .replace(/&#x2F;/g, '/')
    .replace(/&#160;/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&rsquo;/g, "'")
    .replace(/&lsquo;/g, "'")
    .replace(/&rdquo;/g, '"')
    .replace(/&ldquo;/g, '"')
    .replace(/&mdash;/g, '—')
    .replace(/&ndash;/g, '–')
    .replace(/&#(\d+);/g, (match, dec) => String.fromCharCode(dec))
    .replace(/&#x([0-9a-f]+);/gi, (match, hex) => String.fromCharCode(parseInt(hex, 16)))
    .trim();
};

// ✅ STEP 4: Time Formatter
const getRelativeTimeString = (dateString) => {
  try {
    const articleDate = new Date(dateString);
    const now = new Date();
    const diffMs = now - articleDate;
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return articleDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch (e) {
    return 'Recently';
  }
};
const handleRefreshNews = async (allowPartial = false) => {
  // ✅ CRITICAL: Prevent duplicate simultaneous calls
  if (refreshLockRef.current) {
    console.log('🔒 Refresh already in progress, skipping...');
    return;
  }
  
  if (isRefreshing) {
    console.log('⏳ Already refreshing (state check)...');
    return;
  }

  const uniqueSelectedSources = [...new Set(selectedNewsFeeds)];

  if (!uniqueSelectedSources || uniqueSelectedSources.length === 0) {
    console.warn('⚠️ No news sources selected');
    return;
  }

  try {
    // ✅ SET LOCK
    refreshLockRef.current = true;
    
    setIsRefreshing(true);
    setIsRefreshingNews(true);
    
    console.log(`🔄 Refreshing ${allowPartial ? '(with fallback)' : '(manual)'}:`, uniqueSelectedSources);
    
    const sourceConfig = getDefaultSourceConfig();
    const result = await refreshNewsViaBackendSimple(uniqueSelectedSources, sourceConfig);
    
    // ✅ Check result
    const validSources = result.loadedCards || [];
    
    // ✅ Allow partial results if:
    // - allowPartial = true (auto-refresh on init)
    // - At least 2 sources loaded (not a complete failure)
    const hasEnoughData = validSources.length >= 2;
    
    if (!result.success && validSources.length === 0) {
      console.error('❌ Complete refresh failure, checking fallback...');
      
      // Try to use old localStorage cache as fallback
      const oldCache = localStorage.getItem('news_today');
      if (oldCache && allowPartial) {
        try {
          const cached = JSON.parse(oldCache);
          console.log('⚠️ Using old cache as fallback');
          
          setNewsData(cached.data);
          setLoadedNewsCards(Object.keys(cached.data));
          setLastRefreshTime(new Date());
          
          return;
        } catch (e) {
          console.warn('Fallback cache corrupted');
        }
      }
      
      return;
    }
    
    // ✅ If we have partial results (at least 2 sources)
    if (validSources.length >= 2 || (allowPartial && validSources.length > 0)) {
      console.log(`✅ Loaded: ${validSources.length} sources (${allowPartial ? 'allowing partial' : 'manual'})`);
      
      const cleanNewsData = {};
      validSources.forEach(key => {
        cleanNewsData[key] = result.newsData[key];
      });
      
      // Update state with partial results
      setNewsData(cleanNewsData);
      setLoadedNewsCards(validSources);
      setLastRefreshTime(new Date());
      
      // ✅ CRITICAL: Save partial results to localStorage!
      saveToLocalStorage(cleanNewsData, validSources);
      
      // Also save to Firestore as backup
      if (firestoreService?.isReady()) {
        try {
          await firestoreService.saveNewsCacheWithDate(cleanNewsData, validSources);
          console.log('✅ Backed up to Firestore');
        } catch (e) {
          console.warn('⚠️ Firestore backup skipped');
        }
      }
    }
    
  } catch (error) {
    console.error('❌ Refresh error:', error);
    
    // ✅ If error during auto-refresh, try fallback cache
    if (allowPartial) {
      const fallback = localStorage.getItem('news_today');
      if (fallback) {
        try {
          const cached = JSON.parse(fallback);
          console.log('⚠️ Error occurred, using fallback cache');
          setNewsData(cached.data);
          setLoadedNewsCards(Object.keys(cached.data));
        } catch (e) {}
      }
    }
  } finally {
    // ✅ ALWAYS RELEASE LOCK
    refreshLockRef.current = false;
    setIsRefreshing(false);
    setIsRefreshingNews(false);
  }
};
// ✅ Smart Background Retry - Only retries missing sources
const retryMissingSources = async (allUserSources, currentLoadedSources, retryAttempts = {}) => {
  // Find which sources are missing
  const missingSources = allUserSources.filter(
    source => !currentLoadedSources.includes(source)
  );
  
  if (missingSources.length === 0) {
    console.log('✅ All sources loaded! No retry needed.');
    setIsBackgroundRetrying(false);
    setMissingSourceRetries({});
    return;
  }
  
  // Check if any source has hit max retries (5 attempts)
  const sourcesStillRetrying = missingSources.filter(source => {
    const attempts = retryAttempts[source] || 0;
    return attempts < 5;
  });
  
  if (sourcesStillRetrying.length === 0) {
    console.log('⏹️ All missing sources hit max retries (5). Stopping.');
    setIsBackgroundRetrying(false);
    return;
  }
  
  console.log(`🔄 Retrying ${sourcesStillRetrying.length} missing sources:`, sourcesStillRetrying);
  console.log('📊 Retry counts:', sourcesStillRetrying.map(s => `${s}: ${(retryAttempts[s] || 0) + 1}/5`));
  
  try {
    setIsBackgroundRetrying(true);
    
    const sourceConfig = getDefaultSourceConfig();
    const result = await refreshNewsViaBackendSimple(sourcesStillRetrying, sourceConfig);
    
    if (result.success && result.loadedCards.length > 0) {
      console.log(`✅ Successfully loaded ${result.loadedCards.length} missing sources:`, result.loadedCards);
      
      // ✅ MERGE new data with existing
      setNewsData(prev => ({
        ...prev,
        ...result.newsData
      }));
      
      setLoadedNewsCards(prev => {
        const updated = [...new Set([...prev, ...result.loadedCards])];
        console.log(`📊 Total sources now: ${updated.length}/${allUserSources.length}`);
        return updated;
      });
      
      // ✅ Update retry counts for sources that loaded
      const newRetryAttempts = { ...retryAttempts };
      result.loadedCards.forEach(source => {
        delete newRetryAttempts[source]; // Remove successful ones
      });
      
      // Increment retry count for sources that failed
      sourcesStillRetrying.forEach(source => {
        if (!result.loadedCards.includes(source)) {
          newRetryAttempts[source] = (retryAttempts[source] || 0) + 1;
        }
      });
      
      setMissingSourceRetries(newRetryAttempts);
      
      // ✅ Save updated data
      const mergedData = {
        ...newsData,
        ...result.newsData
      };
      const allLoaded = [...new Set([...currentLoadedSources, ...result.loadedCards])];
      
      saveToLocalStorage(mergedData, allLoaded);
      
      if (firestoreService?.isReady()) {
        try {
          await firestoreService.saveNewsCacheWithDate(mergedData, allLoaded);
        } catch (e) {
          console.warn('⚠️ Firestore backup skipped');
        }
      }
      
    } else {
      console.warn(`⚠️ Retry attempt failed for sources:`, sourcesStillRetrying);
      
      // Increment retry count for all sources that failed
      const newRetryAttempts = { ...retryAttempts };
      sourcesStillRetrying.forEach(source => {
        newRetryAttempts[source] = (retryAttempts[source] || 0) + 1;
      });
      
      setMissingSourceRetries(newRetryAttempts);
    }
    
  } catch (error) {
    console.error('❌ Retry error:', error);
    
    // Still increment retry count on error
    const newRetryAttempts = { ...retryAttempts };
    sourcesStillRetrying.forEach(source => {
      newRetryAttempts[source] = (retryAttempts[source] || 0) + 1;
    });
    
    setMissingSourceRetries(newRetryAttempts);
  } finally {
    setIsBackgroundRetrying(false);
  }
};
// ✅ SMART BACKGROUND WORKER - Auto-retry missing sources every 5 seconds
useEffect(() => {
  // Only run if:
  // 1. We have selected sources
  // 2. We have loaded some data
  // 3. Not currently refreshing manually
  // 4. Not already running a background retry
  if (!selectedNewsFeeds || selectedNewsFeeds.length === 0) {
    return;
  }
  
  if (isRefreshing || isBackgroundRetrying) {
    return;
  }
  
  if (!newsData || Object.keys(newsData).length === 0) {
    return; // Wait for initial load
  }
  
  // Check if we're missing any sources
  const currentLoaded = Object.keys(newsData);
  const missing = selectedNewsFeeds.filter(source => !currentLoaded.includes(source));
  
  if (missing.length === 0) {
    // All sources loaded - stop worker
    if (missingSourceRetries && Object.keys(missingSourceRetries).length > 0) {
      console.log('🎉 All sources now loaded! Stopping background worker.');
      setMissingSourceRetries({});
    }
    return;
  }
  
  // Check if any missing sources still have retries left
  const canRetry = missing.some(source => {
    const attempts = missingSourceRetries[source] || 0;
    return attempts < 5;
  });
  
  if (!canRetry) {
    console.log('⏹️ All missing sources exhausted retries. Stopping worker.');
    return;
  }
  
  // ✅ Start background retry timer (5 seconds)
  console.log(`🔍 Background worker: ${missing.length} sources still missing`);
  console.log(`   Missing: ${missing.join(', ')}`);
  console.log(`   Retry counts:`, missing.map(s => `${s}: ${missingSourceRetries[s] || 0}/5`).join(', '));
  
  const timer = setTimeout(() => {
    console.log('⏰ 5 seconds elapsed - attempting background retry...');
    retryMissingSources(selectedNewsFeeds, currentLoaded, missingSourceRetries);
  }, 5000);
  
  return () => {
    clearTimeout(timer);
  };
  
}, [
  selectedNewsFeeds,
  newsData,
  isRefreshing,
  isBackgroundRetrying,
  missingSourceRetries
]);
// ✅ Shimmer Watchdog - Auto-recovery if stuck loading
useEffect(() => {
  let shimmerTimer = null;
  
  // ✅ CRITICAL FIX: Don't run watchdog when viewing saved content
  if (activeTab === 'saved') {
    return; // Skip watchdog entirely for saved tab
  }
  
  // Only run watchdog if we're showing shimmer
  const isShowingShimmer = isRefreshing || newsLoadState === 'loading' || !newsData || Object.keys(newsData).length === 0;
  
  if (isShowingShimmer && !isRefreshing) {
    console.log('⏱️ Shimmer watchdog started (10s timeout)');
    
    shimmerTimer = setTimeout(() => {
      console.warn('⚠️ SHIMMER STUCK - Auto-recovering...');
      
      // ... rest of your recovery logic
    }, 10000);
  }
  
  return () => {
    if (shimmerTimer) {
      clearTimeout(shimmerTimer);
    }
  };
}, [isRefreshing, newsLoadState, newsData, activeTab]); // ✅ Add activeTab dependency
// ✅ Clear cache if day changed
const clearOldNewsCache = () => {
  try {
    const cached = localStorage.getItem('news_today');
    if (cached) {
      const data = JSON.parse(cached);
      const today = new Date().toISOString().split('T')[0];
      
      if (data.cachedDate !== today) {
        console.log(`📅 Day changed, clearing old cache`);
        localStorage.removeItem('news_today');
      }
    }
  } catch (e) {
    localStorage.removeItem('news_today');
  }
};

// Call on app load
useEffect(() => {
  clearOldNewsCache();
}, []);
const DEFAULT_USER_SETTINGS = {
  newsFeeds: ['abc', 'variety', 'techcrunch', 'espn'],
  newsCategories: {
    news: ['abc'],
    entertainment: ['variety'],
    tech: ['techcrunch'],
    sports: ['espn'],
    business: []
  },
  selectedStocks: ['AAPL', 'NVDA', 'SPX', 'TSLA'], // ✅ Changed default stocks
  selectedCryptos: ['bitcoin', 'ethereum'],
  showNewsCard: true,
  showStocksCard: true,
  showCryptoCard: false
};


// ✅ Check for new UTC day and auto-refresh silently
useEffect(() => {
  const checkForUTCNewDay = () => {
    const now = new Date();
    const utcDateString = now.toISOString().split('T')[0]; // YYYY-MM-DD in UTC
    
    const storedUTCDate = localStorage.getItem('lastNewsRefreshDateUTC');
    
    if (storedUTCDate !== utcDateString) {
      console.log(`🌅 NEW UTC DAY: ${storedUTCDate} → ${utcDateString}`);
      console.log('🔄 Silent auto-refresh of news...');
      
      localStorage.setItem('lastNewsRefreshDateUTC', utcDateString);
      localStorage.removeItem('news_today'); // Clear cache for fresh load
      
      // Call refresh after short delay
      setTimeout(() => {
        handleRefreshNews(true); // allowPartial=true = silent
      }, 500);
    }
  };
  
  checkForUTCNewDay();
  
  // Check every minute
  const interval = setInterval(checkForUTCNewDay, 60000);
  
  return () => clearInterval(interval);
}, [handleRefreshNews]);
useEffect(() => {
  if (!firestoreService?.isReady()) {
    console.log('⏳ Firestore not ready for snapshot listener');
    return;
  }

  console.log('📡 Setting up daily news snapshot listener...');
  
  const db = getFirestore();
  
  // ✅ LISTEN for snapshot changes
  const unsubscribe = onSnapshot(
    doc(db, 'global', 'newsStatus'),
    { includeMetadataChanges: false },
    (docSnapshot) => {
      if (!docSnapshot.exists()) {
        console.log('⏳ News status doc not created yet');
        return;
      }
      
      const status = docSnapshot.data();
      console.log('📢 News snapshot updated at:', status.lastUpdatedAt);
      console.log(`   Sources: ${status.sourceCount}, Articles: ${status.totalArticles}`);
      
      // ✅ TRIGGER REFRESH when snapshot updates
      const now = new Date();
      const hour = now.getHours();
      const minute = now.getMinutes();
      
      // Only auto-refresh if within 30 minutes of 5 AM UTC
      // (accounts for timezone conversion and function execution time)
      const utcHour = now.getUTCHours();
      const utcMinute = now.getUTCMinutes();
      
      if ((utcHour === 5 && utcMinute <= 30) || (utcHour === 4 && utcMinute >= 55)) {
        console.log('🔄 Snapshot updated near 5 AM UTC - auto-refreshing news...');
        
        // Clear cache to force fresh load
        localStorage.removeItem('news_today');
        
        // Small delay to ensure snapshot is fully written
        setTimeout(() => {
          handleRefreshNews(true);
        }, 2000);
      }
    },
    (error) => {
      console.error('❌ Snapshot listener error:', error);
    }
  );
  
  return () => {
    console.log('🔌 Cleaning up snapshot listener');
    unsubscribe();
  };
}, [firestoreService]);

// ✅ SMART LOAD WITH FALLBACK: localStorage → Global → Partial Refresh
// ✅ MODIFY the smartLoadNews effect to check snapshot first
useEffect(() => {
  const smartLoadNews = async () => {
    if (!firestoreService?.isReady()) {
      console.log('⏳ Service not ready yet');
      return;
    }

    try {
      setIsInitialLoading(true);
      setNewsLoadState('checking'); // ✅ Show we're checking
      console.log('📥 Loading news (checking global snapshot first)...');

      // Get user preferences
      const userPrefs = await firestoreService.getSelectedNewsSources();
      const userSources = userPrefs?.sources || DEFAULT_USER_SETTINGS.newsFeeds;
      setSelectedNewsFeeds(userSources);

      // ✅ CRITICAL: Check if it's a NEW DAY
      const today = new Date().toISOString().split('T')[0];
      const lastLoadedDay = localStorage.getItem('lastLoadedDay');
      const isNewDay = lastLoadedDay !== today;

      if (isNewDay) {
        console.log(`📅 NEW DAY DETECTED! (${lastLoadedDay} → ${today})`);
        console.log('🔄 Force clearing old cache and refreshing...');
        localStorage.removeItem('news_today');
        localStorage.removeItem('lastLoadedDay');
      }

      // ✅ STEP 0: Check TODAY'S GLOBAL SNAPSHOT (fastest!)
      console.log('🔍 Step 0: Checking global daily snapshot...');
      const globalSnapshot = await loadTodaysNewsSnapshot();
      
      if (globalSnapshot && Object.keys(globalSnapshot).length >= 2) {
        console.log(`⚡ INSTANT: Global snapshot has ${Object.keys(globalSnapshot).length} sources`);
        
        // Filter to user's selected sources
        const userNewsData = {};
        userSources.forEach(sourceId => {
          if (globalSnapshot[sourceId]) {
            userNewsData[sourceId] = globalSnapshot[sourceId];
          }
        });
        
        if (Object.keys(userNewsData).length >= 2) {
          console.log(`✅ Showing ${Object.keys(userNewsData).length} sources from global snapshot`);
          
          setNewsLoadState('loading'); // ✅ Still show skeleton briefly
          
          // Save to localStorage too
          saveToLocalStorage(userNewsData, Object.keys(userNewsData));
          
          // Save last loaded day
          localStorage.setItem('lastLoadedDay', today);
          
          // Wait 500ms minimum to show skeleton, then load
          await new Promise(resolve => setTimeout(resolve, 500));
          
          setNewsData(userNewsData);
          setLoadedNewsCards(Object.keys(userNewsData));
          setLastRefreshTime(new Date(globalSnapshot.generatedAt));
          setIsInitialLoading(false);
          setNewsLoadState('loaded');
          
          console.log('✅ News loaded from global snapshot');
          
          // Background: fetch any missing sources
          const missingCount = userSources.length - Object.keys(userNewsData).length;
          if (missingCount > 0) {
            console.log(`🔄 Background: Fetching ${missingCount} missing sources...`);
            setTimeout(() => {
              silentBackgroundRefresh(userSources, userNewsData, Object.keys(userNewsData));
            }, 1000);
          }
          
          return; // ✅ EXIT - Got data from snapshot!
        }
      }

      // ✅ STEP 1: Check localStorage (with new day check)
      console.log('🔍 Step 1: Checking localStorage cache...');
      const cachedToday = localStorage.getItem('news_today');
      
      if (cachedToday && !isNewDay) {
        try {
          const cached = JSON.parse(cachedToday);
          
          if (cached.cachedDate === today && cached.data && Object.keys(cached.data).length > 0) {
            const cachedSources = Object.keys(cached.data);
            
            if (cachedSources.length >= 2) {
              console.log(`⚡ INSTANT: Showing ${cachedSources.length} cached sources (same day)`);
              
              setNewsLoadState('loading'); // ✅ Show skeleton
              
              // Wait 300ms minimum to show skeleton
              await new Promise(resolve => setTimeout(resolve, 300));
              
              setNewsData(cached.data);
              setLoadedNewsCards(cachedSources);
              setLastRefreshTime(new Date(cached.loadedAt));
              setIsInitialLoading(false);
              setNewsLoadState('loaded');
              
              // Silent background refresh if incomplete
              const missingSourcesCount = userSources.filter(
                source => !cachedSources.includes(source)
              ).length;
              
              if (missingSourcesCount > 0) {
                console.log(`🔄 Background: Fetching ${missingSourcesCount} missing sources...`);
                setTimeout(() => {
                  silentBackgroundRefresh(userSources, cached.data, cachedSources);
                }, 1000);
              }
              
              return;
            }
          }
        } catch (e) {
          console.warn('⚠️ Corrupted cache');
          localStorage.removeItem('news_today');
        }
      }

      // ✅ STEP 2: Try Firestore backup
      console.log('🔍 Step 2: Loading from Firestore backup...');
      const globalNews = await firestoreService.getGlobalDailyNewsSnapshot();

      if (globalNews && Object.keys(globalNews).length > 0) {
        const userNewsData = {};
        userSources.forEach(sourceId => {
          if (globalNews[sourceId]) {
            userNewsData[sourceId] = globalNews[sourceId];
          }
        });

        if (Object.keys(userNewsData).length >= 2) {
          console.log(`✅ Global backup: ${Object.keys(userNewsData).length} sources`);
          
          setNewsLoadState('loading'); // ✅ Show skeleton
          
          await new Promise(resolve => setTimeout(resolve, 400));
          
          saveToLocalStorage(userNewsData, Object.keys(userNewsData));
          setNewsData(userNewsData);
          setLoadedNewsCards(Object.keys(userNewsData));
          setLastRefreshTime(new Date());
          setIsInitialLoading(false);
          setNewsLoadState('loaded');
          
          localStorage.setItem('lastLoadedDay', today);
          
          const missingCount = userSources.length - Object.keys(userNewsData).length;
          if (missingCount > 0) {
            setTimeout(() => {
              silentBackgroundRefresh(userSources, userNewsData, Object.keys(userNewsData));
            }, 1000);
          }
          
          return;
        }
      }

      // ✅ STEP 3: No cache - do full refresh (shows skeleton while fetching)
      console.log('📰 No cache - fetching fresh...');
      setNewsLoadState('loading'); // ✅ Show skeleton IMMEDIATELY
      setIsInitialLoading(false); // ✅ But don't block UI
      
      localStorage.setItem('lastLoadedDay', today);
      
      setTimeout(() => {
        if (!refreshLockRef.current) {
          console.log('🔄 Starting full refresh from backend...');
          handleRefreshNews(true);
        }
      }, 500);

    } catch (error) {
      console.error('❌ Smart load error:', error);
      setIsInitialLoading(false);
      setNewsLoadState('error');
    }
  };

  smartLoadNews();
}, [firestoreService?.isReady()]);
useEffect(() => {
  if (newsLoadState === 'checking') {
    console.log('🔍 NEWS STATE: Checking cache/snapshot');
  } else if (newsLoadState === 'loading') {
    console.log('⏳ NEWS STATE: Loading (showing skeleton)');
  } else if (newsLoadState === 'loaded') {
    console.log('✅ NEWS STATE: Data loaded');
  } else if (newsLoadState === 'error') {
    console.error('❌ NEWS STATE: Error loading');
  }
}, [newsLoadState]);
// Add this effect to handle midnight refresh
useEffect(() => {
  const checkForNewDay = () => {
    const today = new Date().toISOString().split('T')[0];
    const lastLoadedDay = localStorage.getItem('lastLoadedDay');
    
    if (lastLoadedDay && lastLoadedDay !== today) {
      console.log(`\n🌅 NEW DAY DETECTED! Clearing cache and refreshing...`);
      localStorage.removeItem('news_today');
      localStorage.removeItem('lastLoadedDay');
      localStorage.removeItem('lastArticleRotate');
      
      // Force a smart refresh
      if (firestoreService?.isReady()) {
        console.log('🔄 Triggering new day refresh...');
        handleRefreshNews(true);
      }
    }
  };

  // Check every minute
  const interval = setInterval(checkForNewDay, 60000);
  
  // Also check on page visibility change (user switches tabs)
  const handleVisibilityChange = () => {
    if (!document.hidden) {
      console.log('👀 Page became visible - checking for new day');
      checkForNewDay();
    }
  };
  
  document.addEventListener('visibilitychange', handleVisibilityChange);

  return () => {
    clearInterval(interval);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };
}, [firestoreService?.isReady(), handleRefreshNews]);

// ✅ SILENT snapshot check - no console spam
useEffect(() => {
  let mounted = true;
  let hasRunOnce = false; // ✅ Prevent duplicate runs

  const checkAndRefreshStaleSnapshot = async () => {
    if (!firestoreService?.isReady() || hasRunOnce) {
      return; // Silent wait
    }

    hasRunOnce = true;

    try {
      const db = getFirestore();
      const snapshotDocRef = doc(db, 'global', 'dailyNewsSnapshot');
      const snapshotDoc = await getDoc(snapshotDocRef);
      
      if (!snapshotDoc.exists()) {
        return; // ✅ SILENT
      }

      const data = snapshotDoc.data();
      
      if (!data.generatedFor || !data.sourceCount) {
        return; // ✅ SILENT validation fail
      }

      const snapshotDate = data.generatedFor;
      
      const today = new Date().toLocaleDateString('en-US', { 
        timeZone: 'UTC',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });

      if (snapshotDate === today) {
        return; // ✅ SILENT - snapshot is fresh
      }

      // ✅ STALE - regenerate
      if (!mounted) return;

      localStorage.removeItem('news_today');
      
      try {
        const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'fir-7c9c9';
        const response = await fetch(
          `https://us-central1-${projectId}.cloudfunctions.net/generateDailyNewsSnapshotOnDemand`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ forceRefresh: true })
          }
        );

        if (!mounted) return;

        setTimeout(() => {
          handleRefreshNews(true);
        }, response.ok ? 2000 : 1000);
      } catch (error) {
        if (!mounted) return;
        setTimeout(() => {
          handleRefreshNews(true);
        }, 1000);
      }

    } catch (error) {
      return; // ✅ SILENT error handling
    }
  };

  if (firestoreService?.isReady()) {
    checkAndRefreshStaleSnapshot();
  }

  return () => {
    mounted = false;
  };
}, [firestoreService?.isReady()]); // ✅ Only trigger on isReady change

// ✅ ALSO ADD: Better logging to understand the news flow
useEffect(() => {
  console.group('📊 [INIT] NEWS SYSTEM STATUS');
  console.log('🎯 Load State:', newsLoadState);
  console.log('📰 Sources Available:', Object.keys(newsData).length);
  console.log('✅ Valid Sources:', loadedNewsCards);
  console.log('🔄 Refreshing:', isRefreshing);
  console.log('⏳ Last Refresh:', lastRefreshTime?.toLocaleTimeString());
  console.groupEnd();
}, [newsLoadState, newsData, loadedNewsCards, isRefreshing, lastRefreshTime]);

// Smart initial load that checks for today's snapshot
const loadTodaysNewsSnapshot = async () => {
  if (!firestoreService?.isReady()) {
    console.log('⏳ Service not ready');
    return null;
  }

  try {
    const db = getFirestore();
    const today = new Date().toISOString().split('T')[0];
    
    console.log(`📥 Checking for today's snapshot (${today})...`);
    
    const snapshotDoc = await getDoc(doc(db, 'global', 'dailyNewsSnapshot'));
    
    if (!snapshotDoc.exists()) {
      console.log('⏳ Snapshot not available yet (might generate at 5 AM UTC)');
      return null;
    }
    
    const data = snapshotDoc.data();
    
    // ✅ Verify it's from today
    if (data.generatedFor !== today) {
      console.log(`📅 Snapshot is from ${data.generatedFor}, not today`);
      return null;
    }
    
    console.log(`✅ Found today's snapshot with ${data.sourceCount} sources`);
    
    return data.newsData;
    
  } catch (error) {
    console.error('❌ Error loading snapshot:', error);
    return null;
  }
};

// ✅ SILENT BACKGROUND REFRESH - Fetches missing sources without showing skeleton
const silentBackgroundRefresh = async (allUserSources, currentData, loadedSources, retryAttempt = 0) => {
  if (refreshLockRef.current) {
    console.log('🔒 Refresh already in progress, skipping silent refresh');
    return;
  }

  try {
    // ✅ SET LOCK
    refreshLockRef.current = true;
    
    // Find missing sources
    const missingSources = allUserSources.filter(
      source => !loadedSources.includes(source)
    );
    
    if (missingSources.length === 0) {
      console.log('✅ All sources loaded! Stopping background refresh.');
      setBackgroundRetryCount(0);
      if (backgroundRetryTimerRef.current) {
        clearTimeout(backgroundRetryTimerRef.current);
        backgroundRetryTimerRef.current = null;
      }
      refreshLockRef.current = false;
      return;
    }
    
    console.log(`🔄 Silently fetching ${missingSources.length} missing sources (attempt ${retryAttempt + 1}/3):`, missingSources);
    
    const sourceConfig = getDefaultSourceConfig();
    const result = await refreshNewsViaBackendSimple(missingSources, sourceConfig);
    
    if (result.success && result.loadedCards.length > 0) {
      console.log(`✅ Background: Loaded ${result.loadedCards.length} additional sources`);
      
      // ✅ MERGE new data with existing data
      const mergedData = {
        ...currentData,
        ...result.newsData
      };
      
      const allLoadedSources = [...loadedSources, ...result.loadedCards];
      
      // ✅ UPDATE STATE (dots will update automatically!)
      setNewsData(mergedData);
      setLoadedNewsCards(allLoadedSources);
      
      // ✅ SAVE to localStorage
      saveToLocalStorage(mergedData, allLoadedSources);
      
      // ✅ SAVE to Firestore
      if (firestoreService?.isReady()) {
        try {
          await firestoreService.saveNewsCacheWithDate(mergedData, allLoadedSources);
          console.log('✅ Background: Backed up to Firestore');
        } catch (e) {
          console.warn('⚠️ Background: Firestore backup skipped');
        }
      }
      
      console.log(`🎉 Now showing ${allLoadedSources.length}/${allUserSources.length} sources`);
      
      // ✅ CHECK: Do we have everything now?
      const stillMissing = allUserSources.filter(
        source => !allLoadedSources.includes(source)
      );
      
      if (stillMissing.length === 0) {
        console.log('🎊 Complete! All user sources loaded.');
        setBackgroundRetryCount(0);
        refreshLockRef.current = false;
        return; // ✅ STOP - We're done!
      }
      
      // ✅ Still missing some, schedule retry if under limit
      if (retryAttempt < 2) {
        console.log(`⏰ Scheduling retry in 5 secs (${stillMissing.length} sources still missing)...`);
        setBackgroundRetryCount(retryAttempt + 1);
        
        backgroundRetryTimerRef.current = setTimeout(() => {
          silentBackgroundRefresh(allUserSources, mergedData, allLoadedSources, retryAttempt + 1);
}, 5000); // 5 seconds
      } else {
        console.log('⏹️ Max retries (3) reached. Stopping background refresh.');
        setBackgroundRetryCount(0);
      }
    } else {
      console.warn(`⚠️ Background refresh attempt ${retryAttempt + 1} failed`);
      
      // ✅ Retry if under limit
      if (retryAttempt < 2) {
        console.log(`⏰ Retrying in 5 secs...`);
        setBackgroundRetryCount(retryAttempt + 1);
        
        backgroundRetryTimerRef.current = setTimeout(() => {
          silentBackgroundRefresh(allUserSources, currentData, loadedSources, retryAttempt + 1);
}, 5000); // 5 seconds
      } else {
        console.log('⏹️ Max retries reached. Giving up.');
        setBackgroundRetryCount(0);
      }
    }
    
  } catch (error) {
    console.error('❌ Silent background refresh error:', error);
    
    // ✅ Retry on error if under limit
    if (retryAttempt < 2) {
      console.log(`⏰ Error occurred, retrying in 5 secs...`);
      setBackgroundRetryCount(retryAttempt + 1);
      
      backgroundRetryTimerRef.current = setTimeout(() => {
        silentBackgroundRefresh(allUserSources, currentData, loadedSources, retryAttempt + 1);
}, 5); // 5 seconds
    }
  } finally {
    // ✅ ALWAYS RELEASE LOCK
    refreshLockRef.current = false;
  }
};
const loadPodcastPreferencesOnInit = async (
  firestoreService,
  setSelectedHosts,
  setTalkingStyle,
  setSelectedGuest,
  setIntroMusicEnabled,
  throttledLog
) => {
  if (!firestoreService?.isReady()) {
    throttledLog?.log('PREFS_INIT', 'Service not ready yet');
    return;
  }

  try {
    throttledLog?.log('PREFS_INIT', 'Loading podcast preferences...');
    
    const prefs = await firestoreService.getPodcastPreferences();
    
    console.log('🎙️ Preferences loaded from:', prefs.source);
    console.log('📋 Full preferences object:', prefs);
    
    if (prefs.source !== 'default' && prefs.source !== 'default_no_user') {
      // ✅ Only update if we got REAL saved preferences (not defaults)
      console.log('✅ Applying saved preferences to state...');
      
      // ✅ Validate arrays before setting
      if (Array.isArray(prefs.hosts) && prefs.hosts.length > 0) {
        console.log('Setting hosts:', prefs.hosts);
        setSelectedHosts(prefs.hosts);
      }
      
      if (prefs.talkingStyle) {
        console.log('Setting talking style:', prefs.talkingStyle);
        setTalkingStyle(prefs.talkingStyle);
      }
      
      if (prefs.guest !== undefined) {
        console.log('Setting guest:', prefs.guest);
        setSelectedGuest(prefs.guest);
      }
      
      if (prefs.introMusicEnabled !== undefined) {
        console.log('Setting intro music:', prefs.introMusicEnabled);
        setIntroMusicEnabled(prefs.introMusicEnabled);
      }
      
      throttledLog?.log('PREFS_LOADED', `From ${prefs.source}:`, {
        hosts: prefs.hosts,
        style: prefs.talkingStyle,
        guest: prefs.guest
      });
    } else {
      throttledLog?.log('PREFS_DEFAULT', 'Using default preferences');
    }
  } catch (error) {
    throttledLog?.error('PREFS_INIT_ERROR', error.message);
    console.error('Failed to load preferences:', error);
  }
};
// ✅ Cleanup on unmount
useEffect(() => {
  return () => {
    if (backgroundRetryTimerRef.current) {
      clearTimeout(backgroundRetryTimerRef.current);
    }
  };
}, []);
// ✅ Helper function
const saveToLocalStorage = (newsData, sources) => {
  try {
    const today = new Date().toISOString().split('T')[0];
    localStorage.setItem('news_today', JSON.stringify({
      data: newsData,
      sources: sources,
      loadedAt: new Date().toISOString(),
      cachedDate: today,
      expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
    }));
    console.log('💾 Saved to localStorage');
  } catch (e) {
    console.warn('⚠️ localStorage save failed');
  }
};

// ✅ MID-DAY ARTICLE ROTATION (12pm-4pm UTC)
// Fetches fresh articles and rotates them in mid-day
useEffect(() => {
  const rotateArticlesIfNeeded = async () => {
    const now = new Date().getUTCHours();
    
    // Only rotate between 12pm-4pm UTC
    if (now < 12 || now > 16) {
      console.log('⏭️ Not mid-day window, skipping rotation');
      return;
    }
    
    // Check if already rotated today
    const lastRotate = localStorage.getItem('lastArticleRotate');
    const today = new Date().toISOString().split('T')[0];
    
    if (lastRotate === today) {
      console.log('✅ Already rotated today, skipping');
      return;
    }

    console.log('🔄 Mid-day refresh: Rotating articles...');
    
    // Fetch fresh articles for sources
    for (const sourceId of selectedNewsFeeds) {
      try {
        const response = await fetch(
          `https://us-central1-fir-7c9c9.cloudfunctions.net/performWebSearch?source=${sourceId}`
        );
        
        if (response.ok) {
          const data = await response.json();
          
          if (data.results && data.results.length > 3) {
            // We have more than the 3 shown - rotate them!
            const current = newsData[sourceId];
            
            if (!current) {
              console.log(`⏭️ ${sourceId} not in current news, skipping`);
              continue;
            }
            
            const newStories = data.results.map(r => ({
              title: r.title,
              description: r.snippet,
              image: r.image,
              source: r.url,
              time: new Date(r.datePublished).toLocaleTimeString()
            }));
            
            // ✅ SMART ROTATION:
            // Keep first 3 (user saw these), add new 4-6 from fresh fetch
            const rotated = [
              ...current.stories.slice(0, 3),  // Keep first 3
              ...newStories.slice(3, 6)         // Add articles 4-6 from fresh fetch
            ].slice(0, 6);
            
            setNewsData(prev => ({
              ...prev,
              [sourceId]: {
                ...current,
                stories: rotated,
                updatedAt: new Date().toISOString()
              }
            }));
            
            console.log(`✅ ${sourceId}: Rotated articles 4-6`);
          }
        }
      } catch (error) {
        console.error(`⚠️ Failed to rotate ${sourceId}:`, error);
      }
    }
    
    // Mark as rotated today
    localStorage.setItem('lastArticleRotate', today);
    console.log('✅ Articles rotated successfully');
  };

  // Check for mid-day rotation
  rotateArticlesIfNeeded();

}, [selectedNewsFeeds, newsData]);

useEffect(() => {
  console.log('📊 News State Update:', {
    newsDataKeys: Object.keys(newsData),
    newsDataCount: Object.keys(newsData).length,
    selectedNewsFeeds,
    loadedNewsCards,
    isRefreshing,
    showNewsCard
  });
  
  // Log individual source data
  Object.entries(newsData).forEach(([sourceKey, sourceData]) => {
    console.log(`  📰 ${sourceKey}:`, {
      name: sourceData.name,
      storiesCount: sourceData.stories?.length || 0,
      hasStories: !!(sourceData.stories && sourceData.stories.length > 0)
    });
  });
}, [newsData, selectedNewsFeeds, loadedNewsCards, isRefreshing, showNewsCard]);

// Auto-save podcast preferences when they change
const previousPrefsRef = useRef(null);

useEffect(() => {
  const autoSavePodcastPrefs = async () => {
    if (!firestoreService?.isReady()) return;
    
    if (selectedHosts.length !== 2) return; // Only save if valid
    
    // ✅ Check if preferences actually changed
    const currentPrefs = {
      hosts: selectedHosts,
      guest: selectedGuest,
      introMusicEnabled: introMusicEnabled,
      talkingStyle: talkingStyle
    };
    
    const prefsChanged = JSON.stringify(previousPrefsRef.current) !== JSON.stringify(currentPrefs);
    
    if (!prefsChanged) {
      return; // Don't save if nothing changed
    }
    
    try {
      const success = await firestoreService.savePodcastPreferences(currentPrefs);
      
      if (success) {
        previousPrefsRef.current = currentPrefs; // Update tracking
        console.log('✅ Podcast preferences auto-saved:', currentPrefs);
      }
    } catch (error) {
      console.error('❌ Failed to auto-save preferences:', error.message);
    }
  };
  
  const timeoutId = setTimeout(autoSavePodcastPrefs, 1000);
  return () => clearTimeout(timeoutId);
  
}, [selectedHosts, selectedGuest, introMusicEnabled, talkingStyle, firestoreService]);

useEffect(() => {
  if (!firestoreService?.isReady()) {
    return; // Don't log anything - wait silently
  }

  // ✅ Load preferences only ONCE when service becomes ready
  loadPodcastPreferencesOnInit(
    firestoreService,
    setSelectedHosts,
    setTalkingStyle,
    setSelectedGuest,
    setIntroMusicEnabled,
    throttledLog
  );
}, [firestoreService?.isReady()]); // ✅ Dependency: only when isReady changes to true

useEffect(() => {
  if (!firestoreService?.isReady()) return;

  // ✅ Listen for new daily brief generation completion
  const unsubscribe = firestoreService.listenToDailyBriefStatus((status) => {
    if (!status) return;

    console.log('📡 Daily brief status update:', status);

    // Update generating state
    setIsDailyBriefGenerating(status.isGenerating || false);

    // ✅ NEW BRIEF AVAILABLE - Auto-refresh
    if (status.newBriefAvailable) {
      console.log('🎉 NEW DAILY BRIEF AVAILABLE!');
      
      // Clear the flag
      firestoreService.clearNewBriefFlag();
      
      // Reload daily briefs
      (async () => {
        const podcasts = await firestoreService.getSavedPodcasts();
        const dailyBriefs = podcasts.filter(p => p.type === 'daily-brief-catchup');
        setSavedDailyBriefs(dailyBriefs);
        
        // Also set today's brief as ready
        const todaysBrief = await firestoreService.getTodaysDailyBriefCatchUp();
        if (todaysBrief) {
          setDailyBriefPodcast(todaysBrief);
          setIsDailyBriefReady(true);
        }
        
        console.log('✅ Daily briefs refreshed after new generation');
      })();
    }
  });

  return () => {
    if (unsubscribe) unsubscribe();
  };
}, [firestoreService]);

// ✅ Map host labels to actual names
const getHostName = (hostLabel) => {
  const nameMap = {
    'Host 1': 'Emma',
    'Host 2': 'James', 
    'Host 3': 'Jackie',
    'Host 4': 'Kenny'
  };
  return nameMap[hostLabel] || hostLabel;
};

const useOptimizedAudioTracking = (audioRef, isPodcastPlaying) => {
  const [currentAudioTime, setCurrentAudioTime] = React.useState(0);
  const [audioDuration, setAudioDuration] = React.useState(0);
  
  React.useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !isPodcastPlaying) return;

    let rafId;
    let lastUpdate = 0;
    const updateInterval = 250; // Update every 250ms instead of every frame (4x performance boost)
    
    const updateTime = (timestamp) => {
      if (audio && !audio.paused && !audio.ended) {
        if (timestamp - lastUpdate >= updateInterval) {
          setCurrentAudioTime(audio.currentTime);
          
          if (audio.duration && !isNaN(audio.duration) && isFinite(audio.duration)) {
            setAudioDuration(audio.duration);
          }
          
          lastUpdate = timestamp;
        }
        
        rafId = requestAnimationFrame(updateTime);
      }
    };
    
    rafId = requestAnimationFrame(updateTime);
    
    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [isPodcastPlaying]);
  
  return { currentAudioTime, audioDuration };
};


useEffect(() => {
  if (firestoreService) {
    console.log('✅ Podcast service ready');
  } else {
    console.log('⏳ Podcast service initializing...');
  }
}, [firestoreService]);

useEffect(() => {
  const loadAllUserSettings = async () => {
    if (!firestoreService?.isReady()) {
      console.log('⏳ Service not ready for loading settings');
      return;
    }

    try {
      console.log('📥 Loading ALL user settings from Firestore...');
      
      const settings = await firestoreService.getUserSettings();
      
      if (!settings || Object.keys(settings).length === 0) {
        console.log('⚠️ No saved settings found - using defaults');
        return;
      }

      console.log('✅ Settings loaded from Firestore:', settings);

      // ✅ Apply ALL settings to state (including new podcast settings)
      if (settings.selectedHosts && settings.selectedHosts.length === 2) {
        console.log('   🎙️ Setting hosts:', settings.selectedHosts);
        setSelectedHosts(settings.selectedHosts);
      }

      if (settings.selectedGuest !== undefined) {
        console.log('   👤 Setting guest:', settings.selectedGuest);
        setSelectedGuest(settings.selectedGuest);
      }

      if (settings.newsCategories) {
        console.log('   📰 Setting news categories:', settings.newsCategories);
        setSelectedCategories(settings.newsCategories);
      }

      if (settings.newsFeeds && Array.isArray(settings.newsFeeds)) {
        console.log('   📰 Setting news feeds:', settings.newsFeeds);
        setSelectedNewsFeeds(settings.newsFeeds);
      }

      if (settings.selectedStocks && Array.isArray(settings.selectedStocks)) {
        console.log('   📊 Setting stocks:', settings.selectedStocks);
        setSelectedStocks(settings.selectedStocks);
      }

      if (settings.selectedCryptos && Array.isArray(settings.selectedCryptos)) {
        console.log('   💰 Setting cryptos:', settings.selectedCryptos);
        setSelectedCryptos(settings.selectedCryptos);
      }

      if (settings.showNewsCard !== undefined) {
        console.log('   📰 Setting show news card:', settings.showNewsCard);
        setShowNewsCard(settings.showNewsCard);
      }

      if (settings.showStocksCard !== undefined) {
        console.log('   📊 Setting show stocks card:', settings.showStocksCard);
        setShowStocksCard(settings.showStocksCard);
      }

      if (settings.showCryptoCard !== undefined) {
        console.log('   💰 Setting show crypto card:', settings.showCryptoCard);
        setShowCryptoCard(settings.showCryptoCard);
      }

      if (settings.userName) {
        console.log('   👤 Setting user name:', settings.userName);
        setUserName(settings.userName);
      }

      if (settings.introMusicEnabled !== undefined) {
        console.log('   🎵 Setting intro music:', settings.introMusicEnabled);
        setIntroMusicEnabled(settings.introMusicEnabled);
      }

      if (settings.talkingStyle) {
        console.log('   💬 Setting talking style:', settings.talkingStyle);
        setTalkingStyle(settings.talkingStyle);
      }

      console.log('✅ ALL settings applied to state');

    } catch (error) {
      console.error('❌ Failed to load user settings:', error);
    }
  };

  // ✅ Load settings when service is ready
  loadAllUserSettings();
}, [firestoreService?.isReady()]);

const handleToggleNewsSource = (category, sourceId) => {
  // ✅ Visual feedback animation
  setClickedSource(sourceId);
  setTimeout(() => setClickedSource(null), 600);
  
  setSelectedCategories(prev => {
    const newCategories = { ...prev };
    
    if (!newCategories[category]) {
      newCategories[category] = [];
    }
    
    const isCurrentlySelected = newCategories[category].includes(sourceId);
    
    if (isCurrentlySelected) {
      // ✅ REMOVING - no minimum requirement
      newCategories[category] = newCategories[category].filter(id => id !== sourceId);
      console.log(`➖ Removed ${sourceId} from ${category}`);
    } else {
      // ✅ ADDING - Check TOTAL limit across ALL categories (maximum 5)
      const allSelected = Object.values(newCategories).flat();
      const uniqueSelected = [...new Set(allSelected)];
      
      if (uniqueSelected.length >= 5) {
        alert('Maximum 5 news sources total. Please remove a source before adding another.');
        return prev; // Don't update state
      }
      
      newCategories[category] = [...newCategories[category], sourceId];
      console.log(`✅ Added ${sourceId} to ${category}`);
    }
    
    // ✅ Log the full state after change
    const allNewSelected = Object.values(newCategories).flat();
    const uniqueNewSelected = [...new Set(allNewSelected)];
    console.log('📋 Updated categories:', newCategories);
    console.log('📰 All selected sources:', uniqueNewSelected);
    
    return newCategories;
  });
};
const handleSaveSettings = async () => {
  if (selectedHosts.length !== 2) {
    alert('Please select exactly 2 podcast hosts');
    return;
  }
  
  try {
    if (!firestoreService?.isReady()) {
      alert('Service not ready. Please wait a moment and try again.');
      return;
    }

    // ✅ Get all selected sources from categories
    const allSelectedSources = Object.values(selectedCategories).flat();
    const uniqueSources = [...new Set(allSelectedSources)];

    console.log('💾 SAVING COMPLETE SETTINGS:');
    console.log('   📰 News categories:', selectedCategories);
    console.log('   📰 News sources:', uniqueSources);
    console.log('   📊 Stocks:', selectedStocks);
    console.log('   💰 Cryptos:', selectedCryptos);
    console.log('   🎙️ Hosts:', selectedHosts);
    console.log('   👤 Guest:', selectedGuest);
    
    // ✅ CRITICAL: Complete settings object
    const settingsToSave = {
      selectedHosts: selectedHosts,
      selectedGuest: selectedGuest || null,
      newsFeeds: uniqueSources,
      newsCategories: selectedCategories,
      selectedStocks: selectedStocks,
      selectedCryptos: selectedCryptos,
      showNewsCard: showNewsCard,
      showStocksCard: showStocksCard,
      showCryptoCard: showCryptoCard,
      userName: userName,
      introMusicEnabled: introMusicEnabled,
      talkingStyle: talkingStyle,
      lastUpdated: Date.now()
    };
    
    console.log('💾 Full settings object:', settingsToSave);
    
    // ✅ SAVE TO BOTH LOCATIONS
    // Location 1: users/{uid}/settings/preferences (subcollection)
    const db = getFirestore();
    const settingsRef = doc(db, 'users', firestoreService.currentUser.uid, 'settings', 'preferences');
    await setDoc(settingsRef, settingsToSave, { merge: true });
    console.log('✅ Saved to users/{uid}/settings/preferences');
    
    // Location 2: users/{uid} → settings field (for compatibility with onboarding)
    const userRef = doc(db, 'users', firestoreService.currentUser.uid);
    await updateDoc(userRef, {
      'settings.newsFeeds': uniqueSources,
      'settings.newsCategories': selectedCategories,
      'settings.selectedStocks': selectedStocks,
      'settings.selectedCryptos': selectedCryptos,
      'settings.selectedHosts': selectedHosts,
      'settings.selectedGuest': selectedGuest,
      'settings.showNewsCard': showNewsCard,
      'settings.showStocksCard': showStocksCard,
      'settings.showCryptoCard': showCryptoCard,
      'settings.talkingStyle': talkingStyle,
      'settings.introMusicEnabled': introMusicEnabled,
      'settings.lastUpdated': Date.now()
    });
    console.log('✅ Saved to users/{uid} → settings field');

    setShowSettings(false);
    alert('Settings saved successfully!');
    
    // ✅ Refresh with new sources
    setTimeout(async () => {
      await handleRefreshNews(true);
    }, 500);
    
  } catch (error) {
    console.error('❌ Error saving settings:', error);
    alert(`Failed to save settings: ${error.message}`);
  }
};
const RouteCard = ({ savedRoutes, onEditRoute, onViewAllRoutes, onAddRoute }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  // Drag state using useRef to avoid re-renders
  const dragStateRef = useRef({
    isDragging: false,
    dragStart: null,
    dragStartY: null,
    dragOffset: 0
  });
  
  // Separate state for visual updates
  const [visualDragOffset, setVisualDragOffset] = useState(0);
  const [isDraggingVisual, setIsDraggingVisual] = useState(false);
  
  const animationFrameRef = useRef(null);
  const lastUpdateTimeRef = useRef(0);
  
  // Get only the 2 most recent routes
  const recentRoutes = savedRoutes
    .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
    .slice(0, 2);

  // Array of gradient color schemes
  const gradients = [
    {
      bg: 'linear-gradient(260deg,rgba(253, 230, 138, 1) 0%, rgba(252, 211, 77, 1) 53%, rgba(255, 249, 227, 0.97) 60%, rgba(255, 238, 172, 1) 60%, #fff4c8ff 100%)',
      border: 'rgba(255, 200, 180, 0.5)',
      top: 'linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.8) 100%)',
      badge: '#ff7322ff'
    },
    {
      bg: 'linear-gradient(260deg, rgba(216, 180, 254, 1) 0%, rgba(192, 132, 252, 1) 53%, rgba(243, 232, 255, 0.97) 60%, rgba(221, 214, 254, 1) 60%, #e9d5ffff 100%)',
      border: 'rgba(192, 132, 252, 0.5)',
      top: 'linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.8) 100%)',
      badge: '#9333eaff'
    },
    {
      bg: 'linear-gradient(260deg, rgba(147, 197, 253, 1) 0%, rgba(96, 165, 250, 1) 53%, rgba(224, 242, 254, 0.97) 60%, rgba(191, 219, 254, 1) 60%, #dbeafeff 100%)',
      border: 'rgba(147, 197, 253, 0.5)',
      top: 'linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.8) 100%)',
      badge: '#2563ebff'
    },
    {
      bg: 'linear-gradient(260deg, rgba(134, 239, 172, 1) 0%, rgba(74, 222, 128, 1) 53%, rgba(220, 252, 231, 0.97) 60%, rgba(187, 247, 208, 1) 60%, #d1fae5ff 100%)',
      border: 'rgba(134, 239, 172, 0.5)',
      top: 'linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.8) 100%)',
      badge: '#16a34aff'
    },
    {
      bg: 'linear-gradient(260deg, rgba(252, 165, 165, 1) 0%, rgba(248, 113, 113, 1) 53%, rgba(254, 226, 226, 0.97) 60%, rgba(254, 202, 202, 1) 60%, #fecacaff 100%)',
      border: 'rgba(252, 165, 165, 0.5)',
      top: 'linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.8) 100%)',
      badge: '#dc2626ff'
    },
    {
      bg: 'linear-gradient(260deg, rgba(165, 243, 252, 1) 0%, rgba(103, 232, 249, 1) 53%, rgba(207, 250, 254, 0.97) 60%, rgba(186, 230, 253, 1) 60%, #cffafe 100%)',
      border: 'rgba(165, 243, 252, 0.5)',
      top: 'linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.8) 100%)',
      badge: '#0891b2ff'
    },
    {
      bg: 'linear-gradient(260deg, rgba(251, 207, 232, 1) 0%, rgba(244, 114, 182, 1) 53%, rgba(252, 231, 243, 0.97) 60%, rgba(168, 219, 249, 1) 60%, #fce7f3ff 100%)',
      border: 'rgba(207, 217, 251, 0.5)',
      top: 'linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.8) 100%)',
      badge: '#2793dbff'
    },
    {
      bg: 'linear-gradient(260deg, rgba(253, 186, 116, 1) 0%, rgba(251, 146, 60, 1) 53%, rgba(254, 243, 199, 0.97) 60%, rgba(253, 224, 71, 1) 60%, #fef3c7ff 100%)',
      border: 'rgba(253, 186, 116, 0.5)',
      top: 'linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.8) 100%)',
      badge: '#ea580cff'
    },
    {
      bg: 'linear-gradient(260deg, rgba(203, 213, 225, 1) 0%, rgba(148, 163, 184, 1) 53%, rgba(241, 245, 249, 0.97) 60%, rgba(226, 232, 240, 1) 60%, #f1f5f9ff 100%)',
      border: 'rgba(203, 213, 225, 0.5)',
      top: 'linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.8) 100%)',
      badge: '#475569ff'
    }
  ];

  // Function to get gradient for a route based on its index
  const getGradientForRoute = (route, index) => {
    const routeId = route.id || index;
    const hash = typeof routeId === 'string' 
      ? routeId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
      : routeId;
    return gradients[hash % gradients.length];
  };

  const handleAddRoute = () => {
    if (savedRoutes.length >= 10) {
      alert('Maximum of 10 routes reached. Please delete at least one route before creating a new one.');
      return;
    }
    if (onAddRoute) {
      onAddRoute();
    }
  };

  // Throttled visual update
  const updateVisualState = useCallback(() => {
    const now = performance.now();
    if (now - lastUpdateTimeRef.current < 16) return; // ~60fps
    
    lastUpdateTimeRef.current = now;
    setVisualDragOffset(dragStateRef.current.dragOffset);
    setIsDraggingVisual(dragStateRef.current.isDragging);
  }, []);

  // Navigate to next/previous slide
  const goToNextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % recentRoutes.length);
  }, [recentRoutes.length]);

  const goToPrevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + recentRoutes.length) % recentRoutes.length);
  }, [recentRoutes.length]);

  // Handle swipe complete
  const handleSwipeComplete = useCallback((direction) => {
    if (direction === 'left') {
      goToNextSlide();
    } else if (direction === 'right') {
      goToPrevSlide();
    }
    
    dragStateRef.current = {
      isDragging: false,
      dragStart: null,
      dragStartY: null,
      dragOffset: 0
    };
    setVisualDragOffset(0);
    setIsDraggingVisual(false);
  }, [goToNextSlide, goToPrevSlide]);

  // Mouse handlers
  const handleMouseDown = useCallback((e) => {
    if (e.button !== 0 || recentRoutes.length <= 1) return;
    dragStateRef.current.dragStart = e.clientX;
    dragStateRef.current.dragStartY = e.clientY;
    dragStateRef.current.isDragging = true;
    setIsDraggingVisual(true);
  }, [recentRoutes.length]);

  const handleMouseMove = useCallback((e) => {
    if (!dragStateRef.current.isDragging || dragStateRef.current.dragStart === null) return;
    
    const deltaX = e.clientX - dragStateRef.current.dragStart;
    const deltaY = Math.abs(e.clientY - dragStateRef.current.dragStartY);
    
    // Only allow horizontal drag
    if (Math.abs(deltaX) > deltaY * 1.5) {
      dragStateRef.current.dragOffset = deltaX;
      
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      animationFrameRef.current = requestAnimationFrame(updateVisualState);
    }
  }, [updateVisualState]);

  const handleMouseUp = useCallback(() => {
    if (!dragStateRef.current.isDragging) return;
    
    const swipeThreshold = 100;
    if (Math.abs(dragStateRef.current.dragOffset) > swipeThreshold) {
      const direction = dragStateRef.current.dragOffset < 0 ? 'left' : 'right';
      handleSwipeComplete(direction);
    } else {
      dragStateRef.current = {
        isDragging: false,
        dragStart: null,
        dragStartY: null,
        dragOffset: 0
      };
      setVisualDragOffset(0);
      setIsDraggingVisual(false);
    }
  }, [handleSwipeComplete]);

  // Touch handlers
  const handleTouchStart = useCallback((e) => {
    if (recentRoutes.length <= 1) return;
    dragStateRef.current.dragStart = e.touches[0].clientX;
    dragStateRef.current.dragStartY = e.touches[0].clientY;
    dragStateRef.current.isDragging = true;
    setIsDraggingVisual(true);
  }, [recentRoutes.length]);

  const handleTouchMove = useCallback((e) => {
    if (!dragStateRef.current.isDragging || dragStateRef.current.dragStart === null) return;
    
    const deltaX = e.touches[0].clientX - dragStateRef.current.dragStart;
    const deltaY = Math.abs(e.touches[0].clientY - dragStateRef.current.dragStartY);
    
    if (Math.abs(deltaX) > deltaY * 1.5) {
      dragStateRef.current.dragOffset = deltaX;
      
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      animationFrameRef.current = requestAnimationFrame(updateVisualState);
    }
  }, [updateVisualState]);

  const handleTouchEnd = useCallback(() => {
    if (!dragStateRef.current.isDragging) return;
    
    const swipeThreshold = 100;
    if (Math.abs(dragStateRef.current.dragOffset) > swipeThreshold) {
      const direction = dragStateRef.current.dragOffset < 0 ? 'left' : 'right';
      handleSwipeComplete(direction);
    } else {
      dragStateRef.current = {
        isDragging: false,
        dragStart: null,
        dragStartY: null,
        dragOffset: 0
      };
      setVisualDragOffset(0);
      setIsDraggingVisual(false);
    }
  }, [handleSwipeComplete]);

  // Global event listeners
  useEffect(() => {
    if (isDraggingVisual) {
      window.addEventListener('mousemove', handleMouseMove, { passive: true });
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDraggingVisual, handleMouseMove, handleMouseUp]);

  // Cleanup RAF on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  const gradient = gradients[0]; // Default gradient for empty state

  if (recentRoutes.length === 0) {
    return (
      <div style={{
        marginLeft: '-16px',
        marginRight: '-16px',
        marginBottom: '24px'
      }}>
        <div
          onClick={handleAddRoute}
          style={{
            background: gradient.bg,
            borderRadius: '16px',
            margin: '0 16px',
            cursor: 'pointer',
            transition: 'all 0.2s',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            border: `1px solid ${gradient.border}`,
            overflow: 'hidden',
            position: 'relative'
          }}
        >
          <div style={{
            background: gradient.top,
            padding: '16px 16px 12px 16px',
            position: 'relative'
          }}>
            <div style={{
              position: 'absolute',
              top: '12px',
              left: '12px',
              background: '#ffb028ce',
              color: 'white',
              padding: '4px 10px',
              borderRadius: '12px',
              fontSize: '9px',
              fontWeight: '500',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              0/10 Routes
            </div>

            <div style={{
              background: 'white',
              borderRadius: '20px',
              padding: '5px 20px',
              display: 'inline-block',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              marginTop: '32px',
              marginBottom: '10px',
            }}>
              <div style={{
                fontSize: '12px',
                fontWeight: '500',
                color: '#1f2937',
                textAlign: 'left'
              }}>
                Set up your traffic routes
              </div>
            </div>
          </div>

          <div style={{
            background: 'white',
            padding: '16px',
            borderRadius: '0 0 14px 14px'
          }}>
            <div style={{
              marginBottom: '12px'
            }}>
              <div style={{
                fontSize: '11px',
                fontWeight: '600',
                color: '#9ca3af',
                marginBottom: '6px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Description:
              </div>
              <div style={{
                fontSize: '12px',
                color: '#6b7280',
                lineHeight: '1.5',
                marginBottom: '8px',
              }}>
                Add your commute routes to get real-time traffic updates in your daily brief.
              </div>
            </div>

            <button
              style={{
                width: '100%',
                background: '#000000',
                color: 'white',
                border: 'none',
                borderRadius: '16px',
                padding: '10px',
                fontSize: '11.5px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
              }}
            >
              Add Routes
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Calculate transforms
  const rotation = visualDragOffset * 0.03;
  const opacity = isDraggingVisual ? Math.max(0.7, 1 - Math.abs(visualDragOffset) / 300) : 1;
  const swipeProgress = Math.min(Math.abs(visualDragOffset) / 150, 1);

  return (
    <div style={{
      marginLeft: '-16px',
      marginRight: '-16px',
      marginBottom: '24px',
      position: 'relative'
    }}>
      {/* Cards Container */}
      <div style={{
        position: 'relative',
        height: '300px',
        perspective: '1000px'
      }}>
        {recentRoutes.map((route, idx) => {
          const routeGradient = getGradientForRoute(route, idx);
          const isCurrentCard = idx === currentSlide;
          
          // Calculate card position
          let zIndex, scale, translateX, cardOpacity;
          
          if (isCurrentCard) {
            zIndex = 10;
            scale = 1;
            translateX = isDraggingVisual ? visualDragOffset : 0;
            cardOpacity = opacity;
          } else {
            // Stack other cards behind
            const offset = (idx - currentSlide + recentRoutes.length) % recentRoutes.length;
            zIndex = 10 - offset;
            scale = 1 - (offset * 0.05);
            translateX = 0;
            cardOpacity = 0.5;
          }

          const transform = isDraggingVisual && isCurrentCard
            ? `translate3d(calc(-50% + ${translateX}px), 0, 0) rotate(${rotation}deg) scale(${scale})`
            : `translate3d(-50%, 0, 0) scale(${scale})`;

          return (
            <div
              key={route.id || idx}
              style={{
                position: 'absolute',
                top: 0,
                left: '50%',
                transform,
                width: 'calc(100% - 32px)',
                maxWidth: '600px',
                zIndex,
                opacity: cardOpacity,
                transition: isDraggingVisual ? 'none' : 'transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.4s ease-out',
                cursor: isCurrentCard && recentRoutes.length > 1 ? (isDraggingVisual ? 'grabbing' : 'grab') : 'default',
                pointerEvents: isCurrentCard ? 'auto' : 'none',
                willChange: 'transform, opacity',
                WebkitTransform: transform,
                backfaceVisibility: 'hidden',
                perspective: 1000,
                WebkitPerspective: 1000,
                transformStyle: 'preserve-3d'
              }}
              onMouseDown={isCurrentCard ? handleMouseDown : undefined}
              onTouchStart={isCurrentCard ? handleTouchStart : undefined}
              onTouchMove={isCurrentCard ? handleTouchMove : undefined}
              onTouchEnd={isCurrentCard ? handleTouchEnd : undefined}
            >
              <div
                style={{
                  background: routeGradient.bg,
                  borderRadius: '16px',
                  transition: 'all 0.2s',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  border: `1px solid ${routeGradient.border}`,
                  overflow: 'hidden',
                  position: 'relative'
                }}
              >
                <div style={{
                  background: routeGradient.top,
                  padding: '16px 16px 12px 16px',
                  position: 'relative'
                }}>
                  {/* Top Bar with Count and Add Button */}
                  <div style={{
                    position: 'absolute',
                    top: '12px',
                    left: '12px',
                    right: '12px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    {/* Route Count Badge */}
                    <div style={{
                      background: routeGradient.badge,
                      color: 'white',
                      padding: '4px 10px',
                      borderRadius: '12px',
                      fontSize: '9px',
                      fontWeight: '500',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      flexShrink: 0
                    }}>
                      {savedRoutes.length}/10 Routes
                    </div>

                    {/* Add New Route Button */}
                    {savedRoutes.length < 10 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddRoute();
                        }}
                        style={{
                          background: 'white',
                          border: `0.5px solid #f2f2f2`,
                          borderRadius: '12px',
                          padding: '4px 10px',
                          fontSize: '8.5px',
                          fontWeight: '500',
                          color: 'black',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          transition: 'all 0.2s',
                          flexShrink: 0
                        }}
                      >
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                        </svg>
                        ADD NEW
                      </button>
                    )}
                  </div>

<div
  style={{
    background: 'white',
    borderRadius: '20px',
    padding: '5px 16px',
    display: 'inline-flex',        
    alignItems: 'center',
    justifyContent: 'center',
    width: 'fit-content',          
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    border: 'none',
    marginTop: '42px',
    marginBottom: '15px'
  }}
>
  <div
    style={{
      fontSize: '12px',
      fontWeight: '600',
      color: '#1f2937',
      textAlign: 'center',
      lineHeight: '1.1'
    }}
  >
    {route.name || 'Daily Commute Route'}
  </div>
</div>

                </div>

                <div style={{
                  background: 'white',
                  padding: '16px',
                  borderRadius: '0 0 14px 14px'
                }}>
                  <div style={{
                    marginBottom: '12px'
                  }}>
                    <div style={{
                      fontSize: '11px',
                      fontWeight: '600',
                      color: '#9ca3af',
                      marginBottom: '10px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      textAlign: 'left'
                    }}>
                      Route Details:
                    </div>
                    
                    <div style={{
                      marginBottom: '8px'
                    }}>
                      <div style={{
                        fontSize: '10px',
                        color: '#9ca3af',
                        marginBottom: '3px',
                        fontWeight: '500',
                        textAlign: 'left'
                      }}>
                        From
                      </div>
                      <div style={{
                        fontSize: '12px',
                        color: '#1f2937',
                        lineHeight: '1.4',
                        wordBreak: 'break-word',
                        textAlign: 'left'
                      }}>
                        {route.origin || 'Origin not set'}
                      </div>
                    </div>

                    <div>
                      <div style={{
                        fontSize: '10px',
                        color: '#9ca3af',
                        marginBottom: '3px',
                        fontWeight: '500',
                        textAlign: 'left'
                      }}>
                        To
                      </div>
                      <div style={{
                        fontSize: '12px',
                        color: '#1f2937',
                        lineHeight: '1.4',
                        wordBreak: 'break-word',
                        textAlign: 'left'
                      }}>
                        {route.destination || 'Destination not set'}
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditRoute(route);
                    }}
                    style={{
                      width: '100%',
                      background: '#000000',
                      color: 'white',
                      border: 'none',
                      borderRadius: '16px',
                      padding: '10px',
                      fontSize: '11.5px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                      marginTop: '4px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-1px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
                    }}
                  >
                    Edit Route
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Dots Indicator (only show if more than 1 route) */}
      {recentRoutes.length > 1 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '6px',
          marginTop: '8px',
          padding: '0 16px'
        }}>
          {recentRoutes.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentSlide(idx)}
              style={{
                width: currentSlide === idx ? '24px' : '8px',
                height: '5px',
                borderRadius: '4px',
                border: 'none',
                background: currentSlide === idx ? '#000000' : '#b1b5b9ff',
                cursor: 'pointer',
                transition: 'all 0.3s',
                padding: 0
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// 5. ADD NEW HANDLER FOR RETURNING TO DAILY BRIEF
const handleReturnToDailyBrief = () => {
  setShowTrafficRouteSetup(false);
  setShowDailyBriefSetup(true);
  setReturnToDailyBrief(false);
  
  // Refresh saved routes
  const { getSavedRoutes } = require('./trafficHandler');
  setSavedRoutes(getSavedRoutes());
};

useEffect(() => {
const loadAllDataFromFirestore = async () => {
  if (!firestoreService?.isReady()) {
    console.log('⏳ Waiting for Firestore service...');
    return;
  }

  try {
    setIsInitialLoading(true);
    console.log('📥 Loading all data from Firestore...');

    // ✅ Load user settings WITH PROPER DEFAULTS
    const settings = await firestoreService.getUserSettings();
    console.log('📋 Raw settings from Firestore:', settings);
    // ✅ CRITICAL: Load stocks with fallback
// ✅ CRITICAL: Load stocks with fallback
if (settings?.selectedStocks && Array.isArray(settings.selectedStocks) && settings.selectedStocks.length > 0) {
  console.log('📈 Applying saved stocks:', settings.selectedStocks);
  setSelectedStocks(settings.selectedStocks);
} else {
  console.log('⚠️ No stocks saved, applying defaults');
  setSelectedStocks(DEFAULT_USER_SETTINGS.selectedStocks);
}


    // ✅ CRITICAL: Load cryptos with fallback
    if (settings?.selectedCryptos && Array.isArray(settings.selectedCryptos) && settings.selectedCryptos.length > 0) {
      console.log('💰 Applying saved cryptos:', settings.selectedCryptos);
      setSelectedCryptos(settings.selectedCryptos);
    } else {
      console.log('⚠️ No cryptos saved, keeping defaults');
      // Don't set defaults here - they're already set in useState
    }

    // ✅ Load news sources from new structure
    const newsSourcesData = await firestoreService.getSelectedNewsSources();
    const newsFeeds = newsSourcesData?.sources && newsSourcesData.sources.length > 0
      ? newsSourcesData.sources
      : settings?.newsFeeds || ['cnn', 'bbc', 'abc'];
    
    setSelectedNewsFeeds(newsFeeds);
    console.log('✅ News feeds loaded:', newsFeeds);

    // Apply other settings
    if (settings?.selectedHosts && settings.selectedHosts.length > 0) setSelectedHosts(settings.selectedHosts);
    if (settings?.userName) setUserName(settings.userName);
    if (settings?.showNewsCard !== undefined) setShowNewsCard(settings.showNewsCard);
    if (settings?.showStocksCard !== undefined) setShowStocksCard(settings.showStocksCard);
    if (settings?.showCryptoCard !== undefined) setShowCryptoCard(settings.showCryptoCard);
    if (settings?.talkingStyle) setTalkingStyle(settings.talkingStyle);
    if (settings?.introMusicEnabled !== undefined) setIntroMusicEnabled(settings.introMusicEnabled);
    
    console.log('✅ All settings loaded successfully');

    // ============================================
    // Load podcasts, briefs, articles (existing code)
    // ============================================
    
    try {
      const newPodcasts = await firestoreService.getSavedPodcasts();
      setSavedPodcasts(newPodcasts || []);
      console.log(`✅ Loaded ${newPodcasts?.length || 0} podcasts`);
    } catch (error) {
      console.error('❌ Error loading podcasts:', error);
      setSavedPodcasts([]);
    }

    try {
      const newBriefs = await firestoreService.getDailyBriefHistory();
      setSavedDailyBriefs(newBriefs || []);
      console.log(`✅ Loaded ${newBriefs?.length || 0} daily briefs`);
    } catch (error) {
      console.error('❌ Error loading briefs:', error);
      setSavedDailyBriefs([]);
    }

    try {
      const newArticles = await firestoreService.getSavedArticles();
      setSavedArticles(newArticles || []);
      console.log(`✅ Loaded ${newArticles?.length || 0} articles`);
    } catch (error) {
      console.error('❌ Error loading articles:', error);
      setSavedArticles([]);
    }

    try {
      const routes = await firestoreService.getTrafficRoutes();
      setSavedRoutes(routes || []);
      console.log(`✅ Loaded ${routes?.length || 0} routes`);
    } catch (error) {
      console.error('❌ Error loading routes:', error);
      setSavedRoutes([]);
    }

    setIsInitialLoading(false);
    console.log('✅ All Firestore data loaded successfully');

  } catch (error) {
    console.error('❌ Failed to load data from Firestore:', error);
    setIsInitialLoading(false);
  }
};

  loadAllDataFromFirestore();
}, [firestoreService]);
useEffect(() => {
  const detectAndSaveTimezone = async () => {
    try {
      // ✅ SILENT detection - no popup, no user interaction needed
      const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      console.log('🌍 Auto-detected timezone:', detectedTimezone);
      
      setUserTimezone(detectedTimezone);
      setTimezoneLoading(false);
      
      // Save to Firestore
      if (firestoreService?.isReady()) {
        await firestoreService.updateUserTimezone(detectedTimezone);
        console.log('✅ Timezone saved to Firestore');
      }
    } catch (error) {
      console.error('❌ Timezone detection failed:', error);
      setUserTimezone('America/New_York'); // Fallback
      setTimezoneLoading(false);
    }
  };
  
  detectAndSaveTimezone();
}, [firestoreService]);
useEffect(() => {
  // Detect user's timezone
  const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  setUserTimezone(detectedTimezone);
  
  // Save to Firestore
  if (firestoreService?.isReady()) {
    firestoreService.updateUserTimezone(detectedTimezone);
  }
  
  console.log('🌍 Detected timezone:', detectedTimezone);
}, [firestoreService]);

useEffect(() => {
  if (!dailyBriefConfig || !firestoreService) return;

  const checkForTodaysBrief = async () => {
    try {
      console.log('🔍 Checking for today\'s daily brief...');
      
      const todaysBrief = await firestoreService.getTodaysDailyBriefCatchUp();
      
      // ✅ CRITICAL FIX: Only set ready if AUDIO EXISTS
      if (todaysBrief && todaysBrief.audioUrl && todaysBrief.dialogue && todaysBrief.dialogue.length > 0) {
        console.log('✅ Found COMPLETE daily brief with audio:', todaysBrief.title);
        setDailyBriefPodcast(todaysBrief);
        setIsDailyBriefReady(true);
        setIsDailyBriefGenerating(false);
      } else if (todaysBrief && !todaysBrief.audioUrl) {
        console.warn('⚠️ Brief found but NO AUDIO - treating as incomplete');
        setDailyBriefPodcast(null);
        setIsDailyBriefReady(false);
        setIsDailyBriefGenerating(true);
      } else {
        console.log('📭 No daily brief generated yet today');
        setDailyBriefPodcast(null);
        setIsDailyBriefReady(false);
        
        const briefStatus = await firestoreService.getDailyBriefStatus();
        if (briefStatus?.isGenerating) {
          console.log('⏳ Brief is currently generating...');
          setIsDailyBriefGenerating(true);
        } else {
          setIsDailyBriefGenerating(false);
        }
      }
    } catch (error) {
      console.error('❌ Error checking for daily brief:', error);
      setIsDailyBriefReady(false);
      setIsDailyBriefGenerating(false);
    }
  };

  checkForTodaysBrief();
  const interval = setInterval(checkForTodaysBrief, 30000);
  return () => clearInterval(interval);
}, [dailyBriefConfig, firestoreService]);
const getNextBriefTime = (configTime, userTimezone) => {
  if (!configTime || !userTimezone) return null;
  
  try {
    const [hour, minute] = configTime.split(':').map(Number);
    
    // Get current time in user's timezone
    const nowInUserTZ = new Date(new Date().toLocaleString('en-US', { 
      timeZone: userTimezone 
    }));
    
    // Create target time for today in user's timezone
    const targetTime = new Date(nowInUserTZ);
    targetTime.setHours(hour, minute, 0, 0);
    
    // If target time has passed, set it for tomorrow
    if (targetTime <= nowInUserTZ) {
      targetTime.setDate(targetTime.getDate() + 1);
    }
    
    return targetTime;
  } catch (error) {
    console.error('Error calculating next brief time:', error);
    return null;
  }
};

// ============================================
// CHANGE 3: Add helper function to format the time display
// ============================================
// Add this function near your other helper functions:

const formatBriefTime = (configTime) => {
  if (!configTime) return '';
  
  try {
    const [hours, minutes] = configTime.split(':');
    const hour = parseInt(hours);
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    const period = hour >= 12 ? 'PM' : 'AM';
    return `${displayHour}${minutes !== '00' ? `:${minutes}` : ''} ${period}`;
  } catch (error) {
    return configTime;
  }
};

useEffect(() => {
  console.log('🔄 Card visibility updated:', {
    showNewsCard,
    showStocksCard,
    showCryptoCard,
    selectedStocks: selectedStocks.length,
    selectedCryptos: selectedCryptos.length
  });
  
  // Force re-render by updating a dummy state if needed
  setIsInitialLoading(false);
}, [showNewsCard, showStocksCard, showCryptoCard, selectedStocks, selectedCryptos]);

useEffect(() => {
  const loadRoutes = async () => {
    // ✅ Wait for service to be ready
    if (!firestoreService) {
      console.log('⏳ Waiting for podcast service...');
      return;
    }
    
    try {
      const routes = await firestoreService.getTrafficRoutes();
      setSavedRoutes(routes);
      console.log('📍 Loaded routes from Firestore:', routes);
    } catch (error) {
      console.error('Error loading routes:', error);
    }
  };
  
  loadRoutes();
}, [firestoreService]); // ✅ Re-run when firestoreService becomes available

useEffect(() => {
  loadPDFJS();
}, []);
// ✅ Force SVG line updates when modules connect
useEffect(() => {
  if (connectedModules.length > 0) {
    // Force re-render to update line positions
    const timer = setTimeout(() => {
      // Trigger component update
      setConnectedModules([...connectedModules]);
    }, 10);
    
    return () => clearTimeout(timer);
  }
}, [connectedModules.length]); // Only on length change

// ✅ SPOTIFY-STYLE TRANSCRIPT SYNC - Slower, more forgiving
useEffect(() => {
  if (!showTranscriptView || !currentPodcast?.dialogue || currentPodcast.dialogue.length === 0) return;
  
  let rafId;
  let lastActiveIndex = -1;

  const syncTranscript = () => {
    const audio = audioRef?.current;
    if (!audio) return;

    const currentTime = audio.currentTime;
    const duration = audio.duration;
    
    if (!duration || duration === 0) {
      rafId = requestAnimationFrame(syncTranscript);
      return;
    }

    const dialogue = currentPodcast.dialogue;
    
    // ✅ Calculate based on character proportion WITH LONGER buffers
    const totalChars = dialogue.reduce((sum, line) => sum + (line.line?.length || 0), 0);
    
    if (totalChars === 0) {
      rafId = requestAnimationFrame(syncTranscript);
      return;
    }
    
    // ✅ INCREASED: 1.5s buffer between each line for more natural feel
    const bufferPerLine = 4.5;
    const totalBufferTime = (dialogue.length - 1) * bufferPerLine;
    const effectiveDuration = duration - totalBufferTime;
    
    // Calculate cumulative positions with buffers
    let cumulativeChars = 0;
    let cumulativeTime = 0;
    let newActiveIndex = 0;
    
    for (let i = 0; i < dialogue.length; i++) {
      const lineChars = dialogue[i].line?.length || 0;
      const lineTime = (lineChars / totalChars) * effectiveDuration;
      const lineStartTime = cumulativeTime;
      const lineEndTime = cumulativeTime + lineTime + bufferPerLine;
      
      // ✅ INCREASED TOLERANCE: Stay on this line longer
      if (currentTime >= lineStartTime - 2.5 && currentTime < lineEndTime) {
        newActiveIndex = i;
        break;
      }
      
      cumulativeChars += lineChars;
      cumulativeTime += lineTime + bufferPerLine;
    }
    
    // Handle end of podcast
    if (currentTime >= duration * 0.95) {
      newActiveIndex = dialogue.length - 1;
    }

    // ✅ Only update if changed
    if (newActiveIndex !== lastActiveIndex) {
      lastActiveIndex = newActiveIndex;
      setActiveLineIndex(newActiveIndex);
      
      console.log(`🎯 Line ${newActiveIndex + 1}/${dialogue.length} at ${currentTime.toFixed(1)}s`);
      
      // Auto-scroll to center current line
      const activeElement = lineRefs.current[newActiveIndex];
      if (activeElement && transcriptRef.current) {
        const container = transcriptRef.current;
        const elementTop = activeElement.offsetTop;
        const containerHeight = container.clientHeight;
        const elementHeight = activeElement.clientHeight;
        
        const targetScroll = elementTop - (containerHeight / 2) + (elementHeight / 2);
        
        container.scrollTo({
          top: targetScroll,
          behavior: 'smooth'
        });
      }
    }
    
    rafId = requestAnimationFrame(syncTranscript);
  };

  rafId = requestAnimationFrame(syncTranscript);

  return () => {
    if (rafId) {
      cancelAnimationFrame(rafId);
    }
  };
}, [showTranscriptView, currentPodcast?.dialogue, audioRef]);
// ✅ Auto-scroll active chapter into view
useEffect(() => {
  if (!showTranscriptView || !currentPodcast || chapters.length === 0) return;
  
  const activeChapterIndex = chapters.findIndex((chapter, idx) => {
    return currentAudioTime >= chapter.timestamp && 
           (idx === chapters.length - 1 || currentAudioTime < chapters[idx + 1]?.timestamp);
  });
  
  if (activeChapterIndex !== -1) {
    const activeButton = chapterRefs.current[activeChapterIndex];
    const container = chaptersRef.current;
    
    if (activeButton && container) {
      const buttonLeft = activeButton.offsetLeft;
      const buttonWidth = activeButton.offsetWidth;
      const containerWidth = container.clientWidth;
      const scrollLeft = buttonLeft - (containerWidth / 2) + (buttonWidth / 2);
      
      container.scrollTo({
        left: scrollLeft,
        behavior: 'smooth'
      });
    }
  }
}, [currentAudioTime, chapters, showTranscriptView, currentPodcast]);
const extractChapterTitle = (text) => {
  if (!text) return 'Untitled Section';
  
  // Extract topic from transition phrases
  const patterns = [
    /(?:Moving on to|Now let's talk about|Let's discuss|Next up|Turning to)\s+(.+?)(?:\.|,|$)/i,
    /(?:In our|For our)\s+(.+?)\s+(?:segment|section)/i,
    /(?:Time for|Here's)\s+(.+?)(?:\.|,|$)/i
  ];
  
  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match && match[1]) {
      return match[1].trim();
    }
  }
  
  // Fallback: take first 50 chars and clean up
  return text
    .substring(0, 50)
    .replace(/[.,!?]$/, '')
    .trim();
};

// ✅ Smart highlight - ONLY truly important words (max 3, NO NAMES)
const highlightImportantWords = (text, keywords = [], userName = null) => {
  if (!keywords || keywords.length === 0) return text;
  
  // ✅ Take only first 3 keywords max
  const limitedKeywords = keywords.slice(0, 3);
  
  let result = text;
  
  // ✅ List of names to never highlight
  const hostNames = ['Emma', 'James', 'Jackie', 'Kenny'];
  const skipNames = [...hostNames];
  if (userName) {
    skipNames.push(userName);
  }
  
  limitedKeywords.forEach(keyword => {
    // Skip host names and user name
    if (skipNames.some(name => name.toLowerCase() === keyword.toLowerCase())) {
      return; // Skip this keyword
    }
    
    // Skip common words that shouldn't be highlighted
    const skipWords = ['afternoon', 'morning', 'evening', 'walk', 'park', 'hat', 
                       'street', 'day', 'time', 'way', 'thing', 'today', 'tonight',
                       'welcome', 'back', 'hey', 'hello', 'right', 'now', 'here'];
    
    if (skipWords.includes(keyword.toLowerCase())) {
      return; // Skip this keyword
    }
    
    // Case-insensitive but preserve original case in text
    const regex = new RegExp(`\\b(${keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})\\b`, 'gi');
    
    result = result.replace(regex, (match) => {
      const type = detectKeywordType(match);
      return `<span class="highlight highlight-${type}">${match}</span>`;
    });
  });
  
  return result;
};

// ✅ Helper function to determine keyword type for color coding
const detectKeywordType = (keyword) => {
  const lower = keyword.toLowerCase();
  
  // Time-related
  if (/\d{1,2}(?::\d{2})?\s*(?:am|pm)/i.test(keyword)) return 'time';
  
  // Numbers/Stats
  if (/[+-]?\d+\.?\d*%/.test(keyword)) return 'percentage';
  if (/\$[\d,]+\.?\d*/.test(keyword)) return 'money';
  if (/\d+°[FC]/.test(keyword)) return 'temp';
  
  // Health/Sleep related
  if (['melatonin', 'rem', 'sleep', 'hours', 'caffeine', 'exercise'].includes(lower)) return 'health';
  
  // Technology terms
  if (['ai', 'algorithm', 'quantum', 'neural', 'machine learning'].includes(lower)) return 'tech';
  
  // Stock tickers (all caps 2-5 letters)
  if (/^[A-Z]{2,5}$/.test(keyword)) return 'stock';
  
  // Companies/Brands
  if (/^[A-Z][a-z]+(?:\s+[A-Z][a-z]+)*$/.test(keyword)) return 'brand';
  
  // Default: general highlight
  return 'general';
};
const calculateTimestamp = (lineIndex, allLines, totalDuration) => {
  if (!allLines || allLines.length === 0 || lineIndex < 0) return 0;
  
  // Calculate based on character count proportion
  const charsBeforeLine = allLines
    .slice(0, lineIndex)
    .reduce((sum, l) => sum + (l.line?.length || 0), 0);
  
  const totalChars = allLines
    .reduce((sum, l) => sum + (l.line?.length || 0), 0);
  
  if (totalChars === 0) return 0;
  
  return (charsBeforeLine / totalChars) * totalDuration;
};

const calculateChapterDuration = (startIndex, allLines, totalDuration) => {
  if (!allLines || allLines.length === 0) return 0;
  
  // Find the next chapter/section
  const nextChapterIndex = allLines.findIndex((l, idx) => {
    if (idx <= startIndex) return false;
    return l.section || 
           l.line?.includes('Moving on to') || 
           l.line?.includes('Now let\'s talk about') ||
           l.line?.includes('Let\'s discuss') ||
           l.line?.includes('Next up') ||
           l.line?.includes('Turning to');
  });
  
  // If no next chapter, duration is from here to end
  if (nextChapterIndex === -1) {
    const endTimestamp = totalDuration;
    const startTimestamp = calculateTimestamp(startIndex, allLines, totalDuration);
    return endTimestamp - startTimestamp;
  }
  
  // Calculate duration to next chapter
  const nextTimestamp = calculateTimestamp(nextChapterIndex, allLines, totalDuration);
  const startTimestamp = calculateTimestamp(startIndex, allLines, totalDuration);
  
  return nextTimestamp - startTimestamp;
};

// ✅ FIX 5: Manual Scroll Detection - This goes FIRST
useEffect(() => {
  const container = transcriptRef.current;
  if (!container) return;

  const handleScroll = () => {
    // User is manually scrolling
    setIsUserScrolling(true);
    
    // Clear existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    
    // Resume auto-scroll after 3 seconds of no manual scrolling
    scrollTimeoutRef.current = setTimeout(() => {
      setIsUserScrolling(false);
    }, 3000);
  };

  container.addEventListener('scroll', handleScroll, { passive: true });
  
  return () => {
    container.removeEventListener('scroll', handleScroll);
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
  };
}, [showTranscriptView]);

// ✅ FIX 4: Real-time Transcript Sync - This goes SECOND
useEffect(() => {
  if (!showTranscriptView || dialogueTimestamps.length === 0) return;

  // ✅ Use requestAnimationFrame for smooth 60fps tracking
  let rafId;
  let lastActiveIndex = -1;

  const syncTranscript = () => {
    if (!audioRef.current) return;

    // ✅ Get current time directly from audio element
    const currentTime = audioRef.current.currentTime;
    
    // Binary search for current line - declare at function scope
    let newActiveIndex = 0;
    let left = 0;
    let right = dialogueTimestamps.length - 1;
    
    while (left <= right) {
      const mid = Math.floor((left + right) / 2);
      const line = dialogueTimestamps[mid];
      
      if (currentTime >= line.startTime && currentTime < line.endTime) {
        newActiveIndex = mid;
        break;
      } else if (currentTime < line.startTime) {
        right = mid - 1;
      } else {
        left = mid + 1;
      }
    }
    
    // Handle end of podcast
    if (currentTime >= dialogueTimestamps[dialogueTimestamps.length - 1].endTime) {
      newActiveIndex = dialogueTimestamps.length - 1;
    }

    // ✅ Only update if line changed (prevents unnecessary renders)
    if (newActiveIndex !== lastActiveIndex) {
      lastActiveIndex = newActiveIndex;
      setActiveLineIndex(newActiveIndex);
      
      // ✅ Smooth auto-scroll ONLY if user isn't manually scrolling
      if (!isUserScrolling) {
        const activeElement = lineRefs.current[newActiveIndex];
        if (activeElement && transcriptRef.current) {
          const container = transcriptRef.current;
          const elementTop = activeElement.offsetTop;
          const containerHeight = container.clientHeight;
          const elementHeight = activeElement.clientHeight;
          
          // Center the active line in viewport
          const targetScroll = elementTop - (containerHeight / 2) + (elementHeight / 2);
          
          container.scrollTo({
            top: targetScroll,
            behavior: 'smooth'
          });
        }
      }
    }
    
    // ✅ Continue animation loop ALWAYS (not just when playing)
    rafId = requestAnimationFrame(syncTranscript);
  };

  // Start sync loop
  rafId = requestAnimationFrame(syncTranscript);

  return () => {
    if (rafId) {
      cancelAnimationFrame(rafId);
    }
  };
}, [showTranscriptView, dialogueTimestamps, isUserScrolling]);
// ✅ Removed isPodcastPlaying from dependencies so it runs continuously
// ✅ Track audio time for transcript sync
// ✅ FIXED: Real-time audio time tracking with better sync
useEffect(() => {
  const audio = audioRef.current;
  if (!audio || !isPodcastPlaying) return;

  let animationFrameId;
  let lastUpdateTime = 0;
  const updateInterval = 100; // Update every 100ms (smooth but efficient)
  
  const updateTime = (timestamp) => {
    if (audio && !audio.paused && !audio.ended) {
      // Throttle updates for performance
      if (timestamp - lastUpdateTime >= updateInterval) {
        const currentTime = audio.currentTime;
        const duration = audio.duration;
        
        // Update time
        setCurrentAudioTime(currentTime);
        
        if (duration && !isNaN(duration) && isFinite(duration)) {
          setAudioDuration(duration);
        }
        
        lastUpdateTime = timestamp;
      }
      
      // Continue animation loop
      animationFrameId = requestAnimationFrame(updateTime);
    }
  };
  
  // Start the animation loop
  animationFrameId = requestAnimationFrame(updateTime);
  
  return () => {
    if (animationFrameId) {
      cancelAnimationFrame(animationFrameId);
    }
  };
}, [isPodcastPlaying]);

// ✅ FIXED: Better metadata loading
useEffect(() => {
  const audio = audioRef.current;
  if (!audio) return;

  const handleLoadedMetadata = () => {
    console.log('🎵 Audio metadata loaded, duration:', audio.duration);
    if (audio.duration && !isNaN(audio.duration) && isFinite(audio.duration)) {
      setAudioDuration(audio.duration);
      console.log('✅ Duration set:', audio.duration);
    }
  };

  const handleCanPlay = () => {
    console.log('🎵 Audio can play');
    if (audio.duration && !isNaN(audio.duration) && isFinite(audio.duration)) {
      setAudioDuration(audio.duration);
    }
  };

  const handleDurationChange = () => {
    console.log('🎵 Duration changed:', audio.duration);
    if (audio.duration && !isNaN(audio.duration) && isFinite(audio.duration)) {
      setAudioDuration(audio.duration);
    }
  };

  audio.addEventListener('loadedmetadata', handleLoadedMetadata);
  audio.addEventListener('canplay', handleCanPlay);
  audio.addEventListener('durationchange', handleDurationChange);

  // If audio is already loaded, set duration immediately
  if (audio.duration && !isNaN(audio.duration) && isFinite(audio.duration)) {
    setAudioDuration(audio.duration);
  }

  return () => {
    audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
    audio.removeEventListener('canplay', handleCanPlay);
    audio.removeEventListener('durationchange', handleDurationChange);
  };
}, []);

const formatTime = (seconds) => {
  if (isNaN(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};


const handleRewind = () => {
  setAnimateRewind(true);
  if (audioRef.current) {
    audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime - 15);
  }
  setTimeout(() => setAnimateRewind(false), 400); // ✅ Changed to 400ms
};

const handleFastForward = () => {
  setAnimateFastForward(true);
  if (audioRef.current) {
    audioRef.current.currentTime = Math.min(audioDuration, audioRef.current.currentTime + 15);
  }
  setTimeout(() => setAnimateFastForward(false), 400); // ✅ Changed to 400ms
};
const handleMouseDown = (e) => {
  setIsDragging(true);
  handleSeek(e);
};

const handleMouseMove = (e) => {
  if (isDragging) {
    handleSeek(e);
  }
};

const handleMouseUp = () => {
  setIsDragging(false);
};

const handleSpeedChange = (speed) => {
  setPlaybackSpeed(speed);
  setShowSpeedMenu(false);
  if (audioRef.current) {
    audioRef.current.playbackRate = speed;
  }
};
const handleSeek = (e) => {
  const rect = e.currentTarget.getBoundingClientRect();
  const percent = (e.clientX - rect.left) / rect.width;
  const newTime = percent * audioDuration;
  
  if (audioRef.current) {
    audioRef.current.currentTime = newTime;
    setCurrentAudioTime(newTime);
  }
};

const deleteSavedPodcast = async (podcastId) => {
  if (!firestoreService) {
    console.error('❌ Podcast service not initialized');
    return;
  }

  if (!window.confirm('Are you sure you want to delete this podcast?')) {
    return;
  }

  try {
    await firestoreService.deletePodcast(podcastId);
    
    // Update local state
    setSavedPodcasts(prev => prev.filter(p => p.id !== podcastId));
    
    console.log('✅ Podcast deleted successfully');
    alert('Podcast deleted successfully!');
  } catch (error) {
    console.error('❌ Failed to delete podcast:', error);
    alert('Failed to delete podcast. Please try again.');
  }
};

// ✅ Load saved podcasts from Firestore on mount
useEffect(() => {
  const loadSavedPodcasts = async () => {
    if (!firestoreService?.isReady()) {
      console.log('⏳ Service not ready yet for loading podcasts');
      return;
    }
    
    try {
      console.log('📥 Loading saved podcasts from Firestore...');
      
      const podcasts = await firestoreService.getSavedPodcasts();
      
      if (podcasts && podcasts.length > 0) {
        setSavedPodcasts(podcasts);
        console.log(`✅ Loaded ${podcasts.length} saved podcasts`);
      } else {
        console.log('📭 No saved podcasts found');
      }
    } catch (error) {
      console.error('❌ Failed to load saved podcasts:', error);
    }
  };
  
  loadSavedPodcasts();
}, [firestoreService?.isReady()]);
// ✅ Initialize podcast with proper chapters and duration
// ✅ FIXED: Initialize podcast playback with proper duration handling
const initializePodcastPlayback = (podcast) => {
  console.log('🎵 Initializing podcast playback:', podcast.title);
  
  setCurrentPodcast(podcast);
  setIsPodcastPlaying(false);
  setShowTranscriptView(true);
  setActiveLineIndex(0);
  setCurrentAudioTime(0);
  setAudioDuration(0);
  
  if (audioRef.current && podcast.audioUrl) {
    const audio = audioRef.current;
    
    // Clear any existing source
    audio.pause();
    audio.currentTime = 0;
    
    // Set new source
    audio.src = podcast.audioUrl;
    
    const handleLoadedMetadata = () => {
      const duration = audio.duration;
      console.log('✅ Audio loaded, duration:', duration);
      
      if (duration && !isNaN(duration) && isFinite(duration)) {
        setAudioDuration(duration);
        
        // Recalculate chapters with actual duration
        if (podcast.chapters && podcast.chapters.length > 0) {
          const recalculatedChapters = podcast.chapters.map(chapter => ({
            ...chapter,
            timestamp: calculateTimestamp(chapter.index, podcast.dialogue, duration),
            duration: calculateChapterDuration(chapter.index, podcast.dialogue, duration)
          }));
          setChapters(recalculatedChapters);
          console.log('✅ Chapters recalculated:', recalculatedChapters.length);
        } else if (podcast.dialogue) {
          // Generate chapters if they don't exist
          const extractedChapters = podcast.dialogue
            .map((line, idx) => {
              const hasSection = line.section;
              const hasTransition = line.line && (
                line.line.includes('Moving on to') || 
                line.line.includes('Now let\'s talk about') ||
                line.line.includes('Let\'s discuss') ||
                line.line.includes('Next up') ||
                line.line.includes('Turning to')
              );
              
              if (hasSection || hasTransition) {
                const title = line.section || extractChapterTitle(line.line);
                const timestamp = calculateTimestamp(idx, podcast.dialogue, duration);
                const chapterDuration = calculateChapterDuration(idx, podcast.dialogue, duration);
                
                return { title, timestamp, index: idx, duration: chapterDuration };
              }
              return null;
            })
            .filter(Boolean);
          
          setChapters(extractedChapters);
          console.log('✅ Generated chapters:', extractedChapters.length);
        }
      }
      
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
    };
    
    const handleError = (e) => {
      console.error('❌ Audio loading error:', e);
      alert('Failed to load audio. Please try again.');
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('error', handleError);
    };
    
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('error', handleError);
    audio.load();
  }
};
/**
 * ✅ Capture user's GPS location for weather accuracy
 */
const captureUserLocation = async () => {
  try {
    console.log('📍 Requesting user location...');
    
    const position = await new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not supported'));
        return;
      }
      
      navigator.geolocation.getCurrentPosition(
        resolve,
        reject,
        { timeout: 10000, enableHighAccuracy: false }
      );
    });
    
    const { latitude, longitude } = position.coords;
    
    // Reverse geocode to get city name
    const response = await fetch(
      `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
    );
    const data = await response.json();
    
    const locationData = {
      lat: latitude,
      lon: longitude,
      city: data.city || data.locality || 'Unknown',
      state: data.principalSubdivision || null,
      country: data.countryName || null,
      detectionMethod: 'gps',
      capturedAt: new Date().toISOString()
    };
    
    // Save to Firestore
    if (firestoreService?.isReady()) {
      await firestoreService.updateUserData({
        location: locationData
      });
      console.log('✅ Location captured:', locationData.city);
    }
    
    return locationData;
    
  } catch (error) {
    console.warn('⚠️ Could not capture location:', error);
    return null;
  }
};
const handleSaveDailyBriefSetup = async () => {
  if (!firestoreService?.isReady()) {
    alert('Service not ready. Please wait a moment and try again.');
    return;
  }

  if (dailyBriefModules.length < 2) {
    alert('Please select at least 2 modules for your daily brief');
    return;
  }
  
  if (dailyBriefModules.includes('traffic') && !dailyBriefTrafficRoute) {
    alert('Please select a traffic route for your daily brief');
    return;
  }
  
  try {
    // ✅ CAPTURE LOCATION if weather is enabled
    if (dailyBriefModules.includes('weather')) {
      console.log('📍 Weather enabled - capturing location...');
      await captureUserLocation();
    }
    
    const config = {
      enabled: true,
      time: dailyBriefTime,
      modules: dailyBriefModules,
      trafficRoute: dailyBriefTrafficRoute,
      hosts: selectedHosts,
      guest: selectedGuest,
      introMusicEnabled: introMusicEnabled,
      timezone: userTimezone || 'America/New_York'
    };
    
    await firestoreService.saveDailyBriefCatchUpConfig(config);
    
    setDailyBriefConfig(config);
    setShowDailyBriefSetup(false);
    setIsDailyBriefReady(false);
    setDailyBriefPodcast(null);
    
    const [hour, minute] = dailyBriefTime.split(':').map(Number);
    const now = new Date();
    const nowInUserTZ = new Date(now.toLocaleString('en-US', { timeZone: userTimezone }));
    const nextGen = new Date(nowInUserTZ);
    nextGen.setHours(hour, minute, 0, 0);
    
    if (nextGen <= nowInUserTZ) {
      nextGen.setDate(nextGen.getDate() + 1);
    }
    
    const timeUntil = Math.floor((nextGen - nowInUserTZ) / 1000 / 60);
    const hoursUntil = Math.floor(timeUntil / 60);
    const minutesUntil = timeUntil % 60;
    
    alert(`Daily Brief configured! Your first brief will be ready at ${formatBriefTime(dailyBriefTime)}`);
    console.log(`✅ Daily brief configured: ${hoursUntil}h ${minutesUntil}m until first brief`);
    
  } catch (error) {
    console.error('❌ Error saving daily brief config:', error);
    alert('Failed to save configuration. Please try again.');
  }
};
const handlePlaySavedPodcast = async (podcast, isDailyBrief = false) => {
  console.log('🎵 Playing saved podcast:', podcast);
  
  // Track play in Firestore
  if (firestoreService && podcast.id) {
    await firestoreService.trackPodcastPlay(podcast.id, isDailyBrief);
  }
  
  // Reset transcript state
  setActiveLineIndex(0);
  // ✅ REMOVED: setShowTranscriptView(true) - Let mini player show instead
  
  // Initialize playback (this will auto-play)
  initializePodcastPlayback(podcast);
};

// Remove saved podcast
const removeSavedPodcast = async (podcastId) => {
  try {
    // Delete from Firestore
    await firestoreService.deletePodcast(podcastId);
    
    // Update local state
    setSavedPodcasts(prev => prev.filter(p => p.id !== podcastId));
    
    console.log('✅ Podcast deleted');
  } catch (error) {
    console.error('❌ Failed to delete podcast:', error);
    alert('Failed to delete podcast');
  }
};
// Check if podcast is saved
const isPodcastSaved = (audioUrl) => {
  return savedPodcasts.some(p => p.audioUrl === audioUrl);
};

const saveArticle = async (articleData) => {
  if (!firestoreService?.isReady()) {
    alert('Service not ready. Please wait a moment and try again.');
    return;
  }

  try {
    // ✅ Check if already saved
    const alreadySaved = savedArticles.some(a => a.source === articleData.source);
    
    if (alreadySaved) {
      alert('Article already saved!');
      return;
    }

    console.log('💾 Saving article:', articleData.title);
    
    // ✅ CRITICAL: Save to Firestore and get the ID back
    const result = await firestoreService.saveArticle({
      ...articleData,
      savedAt: new Date().toISOString()
    });
    
    if (!result || !result.id) {
      throw new Error('Failed to get article ID from Firestore');
    }
    
    console.log('✅ Article saved with ID:', result.id);
    
    // ✅ Add to local state with the Firestore ID
    setSavedArticles(prev => [...prev, {
      ...articleData,
      id: result.id, // ✅ CRITICAL: Store the Firestore ID
      savedAt: result.savedAt || new Date().toISOString()
    }]);
    
  } catch (error) {
    console.error('❌ Failed to save article:', error);
    alert('Failed to save article. Please try again.');
  }
};

// ✅ IMPROVED: Check for today's daily brief with proper date handling
useEffect(() => {
  if (!dailyBriefConfig || !firestoreService || !userTimezone) return;

  const checkForTodaysBrief = async () => {
    try {
      console.log('🔍 Checking for today\'s daily brief...');
      
      // Get today's date in user's timezone
      const todayInUserTZ = getTodayInTimezone(userTimezone);
      console.log(`📅 Today in ${userTimezone}: ${todayInUserTZ}`);
      
const todaysBrief = await firestoreService.getTodaysDailyBriefCatchUp();
      
      if (todaysBrief) {
        console.log(`📦 Found brief:`, {
          title: todaysBrief.title,
          generatedFor: todaysBrief.generatedFor,
          hasAudio: !!todaysBrief.audioUrl,
          listened: todaysBrief.listened
        });
      }
      
      // ✅ CRITICAL: Check if brief is for TODAY and has all required data
      const isBriefForToday = todaysBrief?.generatedFor === todayInUserTZ;
      const hasBriefData = todaysBrief?.audioUrl && 
                          todaysBrief?.dialogue && 
                          todaysBrief.dialogue.length > 0;
      
      if (isBriefForToday && hasBriefData) {
        console.log('✅ Found COMPLETE daily brief for TODAY');
        setDailyBriefPodcast(todaysBrief);
        setIsDailyBriefReady(true);
        setIsDailyBriefGenerating(false);
      } 
      else if (todaysBrief && !isBriefForToday) {
        console.log(`⚠️ Brief found but it's for ${todaysBrief.generatedFor}, not ${todayInUserTZ}`);
        // Brief is old (from yesterday)
        setDailyBriefPodcast(null);
        setIsDailyBriefReady(false);
        
        // Check if currently generating
        const briefStatus = await firestoreService.getDailyBriefStatus();
        setIsDailyBriefGenerating(briefStatus?.isGenerating || false);
      }
      else if (todaysBrief && !hasBriefData) {
        console.warn('⚠️ Brief found but NO AUDIO/DIALOGUE - treating as incomplete');
        setDailyBriefPodcast(null);
        setIsDailyBriefReady(false);
        setIsDailyBriefGenerating(true);
      } 
      else {
        console.log('📭 No daily brief generated yet today');
        setDailyBriefPodcast(null);
        setIsDailyBriefReady(false);
        
        const briefStatus = await firestoreService.getDailyBriefStatus();
        if (briefStatus?.isGenerating) {
          console.log('⏳ Brief is currently generating...');
          setIsDailyBriefGenerating(true);
        } else {
          setIsDailyBriefGenerating(false);
        }
      }
    } catch (error) {
      console.error('❌ Error checking for daily brief:', error);
      setIsDailyBriefReady(false);
      setIsDailyBriefGenerating(false);
    }
  };

  checkForTodaysBrief();
  
  // Check every 30 seconds
  const interval = setInterval(checkForTodaysBrief, 30000);
  
  return () => clearInterval(interval);
}, [dailyBriefConfig, firestoreService, userTimezone]);

// ✅ HELPER: Get today's date in user's timezone (MM/DD/YYYY format)
function getTodayInTimezone(timezone) {
  if (!timezone) return null;
  
  try {
    const now = new Date();
    const dateString = now.toLocaleDateString('en-US', { 
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    
    console.log(`📅 Today in ${timezone}: ${dateString}`);
    return dateString; // Returns: "11/13/2025" (MM/DD/YYYY)
  } catch (error) {
    console.error('Error getting date in timezone:', error);
    return null;
  }
}
// ✅ Load saved articles from Firestore on mount
useEffect(() => {
  const loadSavedArticles = async () => {
    if (!firestoreService?.isReady()) {
      console.log('⏳ Service not ready yet for loading articles');
      return;
    }
    
    try {
      console.log('📥 Loading saved articles from Firestore...');
      
      const articles = await firestoreService.getSavedArticles();
      
      if (articles && articles.length > 0) {
        // ✅ Verify IDs are correct (should be Firestore doc IDs, not URLs)
        const validArticles = articles.map(article => ({
          ...article,
          id: article.id // Firestore document ID
        }));
        
        setSavedArticles(validArticles);
        console.log(`✅ Loaded ${validArticles.length} saved articles`);
        
        // 🔍 DEBUG: Log first article to verify structure
        if (validArticles.length > 0) {
          console.log('Sample article:', {
            id: validArticles[0].id,
            title: validArticles[0].title?.substring(0, 30),
            source: validArticles[0].source?.substring(0, 40)
          });
        }
      } else {
        console.log('📭 No saved articles found');
      }
    } catch (error) {
      console.error('❌ Failed to load saved articles:', error);
    }
  };
  
  loadSavedArticles();
}, [firestoreService?.isReady()]);
// Remove saved article with confirmation
const removeSavedArticle = async (articleId) => {
  try {
    // ✅ Validate article ID
    if (!articleId || typeof articleId !== 'string') {
      console.error('❌ Invalid article ID:', articleId);
      return;
    }
    
    // ✅ Reject if it's a URL instead of an ID
    if (articleId.includes('http') || articleId.includes('www') || articleId.includes('/')) {
      console.error('❌ Received URL instead of ID:', articleId);
      alert('Cannot delete this article. Please refresh the page and try again.');
      return;
    }
    
    console.log('🗑️ Deleting article ID:', articleId);
    
    // ✅ Delete from Firestore using correct path
    await firestoreService.removeSavedArticle(articleId); // Use the correct method name
    
    // ✅ Update local state
    setSavedArticles(prev => prev.filter(a => a.id !== articleId));
    
    console.log('✅ Article deleted successfully');
  } catch (error) {
    console.error('❌ Failed to delete article:', error);
    alert('Failed to delete article. Please try again.');
  }
};
// Check if article is saved
const isArticleSaved = (articleUrl) => {
  return savedArticles.some(a => a.source === articleUrl);
};

  // Fetch stock data
  const fetchStockData = async () => {
    try {
      console.log('📊 Fetching stock data from backend...');
      
      const BACKEND_URL = 'https://us-central1-fir-7c9c9.cloudfunctions.net/fetchStocks';
      
      const response = await fetch(BACKEND_URL, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) {
        console.error(`❌ Backend returned HTTP ${response.status}`);
        return;
      }
      
      const data = await response.json();
      
      if (data.success && data.stocks && data.stocks.length > 0) {
        setStockData(data.stocks);
        console.log(`✅ Stock data loaded: ${data.stocks.length}/11 stocks`);
        
        // Log each stock for verification
        data.stocks.forEach(stock => {
          console.log(`  📈 ${stock.ticker}: $${stock.price} (${stock.percent})`);
        });
      } else {
        console.error('❌ No stock data received from backend');
      }
      
    } catch (error) {
      console.error('❌ Error fetching stock data:', error);
    }
  };

  useEffect(() => {
    fetchStockData();
    const interval = setInterval(fetchStockData, 60000);
    return () => clearInterval(interval);
  }, []);

// Helper function to extract and remove YouTube URL from text
// ✅ Add this ref at the top with your other useRef declarations
const isAutoCleaningUrl = useRef(false);

// ✅ FIXED useEffect - Detect YouTube URL, remove it, and keep icon visible
useEffect(() => {
  const hasYouTubeUrl = containsYouTubeUrl(promptText);
  const videoId = hasYouTubeUrl ? extractYouTubeId(promptText) : null;
  
  if (hasYouTubeUrl && videoId) {
    // ✅ IMMEDIATELY remove URL from input box
    const { cleanText } = extractAndRemoveVideoUrl(promptText);
    
    if (cleanText !== promptText) {
      // Mark that we're auto-cleaning (not user action)
      isAutoCleaningUrl.current = true;
      
      setTimeout(() => {
        setPromptText(cleanText);
        // Reset flag after a short delay
        setTimeout(() => {
          isAutoCleaningUrl.current = false;
        }, 100);
      }, 0);
    }
    
    // ✅ Show YouTube icon IMMEDIATELY
    if (!connectedModules.includes('youtube')) {
      setConnectedModules(prev => [...prev, 'youtube']);
    }
    
    // ✅ Add YouTube to modules list IMMEDIATELY
    setModules(prev => {
      if (!prev.find(m => m.id === 'youtube')) {
        return [
          ...prev,
          { 
            id: 'youtube', 
            name: 'YouTube', 
            icon: YouTubeIcon, 
            color: '#FF0000' 
          }
        ];
      }
      return prev;
    });
    
    // ✅ Only fetch analysis if video ID changed
    if (videoId !== youtubeVideoId) {
      setYoutubeVideoId(videoId);
      setYoutubeVideoTitle('Analyzing...');
      
      (async () => {
        try {
          const analysis = await getYouTubeVideoAnalysis(videoId);
          
          if (analysis) {
            setYoutubeVideoTitle(analysis.title);
            setYoutubeVideoAnalysis(analysis);
            
            console.log('✅ YouTube video analyzed:', {
              title: analysis.title,
              topics: analysis.topics
            });
          } else {
            setYoutubeVideoTitle('YouTube Video');
          }
        } catch (error) {
          console.error('❌ YouTube analysis error:', error);
          setYoutubeVideoTitle('YouTube Video');
        }
      })();
    }
  } else if (!hasYouTubeUrl && youtubeVideoId) {
    // ✅ CRITICAL: Only clean up if user manually cleared (not auto-cleaning)
    if (!isAutoCleaningUrl.current && promptText.length === 0) {
      console.log('🗑️ User manually cleared - removing YouTube module');
      setYoutubeVideoId(null);
      setYoutubeVideoTitle(null);
      setYoutubeVideoAnalysis(null);
      setConnectedModules(prev => prev.filter(id => id !== 'youtube'));
      setModules(prev => prev.filter(m => m.id !== 'youtube'));
    }
  }
}, [promptText]);

// REPLACE this entire useEffect:
useEffect(() => {
  // Only run if we're about to generate AND URL is still in text
  if (youtubeVideoId && containsYouTubeUrl(promptText)) {
    // This will be called BEFORE handleGenerate runs
    // DO NOTHING HERE - we'll clean it in handleGenerate instead
  }
}, []); // Empty dependencies - we'll handle this in handleGenerate


  // Keep audio src synced with currentPodcast
useEffect(() => {
  if (currentPodcast && audioRef.current) {
    if (audioRef.current.src !== currentPodcast.audioUrl) {
      console.log('🔄 Syncing audio src');
      audioRef.current.src = currentPodcast.audioUrl;
      audioRef.current.load();
    }
  }
}, [currentPodcast]);
  // Force update current time more frequently
// ✅ FIXED - Use requestAnimationFrame instead of setInterval
useEffect(() => {
  if (!audioRef.current || !isPodcastPlaying) return;
  
  let rafId;
  
  const updateTime = () => {
    if (audioRef.current && isPodcastPlaying) {
      setCurrentAudioTime(audioRef.current.currentTime);
      rafId = requestAnimationFrame(updateTime);
    }
  };
  
  rafId = requestAnimationFrame(updateTime);
  
  return () => {
    if (rafId) {
      cancelAnimationFrame(rafId);
    }
  };
}, [isPodcastPlaying]);
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);


useEffect(() => {
  if (!firestoreService?.isReady()) return;

  const unsubscribe = firestoreService.listenToDailyBriefStatus((status) => {
    // Only track if brief is currently generating
    setIsGenerating(status?.isGenerating || false);
  });

  return () => unsubscribe?.();
}, [firestoreService]);
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStoryIndex(prev => {
        const newIndex = {};
        Object.keys(newsData).forEach(sourceId => {
          const currentIdx = prev[sourceId] || 0;
          const storiesLength = newsData[sourceId].stories.length;
          newIndex[sourceId] = (currentIdx + 1) % storiesLength;
        });
        return newIndex;
      });
    }, 5000);
    return () => clearInterval(interval);
  }, []);

 const customScrollStyle = {
    display: 'flex', 
    gap: '12px', 
    overflowX: 'auto', 
    paddingLeft: '16px', 
    paddingRight: '16px', 
    paddingBottom: '8px',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    WebkitOverflowScrolling: 'touch',
    scrollbarColor: 'transparent transparent'
  };

// Save shown articles to firestore
useEffect(() => {
  const saveShownArticles = async () => {
    if (!firestoreService || shownArticles.size === 0) return;
    
    try {
      await firestoreService.saveShownArticles(shownArticles);
    } catch (error) {
      console.error('Error saving shown articles:', error);
    }
  };
  
  saveShownArticles();
}, [shownArticles, firestoreService]);
// Load shown articles on mount

  const StockIcon = () => (
    <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="none" viewBox="0 0 24 24">
      <path stroke="#10b981" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.4" d="M13.6 16.733c.234.269.548.456.895.534a1.4 1.4 0 0 0 1.75-.762c.172-.615-.446-1.287-1.242-1.481-.796-.194-1.41-.861-1.241-1.481a1.4 1.4 0 0 1 1.75-.762c.343.077.654.26.888.524m-1.358 4.017v.617m0-5.939v.725M4 15v4m3-6v6M6 8.5 10.5 5 14 7.5 18 4m0 0h-3.5M18 4v3m2 8a5 5 0 1 1-10 0 5 5 0 0 1 10 0Z"/>
    </svg>
  );

  // Add this BEFORE the modules array
const FileUploadModule = () => (
          <svg className="w-6 h-6 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="none" viewBox="0 0 24 24">
  <path stroke="#70a0ffff" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M7 8v8a5 5 0 1 0 10 0V6.5a3.5 3.5 0 1 0-7 0V15a2 2 0 0 0 4 0V8"/>
</svg>
);
// YouTube icon component
const YouTubeIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" fill="#FF0000"/>
  </svg>
);
const extractYouTubeId = (url) => {
  // Remove www. prefix if present
  const cleanUrl = url.replace(/^www\./, '');
  
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /youtube\.com\/shorts\/([^&\n?#]+)/,
    /youtu\.be\/([^&\n?#\s]+)/, // Handle youtu.be format better
    /youtube\.com\/watch\?v=([^&\n?#\s]+)/ // Handle watch format better
  ];
  
  for (const pattern of patterns) {
    const match = cleanUrl.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }
  return null;
};

// Check if text contains a YouTube URL
const containsYouTubeUrl = (text) => {
  // Remove www. for detection
  const cleanText = text.replace(/www\./g, '');
  return /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([^&\n?#\s]+)/.test(cleanText);
};

const [modules, setModules] = useState([
  { id: 'weather', name: 'Weather Forecast', icon: Cloud, color: '#60a5fa' },
  { id: 'news', name: 'News', icon: Newspaper, color: '#f87171' },
  { id: 'schedule', name: 'Schedule', icon: Calendar, color: '#a78bfa' },
  { id: 'stocks', name: 'Stock Watchlist', icon: StockIcon, color: '#10b981' },
  { id: 'traffic', name: 'Live Traffic Update', icon: Car, color: '#f59e0b' },
  { id: 'upload', name: 'Upload File', icon: FileUploadModule, color: '#3b82f6' }
]);
  const getDistance = (x1, y1, x2, y2) => {
    return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
  };

const getGreeting = () => {
  const hour = new Date().getHours();

  if (hour >= 5 && hour < 12) {
    return userName ? `Good morning, ${userName}` : 'Good morning';
  } else if (hour >= 12 && hour < 17) {
    return userName ? `Good afternoon, ${userName}` : 'Good afternoon';
  } else {
    return userName ? `Good evening, ${userName}` : 'Good evening';
  }
};

const toggleModule = (moduleId) => {
  // ✅ CRITICAL: Don't block YouTube removal
  if (moduleId === 'youtube') {
    // Allow removing YouTube module
    setConnectedModules(prev => prev.filter(id => id !== 'youtube'));
    setYoutubeVideoId(null);
    setYoutubeVideoTitle(null);
    setYoutubeVideoAnalysis(null);
    setModules(prev => prev.filter(m => m.id !== 'youtube'));
    setShowModuleDropdown(false);
    return;
  }

  // ✅ NEW: Handle news module with type selector
  if (moduleId === 'news') {
    if (connectedModules.includes('news')) {
      // Remove news module
      setConnectedModules(prev => prev.filter(id => id !== 'news'));
    } else {
      // Check 4-tool limit
      if (connectedModules.length >= 4) {
        alert('Maximum 4 tools allowed. Please remove a tool before adding another.');
        setShowModuleDropdown(false);
        return;
      }
      // Show news type selector
      setShowNewsTypeSelector(true);
    }
    setShowModuleDropdown(false);
    return;
  }

  // ✅ FIXED: Handle traffic module
  if (moduleId === 'traffic' && !connectedModules.includes(moduleId)) {
    if (savedRoutes.length === 0) {
      setShowRouteSetup(true);
      setShowModuleDropdown(false);
      return;
    }
  }
  
  // ✅ CRITICAL FIX: Handle file upload module properly
  if (moduleId === 'upload') {
    if (connectedModules.includes('upload')) {
      // REMOVING upload module - clear file data
      console.log('🗑️ Removing upload module and clearing file');
      setConnectedModules(prev => prev.filter(id => id !== 'upload'));
      setUploadedFile(null);
      setUploadedFileContent(null);
      setUploadedFileType(null);
    } else {
      // ADDING upload module
      
      // ✅ Check 4-tool limit BEFORE opening file picker
      if (connectedModules.length >= 4) {
        alert('Maximum 4 tools allowed. Please remove a tool before uploading a file.');
        setShowModuleDropdown(false);
        return;
      }
      
      console.log('📤 Opening file picker for upload module');
      // Open file picker - handleFileUpload will add to connectedModules
      fileInputRef.current?.click();
    }
    setShowModuleDropdown(false);
    return;
  }
  
  // ✅ Handle all other modules (weather, stocks, schedule, etc.)
  const isAdding = !connectedModules.includes(moduleId);
  
  if (isAdding && connectedModules.length >= 4) {
    alert('Maximum 4 tools allowed. Please remove a tool before adding another.');
    setShowModuleDropdown(false);
    return;
  }
  
  setConnectedModules(prev => 
    prev.includes(moduleId) 
      ? prev.filter(id => id !== moduleId)
      : [...prev, moduleId]
  );
  setShowModuleDropdown(false);
};
// ADD this with your other useEffects:
useEffect(() => {
  const runPersonalization = async () => {
    if (!newsData || Object.keys(newsData).length === 0) return;
    if (isPersonalizing) return;
    
    console.log('🔄 Running personalization...');
    const personalized = await personalizeNewsData(newsData);
    setPersonalizedNewsData(personalized);
  };

  const timer = setTimeout(runPersonalization, 1000);
  
  return () => clearTimeout(timer);
}, [newsData]);

// upload file

const handleFileUpload = async (event) => {
  const file = event.target.files[0];
  if (!file) {
    console.log('❌ No file selected');
    return;
  }

  console.log('📂 File selected:', file.name, file.type);

  try {
    console.log('📖 Extracting text from file...');
    const content = await extractTextFromFile(file);
    
    if (!content) {
      console.error('❌ No content extracted from file');
      alert('Failed to read file content. Please try a different file.');
      event.target.value = '';
      return;
    }
    
    console.log(`✅ File content extracted: ${content.length} characters`);

    // ✅ Determine file type
    let fileType = 'PDF';
    if (file.name.endsWith('.docx') || file.type.includes('wordprocessingml')) {
      fileType = 'DOCX';
    } else if (file.type === 'text/plain' || file.name.endsWith('.txt')) {
      fileType = 'TXT';
    }
    
    console.log(`📄 File type detected: ${fileType}`);
    
    // ✅ UPDATE STATE - All at once for consistency
    setUploadedFile(file);
    setUploadedFileContent(content);
    setUploadedFileType(fileType);
    
    // ✅ CRITICAL: Immediately update connectedModules with 'upload'
    // Use functional update to ensure we get latest state
    setConnectedModules(prev => {
      console.log('📝 Current connectedModules before update:', prev);
      
      if (prev.includes('upload')) {
        console.log('✅ upload module already in connectedModules');
        return prev;
      }
      
      // Check 4-tool limit before adding
      if (prev.length >= 4) {
        alert('Maximum 4 tools allowed. Please remove a tool before uploading a file.');
        setUploadedFile(null);
        setUploadedFileContent(null);
        setUploadedFileType(null);
        event.target.value = '';
        throw new Error('Tool limit reached');
      }
      
      const updated = [...prev, 'upload'];
      console.log('✅ Adding upload module to connectedModules:', updated);
      return updated;
    });
    
    console.log('✅ File upload complete:', {
      name: file.name,
      type: fileType,
      size: content.length,
      timestamp: new Date().toISOString()
    });
    
  } catch (error) {
    if (error.message === 'Tool limit reached') {
      event.target.value = '';
      return;
    }
    
    console.error('❌ File upload error:', error);
    alert('Failed to process file. Please try again.');
    setUploadedFile(null);
    setUploadedFileContent(null);
    setUploadedFileType(null);
  } finally {
    // Clear input so same file can be re-uploaded
    event.target.value = '';
  }
};

// ✅ ADD THIS FUNCTION after your imports (around line 50)
const generateShortPodcastTitle = async (youtubeTitle, topics, AZURE_CONFIG) => {
  try {
    const prompt = `Given this YouTube video title: "${youtubeTitle}"
And these key topics: ${topics.join(', ')}

Generate a SHORT podcast episode title (3-4 words maximum).
Make it catchy and descriptive.

Examples:
- YouTube: "Bill Ackman on Why NYC Elections Matter" → Podcast: "NYC Politics Breakdown"
- YouTube: "The Future of AI and What It Means" → Podcast: "AI's Future Impact"  
- YouTube: "Understanding Climate Change Policy 2024" → Podcast: "Climate Policy 2024"

Return ONLY the short title, nothing else.`;

    const response = await fetch(
      `${AZURE_CONFIG.ENDPOINT}/openai/deployments/${AZURE_CONFIG.DEPLOYMENT}/chat/completions?api-version=2024-02-15-preview`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'api-key': AZURE_CONFIG.KEY
        },
        body: JSON.stringify({
          messages: [{ role: 'user', content: prompt }],
          max_tokens: 50,
          temperature: 0.7
        })
      }
    );

    const data = await response.json();
    const shortTitle = data.choices[0].message.content.trim();
    
    // Remove quotes if AI added them
    return shortTitle.replace(/^["']|["']$/g, '');
    
  } catch (error) {
    console.error('❌ Error generating short title:', error);
    // Fallback: truncate original title
    return youtubeTitle.substring(0, 30).trim() + (youtubeTitle.length > 30 ? '...' : '');
  }
};
const filterUnseenLocalNews = (localNewsData, shownArticles) => {
  if (!localNewsData || !localNewsData.mainCity) {
    console.warn('⚠️ Invalid local news data structure');
    return localNewsData;
  }

  const filtered = {
    ...localNewsData,
    mainCity: {
      ...localNewsData.mainCity,
      articles: (localNewsData.mainCity.articles || []).filter(
        article => !shownArticles.has(article.url)
      )
    },
    nearbyCities: (localNewsData.nearbyCities || []).map(city => ({
      ...city,
      articles: (city.articles || []).filter(article => !shownArticles.has(article.url))
    })),
    stateNews: {
      ...localNewsData.stateNews,
      articles: ((localNewsData.stateNews && localNewsData.stateNews.articles) || []).filter(
        article => !shownArticles.has(article.url)
      )
    }
  };
  
  filtered.totalArticles = 
    filtered.mainCity.articles.length +
    filtered.nearbyCities.reduce((sum, city) => sum + city.articles.length, 0) +
    filtered.stateNews.articles.length;
  
  console.log(`🔍 Filtered local news: ${filtered.totalArticles} unseen articles`);
  
  return filtered;
};
useEffect(() => {
  const checkNewsOnboardingStatus = async () => {
    try {
      // Wait for Firestore to be ready
      if (!firestoreService?.isReady?.()) return;
      
      // ✅ Check Firestore for news onboarding completion
      const userData = await firestoreService.getUserData();
      const hasCompletedNewsOnboarding = userData?.settings?.hasCompletedNewsOnboarding;
      
      console.log('🔍 News Onboarding check:', {
        hasCompletedNewsOnboarding: hasCompletedNewsOnboarding
      });

      if (!hasCompletedNewsOnboarding) {
        console.log('⚠️ News onboarding incomplete - showing modal');
        setShowOnboarding(true);
      } else {
        console.log('✅ News onboarding complete - not showing modal');
        setShowOnboarding(false);
      }
    } catch (error) {
      console.error('Error checking news onboarding:', error);
      // On error, show onboarding to be safe
      setShowOnboarding(true);
    }
  };
  
  checkNewsOnboardingStatus();
}, [firestoreService]);

// ============================================
// HANDLE NEWS ONBOARDING COMPLETE
// ============================================

const handleNewsOnboardingComplete = () => {
  console.log('✅ News Onboarding completed');
  setShowOnboarding(false);
  // Firestore save happens inside FixedNewsOnboarding component
};

const handleGenerate = async () => {
  console.log('🎙️ handleGenerate called');
  
  // ✅ IMPROVED VALIDATION: Check all content sources
  const hasModules = connectedModules.length > 0;
  const hasPrompt = promptText.trim().length > 0;
  const hasFile = uploadedFileContent && uploadedFileContent.trim().length > 0;
  const hasYouTube = youtubeVideoId && youtubeVideoAnalysis;

  console.log('🔍 Generation validation:', {
    hasModules,
    modules: connectedModules,
    hasPrompt,
    promptLength: promptText.length,
    hasFile,
    fileLength: uploadedFileContent?.length || 0,
    fileName: uploadedFile?.name || 'none',
    hasYouTube
  });

  // ✅ FIXED: Allow generation if ANY content source exists
  if (!hasModules && !hasPrompt && !hasFile && !hasYouTube) {
    alert('Please select at least one tool, enter a topic, or upload a file for your podcast.');
    console.log('❌ No content sources available');
    return;
  }

  // ============================================
  // PREPARE USER INPUT WITH FILE CONTENT
  // ============================================
  
  let userInput = promptText || '';
  let fileContentForGeneration = null;

  // ✅ CRITICAL: If file was uploaded, prepare it for generation
  if (uploadedFileContent && uploadedFileContent.trim().length > 0) {
    console.log(`📄 File detected: ${uploadedFile.name}`);
    console.log(`   Content length: ${uploadedFileContent.length} characters`);
    console.log(`   Content preview: ${uploadedFileContent.substring(0, 150)}...`);

    // ✅ Store the actual file content
    fileContentForGeneration = uploadedFileContent;

    // ✅ Create the prompt that INCLUDES file content
    // If user typed something, use it; otherwise create default prompt
    if (userInput.trim()) {
      // User gave specific instruction (e.g., "summarize this")
      console.log(`📝 User instruction: "${userInput}"`);
      
      // DON'T add file content to userInput here - it goes to fileContent param
      // Keep userInput as the specific instruction
    } else {
      // No user instruction - create a good default based on file type
      const defaultPrompts = {
        'PDF': `Create an engaging podcast discussion about this PDF document. Cover the main topics, key points, and important details. Make it conversational and help me understand the material.`,
        'DOCX': `Create an engaging podcast discussion about this Word document. This appears to be a study guide, so explain the key concepts, important details, and help me understand the material for studying.`,
        'TXT': `Create an engaging podcast discussion about this text document. Cover the main topics and important points in a conversational way.`
      };
      
      userInput = defaultPrompts[uploadedFileType] || `Create an engaging podcast discussion about this ${uploadedFileType} document.`;
      
      console.log(`📝 Default instruction for ${uploadedFileType}: "${userInput}"`);
    }
  } else {
    console.log('📄 No file content in generation');
  }

  // ============================================
  // REST OF HANDLE GENERATE
  // ============================================

  // ✅ CHECK: Traffic module selected but no routes configured
  if (connectedModules.includes('traffic') && savedRoutes.length === 0) {
    alert('Please set up at least one commute route to use live traffic updates.');
    setShowRouteSetup(true);
    return;
  }

  // ✅ If traffic is selected and user has multiple routes, show route selector
  if (connectedModules.includes('traffic') && savedRoutes.length > 1 && !selectedRouteForPodcast) {
    setShowRouteSelector(true);
    return;
  }

  const selectedModules = [...connectedModules];

  console.log('📝 Final generation inputs:', {
    modules: selectedModules,
    userInputLength: userInput.length,
    fileContentLength: fileContentForGeneration?.length || 0,
    uploadedFileName: uploadedFile?.name || 'none'
  });
// ✅ Determine if guest should be included
// ✅ Determine if guest should be included
let includeGuest = false;

if (selectedGuest === 'none') {
  includeGuest = false;
  console.log('🚫 Guest disabled by user');
} else if (selectedGuest === null) {
  // Default: 20% chance of guest (not 50%)
  includeGuest = Math.random() > 0.8;
  console.log(`🎲 Random guest (20% chance): ${includeGuest ? 'YES' : 'NO'}`);
} else {
  includeGuest = true;
  console.log(`✅ Specific guest selected: ${selectedGuest}`);
}
  // ✅ CRITICAL FIX: Clean URL from promptText BEFORE generation
  if (youtubeVideoId && containsYouTubeUrl(promptText)) {
    const { cleanText } = extractAndRemoveVideoUrl(promptText);
    userInput = cleanText;
    console.log('🎥 Cleaned YouTube URL from input');
  }

  // ✅ If YouTube video is detected but analysis isn't complete yet, WAIT for it
  if (youtubeVideoId && !youtubeVideoAnalysis) {
    console.log('⏳ YouTube analysis in progress, waiting...');
    setGenerationStage('Analyzing YouTube video...');
    
    let attempts = 0;
    while (!youtubeVideoAnalysis && attempts < 60) {
      await new Promise(resolve => setTimeout(resolve, 500));
      attempts++;
    }
    
    if (!youtubeVideoAnalysis) {
      alert('YouTube video analysis timed out. Please try again.');
      return;
    }
    
    console.log('✅ YouTube analysis complete, proceeding with generation');
  }

  // ✅ If YouTube video is selected AND analysis is complete, enhance input
  if (youtubeVideoId && youtubeVideoAnalysis) {
    const videoContext = generatePodcastContext(youtubeVideoAnalysis);

    let quotesContext = '';
    if (youtubeVideoAnalysis.transcript && youtubeVideoAnalysis.durationSeconds) {
      try {
        const { extractKeyQuotes } = await import('./youtubeGeneration.js');
        const keyQuotes = extractKeyQuotes(
          youtubeVideoAnalysis.transcript, 
          youtubeVideoAnalysis.durationSeconds
        );
        
        if (keyQuotes.length > 0) {
          quotesContext = `\n\n🎯 KEY THEMES AND ARGUMENTS FROM THE VIDEO:\n`;
          quotesContext += `The video covers several major points. Discuss these naturally as if you watched it:\n\n`;
          
          keyQuotes.forEach((quote, idx) => {
            const context = quote.type === 'question' ? 'They raise this important question' : 
                            quote.type === 'debate' ? 'There\'s an interesting debate about' : 
                            'A key point they make is';
            
            quotesContext += `${idx + 1}. ${context}: "${quote.text}"\n`;
          });
        }
      } catch (error) {
        console.warn('⚠️ Could not extract quotes:', error);
      }
    }
    
    if (userInput.trim()) {
      userInput = `${videoContext}${quotesContext}\n\nUser's additional request: ${userInput}`;
    } else {
      userInput = `${videoContext}${quotesContext}`;
    }
    
    console.log('🎥 YouTube context added to podcast generation');
  }

  try {
    setIsGenerating(true);
    setGenerationStage('dialogue');
    setShowBriefingBuilder(true);
    
    console.log('🎙️ Starting podcast generation...');

    const AZURE_CONFIG = {
      ENDPOINT: AZURE_OPENAI_ENDPOINT,
      DEPLOYMENT: AZURE_DEPLOYMENT_NAME,
      KEY: AZURE_OPENAI_KEY
    };

    // ✅ Fetch weather data if weather module is selected
    let fetchedWeatherData = null;
    let detectedLocation = 'your area';
    
    if (selectedModules.includes('weather')) {
      console.log('🌤️ Weather module selected - fetching weather data...');
      try {
        const { handleWeatherRequest } = await import('./weatherHandler');
        const weatherResult = await handleWeatherRequest();
        
        if (weatherResult.success) {
          fetchedWeatherData = weatherResult;
          detectedLocation = weatherResult.location.state 
            ? `${weatherResult.location.city}, ${weatherResult.location.state}`
            : weatherResult.location.city;
          
          console.log('✅ Weather data fetched for:', detectedLocation);
        }
      } catch (weatherError) {
        console.error('❌ Error fetching weather:', weatherError);
      }
    }

    // ✅ Fetch news data if needed
    let newsDataToUse = newsData;

    if (selectedModules.includes('news')) {
      if (selectedNewsType === 'local') {
        console.log('📰 Local news selected - fetching location-based news...');
        setIsLoadingLocalNews(true);
        
        try {
          const localNewsModule = await import('./localNewsHandler');
          const { fetchLocalNews } = localNewsModule;
          const localNews = await fetchLocalNews(true);

          if (localNews && localNews.totalArticles > 0) {
            newsDataToUse = localNews;
            setLocalNewsData(localNews);
            console.log('✅ Local news loaded:', localNews.totalArticles, 'articles');
          } else {
            throw new Error('No local news articles found');
          }
        } catch (error) {
          console.error('❌ Local news fetch failed:', error);
          alert('Could not load local news. Using general news instead.');
          newsDataToUse = newsData;
        } finally {
          setIsLoadingLocalNews(false);
        }
      } else {
        console.log('📰 Using general news data');
        newsDataToUse = newsData;
      }
    }

// ✅ Load schedule data if schedule module is selected
let scheduleDataToPass = null;
if (selectedModules.includes('schedule')) {
  console.log('📅 Schedule module selected - loading calendar data...');
  try {
    const { loadCalendarFromStorage } = await import('./schedulerHandlers');
    scheduleDataToPass = await loadCalendarFromStorage(firestoreService);
    
    if (scheduleDataToPass && Object.keys(scheduleDataToPass).length > 0) {
      console.log('✅ Schedule data loaded:', Object.keys(scheduleDataToPass).length, 'dates');
      console.log('   Sample dates:', Object.keys(scheduleDataToPass).slice(0, 5));
      
      // ✅ Log sample events for debugging
      const firstDateKey = Object.keys(scheduleDataToPass)[0];
      if (firstDateKey) {
        console.log('   Sample events:', scheduleDataToPass[firstDateKey]);
      }
    } else {
      console.warn('⚠️ Schedule data is empty - user has no events');
    }
  } catch (scheduleError) {
    console.error('❌ Error loading schedule:', scheduleError);
    scheduleDataToPass = null;
  }
}

    // ✅ Fetch traffic data using selected route
    let fetchedTrafficData = null;
    if (selectedModules.includes('traffic')) {
      console.log('🚗 Traffic module selected - fetching traffic data...');
      try {
        const { getTrafficData } = await import('./trafficHandler');
        
        const routeToUse = selectedRouteForPodcast || savedRoutes[0];
        
        console.log('📍 Using route for traffic:', routeToUse.name);
        const trafficResult = await getTrafficData(routeToUse.name);
        
        if (trafficResult) {
          fetchedTrafficData = trafficResult;
          console.log('✅ Traffic data fetched');
        }
        
        setSelectedRouteForPodcast(null);
        
      } catch (trafficError) {
        console.error('❌ Error fetching traffic:', trafficError);
      }
    }

    // ✅ Final validation before generation
    const hasContentToGenerate = 
      userInput.trim().length > 0 || 
      selectedModules.length > 0;
    
    if (!hasContentToGenerate) {
      throw new Error('No content available for podcast generation.');
    }

    console.log('✅ All data prepared, calling generateCustomInnerLoopPodcast...');
    console.log('🚀 Generation parameters:', {
      modules: selectedModules,
      userInputLength: userInput.length,
      fileContentLength: fileContentForGeneration?.length || 0,
      fileType: uploadedFileType,
      fileName: uploadedFile?.name
    });

    // ✅ CRITICAL: Generate dialogue with file content
    // The fileContent parameter is passed separately from userInput
    const result = await generateCustomInnerLoopPodcast(
      selectedModules,              // connectedModules
      userInput,                    // promptText (with default or user input)
      fetchedWeatherData,           // weatherData
      selectedNewsType === 'local' ? newsDataToUse : newsData, // newsData
      scheduleDataToPass,           // scheduleData
      stockData,                    // stockData
      userName,                     // userName
      detectedLocation,             // userLocation
      {
        ENDPOINT: AZURE_OPENAI_ENDPOINT,
        DEPLOYMENT: AZURE_DEPLOYMENT_NAME,
        KEY: AZURE_OPENAI_KEY
      },                            // AZURE_CONFIG
      fetchedTrafficData,           // trafficData
      fileContentForGeneration,     // ✅ CRITICAL: Pass actual file content here
      selectedHosts,                // selectedHosts
      talkingStyle,                  // talkingStyle
      includeGuest
    );

    console.log('✅ Dialogue generated:', result.dialogue.length, 'lines');

    // ✅ Generate audio
    if (combineAudioSegments) {
      setGenerationStage('audio');
      
      const audioUrl = await combineAudioSegments(
        result.dialogue,
        (progress) => {
          console.log('🎵 Audio progress:', progress);
          setGenerationStage('audio');
        },
        introMusicEnabled
      );

      console.log('✅ Audio generated');

      const getTimeOfDay = () => {
        const hour = new Date().getHours();
        if (hour >= 5 && hour < 12) return 'Morning';
        if (hour >= 12 && hour < 17) return 'Afternoon';
        if (hour >= 17 && hour < 21) return 'Evening';
        return 'Night';
      };

      const timeOfDay = getTimeOfDay();
      
      // ✅ Generate dynamic title
// ✅ Generate dynamic title with AI summarization
      let dynamicTitle;
      
      if (youtubeVideoTitle && youtubeVideoTitle !== 'Analyzing...' && youtubeVideoTitle !== 'YouTube Video') {
        try {
          const shortTitle = await generateShortPodcastTitle(
            youtubeVideoTitle,
            youtubeVideoAnalysis.topics,
            AZURE_CONFIG
          );
          dynamicTitle = shortTitle;
        } catch (error) {
          console.error('❌ Failed to generate short title');
          dynamicTitle = youtubeVideoTitle.substring(0, 40).trim();
        }
      } else if (uploadedFileType && uploadedFile) {
        // ✅ AI-summarized title for file-based podcasts
        const fileName = uploadedFile.name.replace(/\.[^/.]+$/, "");
        const longTitle = `Study guide discussion about ${fileName}`;
        dynamicTitle = await summarizeTitleWithAI(longTitle);
        console.log('📄 File podcast title:', dynamicTitle);
      } else if (userInput.trim()) {
        // ✅ AI-summarized title for custom prompts
        dynamicTitle = await summarizeTitleWithAI(userInput);
        console.log('💬 Custom podcast title:', dynamicTitle);
      } else {
        dynamicTitle = `${timeOfDay} Briefing`;
      }

      const estimatedDuration = result.metadata?.estimatedDuration || 180;
      
      const extractedChapters = result.dialogue
        .map((line, idx) => {
          const hasSection = line.section;
          const hasTransition = line.line && (
            line.line.includes('Moving on to') || 
            line.line.includes('Now let\'s talk about') ||
            line.line.includes('Let\'s discuss') ||
            line.line.includes('Next up') ||
            line.line.includes('Turning to')
          );
          
          if (hasSection || hasTransition) {
            const title = line.section || extractChapterTitle(line.line);
            const timestamp = calculateTimestamp(idx, result.dialogue, estimatedDuration);
            const duration = calculateChapterDuration(idx, result.dialogue, estimatedDuration);
            
            return { title, timestamp, index: idx, duration };
          }
          return null;
        })
        .filter(Boolean);

      console.log(`✅ Extracted ${extractedChapters.length} chapters`);
      setChapters(extractedChapters);

      const podcastData = {
        audioUrl,
        dialogue: result.dialogue,
        title: dynamicTitle,
        duration: estimatedDuration,
        sections: result.sections,
        type: 'manual-podcast', 
        chapters: extractedChapters,
        generatedAt: new Date().toISOString(),
        savedAt: new Date().toISOString(),
        youtubeVideo: youtubeVideoAnalysis ? {
          id: youtubeVideoId,
          title: youtubeVideoTitle,
          channel: youtubeVideoAnalysis.channel,
          topics: youtubeVideoAnalysis.topics
        } : null,
        uploadedFile: uploadedFile ? {
          name: uploadedFile.name,
          type: uploadedFileType,
          size: uploadedFile.size
        } : null
      };

      setCurrentPodcast(podcastData);
      setGenerationStage('ready');

      // ✅ Clear file data and YouTube data after successful generation
      setTimeout(() => {
        console.log('🧹 Clearing data after successful generation');
        setUploadedFile(null);
        setUploadedFileContent(null);
        setUploadedFileType(null);
        setConnectedModules([]);
        
        setYoutubeVideoId(null);
        setYoutubeVideoTitle(null);
        setYoutubeVideoAnalysis(null);
        setModules(prev => prev.filter(m => m.id !== 'youtube'));
      }, 500);
      
      setTimeout(() => {
        setIsGenerating(false);
        setShowBriefingBuilder(false);
        
        if (audioRef.current) {
          const audio = audioRef.current;
          audio.src = audioUrl;
          
          const handleCanPlay = () => {
            console.log('✅ Audio ready to play');
            
            if (audio.duration && !isNaN(audio.duration) && isFinite(audio.duration)) {
              setAudioDuration(audio.duration);
              
              const recalculatedChapters = extractedChapters.map(chapter => ({
                ...chapter,
                timestamp: calculateTimestamp(chapter.index, result.dialogue, audio.duration),
                duration: calculateChapterDuration(chapter.index, result.dialogue, audio.duration)
              }));
              
              setChapters(recalculatedChapters);
            }
            
            setIsPodcastPlaying(true);
            setShowTranscriptView(true);
            
            audio.play()
              .catch(err => {
                console.error('❌ Autoplay failed:', err);
                setIsPodcastPlaying(false);
              });
            
            audio.removeEventListener('canplay', handleCanPlay);
            audio.removeEventListener('error', handleError);
          };
          
          const handleMetadata = () => {
            console.log('🎵 Metadata loaded');
            if (audio.duration && !isNaN(audio.duration) && isFinite(audio.duration)) {
              setAudioDuration(audio.duration);
            }
          };
          
          const handleError = (e) => {
            console.error('❌ Audio error:', e);
            alert('Failed to load podcast audio.');
          };
          
          audio.addEventListener('loadedmetadata', handleMetadata);
          audio.addEventListener('canplay', handleCanPlay);
          audio.addEventListener('error', handleError);
          audio.load();
        }
      }, 500);
    } else {
      alert('Audio generation not available');
      setIsGenerating(false);
      setShowBriefingBuilder(false);
    }
    
  } catch (error) {
    console.error('❌ Generation error:', error);
    alert(`Failed: ${error.message}`);
    setIsGenerating(false);
    setGenerationStage('');
    setShowBriefingBuilder(false);
    
    // Clear YouTube and file data on error
    setYoutubeVideoId(null);
    setYoutubeVideoTitle(null);
    setYoutubeVideoAnalysis(null);
    setModules(prev => prev.filter(m => m.id !== 'youtube'));
    setSelectedRouteForPodcast(null);
  }
};
  const renderDragLine = () => {
    if (!draggedModule || !touchPosition || !svgRef.current) return null;
    
    const moduleElement = moduleRefs.current[draggedModule];
    const buttonElement = generateButtonRef.current;
    if (!moduleElement || !buttonElement) return null;

    try {
      const svgRect = svgRef.current.getBoundingClientRect();
      const moduleRect = moduleElement.getBoundingClientRect();
      
      const startX = moduleRect.right + 5 - svgRect.left;
      const startY = moduleRect.top + moduleRect.height / 2 - svgRect.top;
      const endX = touchPosition.x - svgRect.left;
      const endY = touchPosition.y - svgRect.top;
      
      const module = modules.find(m => m.id === draggedModule);
      if (!module) return null;
      
      const snapProgress = Math.max(0, 1 - dragDistance / 100);
      
      const midX = (startX + endX) / 2;
      const midY = (startY + endY) / 2;
      const distance = Math.sqrt(Math.pow(endX - startX, 2) + Math.pow(endY - startY, 2));
      const sagAmount = Math.min(40, distance * 0.2);
      
      return (
        <g>
          <path
            d={`M ${startX} ${startY} Q ${midX} ${midY + sagAmount}, ${endX} ${endY}`}
            stroke={module.color}
            strokeWidth={3 + snapProgress * 2}
            fill="none"
            strokeLinecap="round"
            opacity={0.8}
            style={{ 
              filter: snapProgress > 0.7 ? `drop-shadow(0 0 ${snapProgress * 10}px ${module.color})` : 'none'
            }}
          />
          
          <circle 
            cx={endX} 
            cy={endY} 
            r={6 + snapProgress * 4} 
            fill={module.color} 
            opacity={0.95}
            style={{
              filter: `drop-shadow(0 2px 6px ${module.color}60)`
            }}
          />
          
          <circle 
            cx={endX} 
            cy={endY - 1.5} 
            r={2.5 + snapProgress * 2} 
            fill="white" 
            opacity={0.7}
          />
        </g>
      );
    } catch (error) {
      console.error('Error rendering drag line:', error);
      return null;
    }
  };

  const shouldShowOnlyIcons = connectedModules.length >= 4;

return (
    <>
{showOnboarding && (
  <OnboardingModal
   isOpen={showOnboarding}
    onComplete={handleNewsOnboardingComplete}
    firestoreService={firestoreService}
  />
)}
<div style={{ 
  minHeight: '100vh', 
  overflowY: 'auto', 
  overflowX: 'hidden', 
  background: 'linear-gradient(170deg, #ecececf1 0%, #fefefedc 60%, #e0e7fcb2 100%)', 
  padding: '16px', 
  paddingTop: '20px', 
  paddingBottom: isPodcastPlaying ? '100px' : '20px', 
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', 
  position: 'relative', 
  width: '100%',
  boxSizing: 'border-box'
}}>    <input
      ref={fileInputRef}
      type="file"
      accept=".txt,.pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
      onChange={handleFileUpload}
      style={{ display: 'none' }}
    />
<div style={{ maxWidth: '860px', margin: '0 auto', width: '100%' }}>
         <div style={{ marginBottom: '5px' }}>
     </div>
<GreetingCard 
  userName={userName} 
  currentTime={currentTime} 
/>

{/* 
  Daily Brief Card
  Props:
  - firestoreService: Service for database operations
  - userTimezone: User's timezone for time calculations
  - onPlayBrief: Callback when user plays the daily brief
  - savedRoutes: Array of saved routes (NOT ACTIVE YET)
  - setSavedRoutes: Function to update saved routes (NOT ACTIVE YET)
  - onOpenRouteSetup: Callback to open route setup modal (NOT ACTIVE YET)
  
  <DailyBriefCatchUp 
    firestoreService={firestoreService}
    userTimezone={userTimezone}
    onPlayBrief={handlePlayDailyBrief}
    savedRoutes={savedRoutes}
    setSavedRoutes={setSavedRoutes}
    onOpenRouteSetup={() => setShowRouteSetupFromDailyBrief(true)}
  />
*/}
{/* Shimmer Animation */}
<style>{`
  @keyframes shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
`}</style>
        <div style={{ 
          backdropFilter: 'blur(20px)', 
          borderRadius: '20px', 
          padding: '2px', 
          marginBottom: '15px',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
        }}>
          <div style={{           background: 'linear-gradient(250deg, rgba(240, 250, 255, 1) 20%, rgba(255, 230, 240, 1) 30%, rgba(230, 240, 255, 0.6) 50%, rgba(255, 255, 255, 1) 60%, rgba(255, 255, 255, 1) 100%)',
 borderRadius: '20px', border: '1px solid rgba(230, 230, 230, 0.93)', position: 'relative', overflow: 'visible' }}>
            
      {!showBriefingBuilder && (
        <div
          style={{
            padding: "20px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "12px",
          }}
        >
          {/* Create Podcast Button */}
          <button
onClick={() => setShowCreatePodcastModal(true)}
            style={{
              background: "#080808ff",
              border: "none",
              color: "white",
              padding: "10px 16px",
              borderRadius: "24px",
              cursor: "pointer",
              fontSize: "11px",
              fontWeight: "500",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              transition: "all 0.2s",
            }}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 15 15"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8 2.75C8 2.47386 7.77614 2.25 7.5 2.25C7.22386 2.25 7 2.47386 7 2.75V7H2.75C2.47386 7 2.25 7.22386 2.25 7.5C2.25 7.77614 2.47386 8 2.75 8H7V12.25C7 12.5261 7.22386 12.75 7.5 12.75C7.77614 12.75 8 12.5261 8 12.25V8H12.25C12.5261 8 12.75 7.77614 12.75 7.5C12.75 7.22386 12.5261 7 12.25 7H8V2.75Z"
                fill="currentColor"
              />
            </svg>
            Create Podcast
          </button>

          {/* Settings Button */}
          <button
            onClick={() => setShowSettings(true)}
            style={{
              background: "#ffffffff",
              border: "none",
              color: "#080c11f3",
              padding: "10px 16px",
              borderRadius: "20px",
              fontSize: "11px",
              fontWeight: "500",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "6px",
            }}
          >
<svg class="w-6 h-6 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="none" viewBox="0 0 24 24">
  <path stroke="#080c11f3" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M21 13v-2a1 1 0 0 0-1-1h-.757l-.707-1.707.535-.536a1 1 0 0 0 0-1.414l-1.414-1.414a1 1 0 0 0-1.414 0l-.536.535L14 4.757V4a1 1 0 0 0-1-1h-2a1 1 0 0 0-1 1v.757l-1.707.707-.536-.535a1 1 0 0 0-1.414 0L4.929 6.343a1 1 0 0 0 0 1.414l.536.536L4.757 10H4a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h.757l.707 1.707-.535.536a1 1 0 0 0 0 1.414l1.414 1.414a1 1 0 0 0 1.414 0l.536-.535 1.707.707V20a1 1 0 0 0 1 1h2a1 1 0 0 0 1-1v-.757l1.707-.708.536.536a1 1 0 0 0 1.414 0l1.414-1.414a1 1 0 0 0 0-1.414l-.535-.536.707-1.707H20a1 1 0 0 0 1-1Z"/>
  <path stroke="#080c11f3" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z"/>
</svg>
            <span>Settings</span>
          </button>
        </div>
      )}

{showBriefingBuilder && !isGenerating && (
  <>
    <div style={{ 
      border: '1px solid #d1d5db',
      borderRadius: '24px',
      margin: '12px',
      background: 'white',
      animation: 'fadeIn 0.3s ease-out',
      position: 'relative',
      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
    }}>
<svg 
  ref={svgRef} 
  width="100%" 
  height="200" 
  style={{ 
    position: 'absolute', 
    top: 0, 
    left: 0, 
    pointerEvents: 'none', 
    zIndex: 5
  }}
>
  <defs>
    <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
      <stop offset="0%" style={{ stopColor: '#9ca3af', stopOpacity: 0.6 }} />
      <stop offset="100%" style={{ stopColor: '#6b7280', stopOpacity: 1 }} />
    </linearGradient>
  </defs>
  
  {connectedModules.length > 0 && connectedModules.map((modId) => {
    const moduleElement = moduleRefs.current[modId];
    const buttonElement = generateButtonRef.current;
    
    // ✅ INSTANT CHECK - No delay
    if (!moduleElement || !buttonElement || !svgRef.current) {
      // Force re-render on next frame if refs aren't ready yet
      setTimeout(() => {
        if (moduleElement && buttonElement && svgRef.current) {
          // Trigger re-render by updating a dummy state if needed
        }
      }, 0);
      return null;
    }

    const svgRect = svgRef.current.getBoundingClientRect();
    const moduleRect = moduleElement.getBoundingClientRect();
    const buttonRect = buttonElement.getBoundingClientRect();
    
    const isLastModule = modId === connectedModules[connectedModules.length - 1];
    if (!isLastModule) return null;
    
    const startX = moduleRect.right - svgRect.left;
    const startY = moduleRect.top + moduleRect.height / 2 - svgRect.top;
    const endX = buttonRect.left + buttonRect.width / 2 - svgRect.left;
    const endY = buttonRect.bottom - svgRect.top;
    
    const midX = (startX + endX) / 2;
    const midY = (startY + endY) / 2;
    
    return (
      <g key={`${modId}-gen`}>
        <path
          d={`M ${startX} ${startY} Q ${midX} ${midY + 15}, ${endX} ${endY}`}
          stroke="#9ca3af" 
          strokeWidth="2"
          fill="none"
          style={{
            transition: 'none' // ✅ Remove transition delay
          }}
        />
        <circle cx={endX} cy={endY} r="3" fill="#272727" />
      </g>
    );
  })}
  {renderDragLine()}
</svg>
      <div style={{ padding: '12px 16px', position: 'relative' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ position: 'relative' }}>
<button
  ref={dropdownRef}
  onClick={() => {
    setShowModuleDropdown(!showModuleDropdown);
    setAnimatePlus(true);
    setTimeout(() => setAnimatePlus(false), 400);
  }}
  style={{
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    background: 'white',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
    flexShrink: 0,
    position: 'relative'
  }}
>
<svg 
  aria-hidden="true" 
  xmlns="http://www.w3.org/2000/svg" 
  width="20" 
  height="20" 
  fill="none" 
  viewBox="0 0 24 24"
  style={{
    animation: animatePlus ? 'spinPlus 0.25s ease-in-out' : 'none'
  }}
>
  <path stroke="#383838ff" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.4" d="M5 12h14m-7 7V5"/>
</svg>
            </button>
<style>{`
   ======================================== */
     * {
          box-sizing: border-box;
        }
@keyframes fadeIn {
  from { 
    opacity: 0; 
  }
  to { 
    opacity: 1; 
  }
}

@keyframes slideUp {
  from { 
    opacity: 0;
    transform: translateY(100%); 
  }
  to { 
    opacity: 1;
    transform: translateY(0); 
  }
}

@keyframes slideUpSlow {
  from { 
    opacity: 0;
    transform: translateY(60px); 
  }
  to { 
    opacity: 1;
    transform: translateY(0); 
  }
}

@keyframes scaleIn {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}
  @keyframes dash {
    to { stroke-dashoffset: -1000; }
  }
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes slideUp {
    from { 
      opacity: 0;
      transform: translateY(20px);
    }
    to { 
      opacity: 1;
      transform: translateY(0);
    }
  }
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  
  /* ✅ NEW - Plus icon spin */
  @keyframes spinPlus {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(180deg); }
  }
  
  @keyframes progress {
    0% { transform: translateX(-100%); }
    50% { transform: translateX(0%); }
    100% { transform: translateX(100%); }
  }
  @keyframes pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.5; transform: scale(1.2); }
  }
  
  .custom-scroll::-webkit-scrollbar {
    display: none;
  }
  @keyframes slideDown {
    from { 
      opacity: 0;
      transform: translateY(-10px);
      maxHeight: 0;
    }
    to { 
      opacity: 1;
      transform: translateY(0);
      maxHeight: 500px;
    }
  }
  
  /* Gradient shift and bounce animations */
  @keyframes bounceRewind {
    0% { transform: scaleX(-1) rotate(0deg); }
    50% { transform: scaleX(-1) rotate(30deg); }
    100% { transform: scaleX(-1) rotate(0deg); }
  }
  @keyframes bounceFastForward {
    0% { transform: rotate(0deg); }
    50% { transform: rotate(30deg); }
    100% { transform: rotate(0deg); }
  }
  @keyframes gradientShift {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
`}</style>
{showModuleDropdown && (
  <div style={{
    position: 'absolute',
    bottom: '48px',
    left: '0',
    background: 'white',
    borderRadius: '10px',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
    border: '1px solid #e5e7eb',
    padding: '6px',
    minWidth: '260px', // ✅ Width for 2 columns
    zIndex: 100,
    animation: 'fadeIn 0.2s ease-out'
  }}>
    {/* ✅ First 4 modules in a 2x2 grid */}
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)', // 2 columns
      gap: '4px',
      marginBottom: '4px'
    }}>
      {modules.slice(0, 4).map(module => {
        const Icon = module.icon;
        const isSelected = connectedModules.includes(module.id);
        return (
          <button
            key={module.id}
            onClick={() => toggleModule(module.id)}
            style={{
              padding: '8px 10px',
              background: isSelected ? '#f3f4f6' : 'transparent',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
            onMouseLeave={(e) => e.currentTarget.style.background = isSelected ? '#f3f4f6' : 'transparent'}
          >
            <div style={{
              width: '24px',
              height: '24px',
              background: isSelected ? 'white' : '#f9fafb',
              borderRadius: '5px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: `1px solid ${isSelected ? '#d1d5db' : '#e5e7eb'}`
            }}>
              <Icon size={13} color={module.color} />
            </div>
            <span style={{
              fontSize: '12px',
              fontWeight: '500',
              color: '#1f2937'
            }}>
              {module.name}
            </span>
          </button>
        );
      })}
    </div>
    
    {/* ✅ Divider */}
    <div style={{
      height: '1px',
      background: '#e5e7eb8e',
      margin: '4px 0'
    }} />
    
    {/* ✅ Last 2 modules (Traffic + Upload) in a single row */}
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)', // 2 columns
      gap: '4px'
    }}>
      {modules.slice(4, 6).map(module => {
        const Icon = module.icon;
        const isSelected = connectedModules.includes(module.id);
        return (
          <button
            key={module.id}
            onClick={() => toggleModule(module.id)}
            style={{
              padding: '8px 10px',
              background: isSelected ? '#f3f4f6' : 'transparent',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
            onMouseLeave={(e) => e.currentTarget.style.background = isSelected ? '#f3f4f6' : 'transparent'}
          >
            <div style={{
              width: '24px',
              height: '24px',
              background: isSelected ? 'white' : '#f9fafb',
              borderRadius: '5px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: `1px solid ${isSelected ? '#d1d5db' : '#e5e7eb'}`
            }}>
              <Icon size={13} color={module.color} />
            </div>
            <span style={{
              fontSize: '12px',
              fontWeight: '500',
              color: '#1f2937'
            }}>
              {module.name}
            </span>
          </button>
        );
      })}
    </div>
  </div>
)}
          </div>

          <input
            type="text"
            value={promptText}
            onChange={(e) => setPromptText(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleGenerate()}
            placeholder="Create podcasts on any topic..."
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              fontSize: '14px',
              color: '#1f2937',
              background: 'transparent',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
            }}
          />

          <button
            ref={generateButtonRef}
            onClick={handleGenerate}
            disabled={connectedModules.length === 0 && !promptText.trim()}
            style={{
              width: '26px',
              height: '26px',
              borderRadius: '50%',
              background: (connectedModules.length === 0 && !promptText.trim()) ? '#9ca3af' : '#272727',
              border: 'none',
              cursor: (connectedModules.length === 0 && !promptText.trim()) ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s',
              flexShrink: 0,
              position: 'relative'
            }}
          >
            <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24">
              <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m9 5 7 7-7 7"/>
            </svg>
          </button>
        </div>
      </div>

{connectedModules.length > 0 && (
  <div style={{ 
    padding: '0px 16px 12px', 
    display: 'flex', 
    justifyContent: 'flex-start', 
    alignItems: 'center',
    gap: '0px',
    animation: 'fadeIn 0.3s ease-out'
  }}>
    {connectedModules.map((modId, index) => {
      const module = modules.find(m => m.id === modId);
      if (!module) return null;
      const Icon = module.icon;
      
      const showText = !shouldShowOnlyIcons && index === 0;
      const isUpload = modId === 'upload';
      const isYouTube = modId === 'youtube';

      const getFileIcon = () => {
        if (!isUpload || !uploadedFileType) return null;
        
        if (uploadedFileType === 'PDF') {
          return (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ef4444">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6" />
            </svg>
          );
        } else if (uploadedFileType === 'DOCX') {
          return (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2563eb">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9h1m-1 4h6m-6 4h6m2-10V7" />
            </svg>
          );
        } else {
          return (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b7280">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          );
        }
      };
      
      return (
        <React.Fragment key={modId}>
          <div
            ref={el => moduleRefs.current[modId] = el}
            style={{
              background: 'white',
              border: `1.5px solid ${isYouTube ? '#FF0000' : '#e5e7eb'}`,
              borderRadius: '22px',
              padding: showText && !isYouTube ? '6px 14px' : '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              cursor: 'pointer',
              position: 'relative',
              touchAction: 'none',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
            }}
          >
            {/* ✅ NEW: X button for YouTube module */}
            {isYouTube && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  // Remove YouTube module
                  setYoutubeVideoId(null);
                  setYoutubeVideoTitle(null);
                  setYoutubeVideoAnalysis(null);
                  setConnectedModules(prev => prev.filter(id => id !== 'youtube'));
                  setModules(prev => prev.filter(m => m.id !== 'youtube'));
                  console.log('🗑️ YouTube module removed by user');
                }}
                style={{
                  position: 'absolute',
                  top: '-6px',
                  right: '-6px',
                  width: '16px',
                  height: '16px',
                  borderRadius: '50%',
                  background: '#FF0000',
                  border: '1.5px solid white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 0,
                  transition: 'all 0.2s',
                  zIndex: 10
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.1)';
                  e.currentTarget.style.background = '#dc2626';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.background = '#FF0000';
                }}
              >
                <X size={10} color="white" strokeWidth={3} />
              </button>
            )}
            
            {/* Icon */}
            <div style={{
              width: '14px',
              height: '14px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {isUpload && uploadedFileType ? (
                getFileIcon()
              ) : isYouTube ? (
                <YouTubeIcon />
              ) : (
                <Icon size={14} color={module.color} />
              )}
            </div>
            
            {/* Text - ONLY show for non-YouTube first module OR file upload */}
            {showText && !isYouTube && (
              <span style={{ 
                fontSize: '13px', 
                fontWeight: '500', 
                color: '#374151'
              }}>
                {isUpload && uploadedFileType ? uploadedFileType : module.name}
              </span>
            )}
          </div>
          
          {index < connectedModules.length - 1 && (
            <div style={{
              width: '24px',
              height: '2px',
              background: '#d1d5db',
              margin: '0 -1px'
            }} />
          )}
        </React.Fragment>
      );
    })}
  </div>
)}
    </div>
  </>
)}

{showBriefingBuilder && isGenerating && (
  <div style={{
    border: '1px solid #d1d5db',
    borderRadius: '24px',
    margin: '12px',
    background: 'white',
    padding: '40px 20px',
    textAlign: 'center',
    animation: 'fadeIn 0.3s ease-out'
  }}>
    {/* Spinner - NO ENLARGING */}
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <g>
        <rect x="11" y="1" width="2" height="5" rx="1" fill="#272727" opacity="1"/>
        <rect x="11" y="1" width="2" height="5" rx="1" fill="#272727" opacity="0.875" transform="rotate(45 12 12)"/>
        <rect x="11" y="1" width="2" height="5" rx="1" fill="#272727" opacity="0.75" transform="rotate(90 12 12)"/>
        <rect x="11" y="1" width="2" height="5" rx="1" fill="#272727" opacity="0.625" transform="rotate(135 12 12)"/>
        <rect x="11" y="1" width="2" height="5" rx="1" fill="#272727" opacity="0.5" transform="rotate(180 12 12)"/>
        <rect x="11" y="1" width="2" height="5" rx="1" fill="#272727" opacity="0.375" transform="rotate(225 12 12)"/>
        <rect x="11" y="1" width="2" height="5" rx="1" fill="#272727" opacity="0.25" transform="rotate(270 12 12)"/>
        <rect x="11" y="1" width="2" height="5" rx="1" fill="#272727" opacity="0.125" transform="rotate(315 12 12)"/>
        <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite"/>
      </g>
    </svg>

    {/* Status Text - NO ANIMATION */}
    <div style={{
      fontSize: '12px',
      fontWeight: '600',
      color: '#1f2937',
      marginTop: '16px',
      marginBottom: '8px'
    }}>
      {generationStage === 'dialogue' && 'Creating your podcast...'}
      {generationStage === 'audio' && 'Generating podcast...'}
      {generationStage === 'ready' && 'Almost ready!'}
    </div>

    <div style={{
      fontSize: '12px',
      color: '#6b7280'
    }}>
      {generationStage === 'dialogue' && 'This will take few secs'}
      {generationStage === 'audio' && 'Almost there...'}
      {generationStage === 'ready' && 'Preparing playback'}
    </div>

    {/* Progress Dots - NO PULSE ANIMATION */}
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      gap: '8px',
      marginTop: '20px'
    }}>
      <div style={{
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        background: generationStage === 'dialogue' ? '#272727' : '#e5e7eb',
        transition: 'background 0.2s ease'
      }} />
      <div style={{
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        background: generationStage === 'audio' ? '#272727' : '#e5e7eb',
        transition: 'background 0.2s ease'
      }} />
      <div style={{
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        background: generationStage === 'ready' ? '#272727' : '#e5e7eb',
        transition: 'background 0.2s ease'
      }} />
    </div>
  </div>
)}
<style>{`
  @keyframes dash {
    to { stroke-dashoffset: -1000; }
  }
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes slideUp {
    from { 
      opacity: 0;
      transform: translateY(20px);
    }
    to { 
      opacity: 1;
      transform: translateY(0);
    }
  }
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  @keyframes progress {
    0% { transform: translateX(-100%); }
    50% { transform: translateX(0%); }
    100% { transform: translateX(100%); }
  }
  @keyframes pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.5; transform: scale(1.2); }
  }
  
  .custom-scroll::-webkit-scrollbar {
    display: none;
  }
  @keyframes slideDown {
    from { 
      opacity: 0;
      transform: translateY(-10px);
      maxHeight: 0;
    }
    to { 
      opacity: 1;
      transform: translateY(0);
      maxHeight: 500px;
    }
  }
`}</style>
<style>{`
@keyframes flowLine {
  from {
    strokeDashoffset: 0;
  }
  to {
    strokeDashoffset: 20;
  }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
  @keyframes dash {
    to { stroke-dashoffset: -1000; }
  }
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes slideUp {
    from { 
      opacity: 0;
      transform: translateY(20px);
    }
    to { 
      opacity: 1;
      transform: translateY(0);
    }
  }
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  @keyframes progress {
    0% { transform: translateX(-100%); }
    50% { transform: translateX(0%); }
    100% { transform: translateX(100%); }
  }
  @keyframes pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.5; transform: scale(1.2); }
  }
  
  .custom-scroll::-webkit-scrollbar {
    display: none;
  }
  @keyframes slideDown {
    from { 
      opacity: 0;
      transform: translateY(-10px);
      maxHeight: 0;
    }
    to { 
      opacity: 1;
      transform: translateY(0);
      maxHeight: 500px;
    }
  }
`}</style>

          </div>
        </div>


{/* Tab Header - Latest Updates / For You / Saved */}
<div style={{ width: '100%', display: 'flex', justifyContent: 'center', marginBottom: '24px' }}>
  <div style={{
    display: 'flex',
    background: '#edededff',
    borderRadius: '24px',
    border: '1.5px solid #ffffff9d',
    padding: '4px',
    width: '100%',
  }}>
    {[
      { id: 'latest', label: 'Latest Updates' },
      { id: 'foryou', label: 'For You' },
      { id: 'saved', label: 'Saved', badge: showBadge && (savedArticles.length + savedPodcasts.length) > 0 ? savedArticles.length + savedPodcasts.length : null },
    ].map(({ id, label, badge }) => (
      <button
        key={id}
        onClick={() => setActiveTab(id)}
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '6px',
          padding: '8px 12px',
          borderRadius: '20px',
          border: 'none',
          cursor: 'pointer',
          fontSize: '12px',
          fontWeight: '500',
          transition: 'background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease',
          background: activeTab === id ? 'white' : 'transparent',
          color: activeTab === id ? '#111' : '#6b7280',
          boxShadow: activeTab === id ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
        {badge && (
          <span style={{
            background: '#fef3c7',
            color: '#92400e',
            fontSize: '11px',
            fontWeight: '600',
            padding: '2px 7px',
            borderRadius: '10px',
            minWidth: '20px',
            textAlign: 'center',
            opacity: badgeOpacity,
            transition: 'opacity 0.6s ease-out',
          }}>
            {badge}
          </span>
        )}
      </button>
    ))}
  </div>
</div>

{/* Content - Latest Updates Tab */}
{activeTab === 'latest' && (
  <>
    {/* News Card - Top Stories */}
    {showNewsCard && (
      <>
        <h2 style={{ 
          fontSize: '12.5px', 
          fontWeight: '600', 
          color: '#1f2937', 
          margin: '0 0 16px 0', 
          display: 'flex', 
          alignItems: 'center', 
          gap: '8px',
          marginTop: '10px',
          marginBottom: '18px'
        }}>
          <TrendingUp size={16} color="#ef4444" />
          Top Stories
        </h2>
        
        <div style={{ marginBottom: '20px' }}>
{showNewsCard && (
  (() => {
   // ✅ CRITICAL: Check if we have data
    const hasValidData = newsData && Object.keys(newsData).length > 0;
    
    // ✅ FIXED: Only show shimmer if:
    // 1. We're actively refreshing AND
    // 2. We don't have any data yet
    const shouldShowShimmer = isRefreshing && !hasValidData;
    
    // ✅ use personalized data if available
    const displayData = Object.keys(personalizedNewsData).length > 0 
      ? personalizedNewsData 
      : newsData;
    
    if (hasValidData) {
      return (
        <SwipeableNewsCards
          newsData={displayData} // ✅ CHANGE from newsData to displayData
          selectedNewsFeeds={selectedNewsFeeds}
          isRefreshing={isRefreshing}
          onSaveArticle={saveArticle}
           savedArticles={savedArticles} // ✅ ADD THIS
          onRemoveArticle={removeSavedArticle}
          isArticleSaved={isArticleSaved}
          onViewSource={(data) => {
            setSelectedNewsSource(data);
            setInAppBrowserUrl(null);
          }}
onTuneIn={(data) => {
  // ✅ Close any playing podcast
  if (audioRef.current) {
    audioRef.current.pause();
    setIsPodcastPlaying(false);
  }
  setCurrentPodcast(null);
  setShowTranscriptView(false);
  
  setSelectedNewsStationData({
    selectedSources: selectedNewsFeeds,
    newsData: displayData
  });
  setShowSelectedNewsStation(true);
}}
          onRefreshNews={handleRefreshNews}
          firestoreService={firestoreService} // ✅ ADD THIS
          analyzeArticleWithAI={analyzeArticleWithAI} // ✅ ADD THIS
        />
      );
    }
    // ✅ Only show shimmer if we're loading AND have NO data
    if (shouldShowShimmer) {
      console.log('⏳ Rendering shimmer skeleton (loading, no data yet)');
      return (
        <SwipeableNewsCards
          newsData={{}}
          selectedNewsFeeds={selectedNewsFeeds}
          isRefreshing={true}
          onSaveArticle={saveArticle}
          onRemoveArticle={removeSavedArticle}
          isArticleSaved={isArticleSaved}
          onViewSource={(data) => {
            setSelectedNewsSource(data);
            setInAppBrowserUrl(null);
          }}
          onTuneIn={(data) => {
            setSelectedNewsStationData({
              selectedSources: selectedNewsFeeds,
              newsData: newsData
            });
            setShowSelectedNewsStation(true);
          }}
          onRefreshNews={handleRefreshNews}
        />
      );
    }
    
    // ✅ Fallback: Show shimmer if we have no data and no refresh in progress
    // (This shouldn't happen with the auto-refresh system, but just in case)
    console.log('⏳ Rendering shimmer fallback (no data, not actively refreshing)');
    return (
      <SwipeableNewsCards
        newsData={{}}
        selectedNewsFeeds={selectedNewsFeeds}
        isRefreshing={true}
        onSaveArticle={saveArticle}
        onRemoveArticle={removeSavedArticle}
        isArticleSaved={isArticleSaved}
        onViewSource={(data) => {
          setSelectedNewsSource(data);
          setInAppBrowserUrl(null);
        }}
        onTuneIn={(data) => {
          setSelectedNewsStationData({
            selectedSources: selectedNewsFeeds,
            newsData: newsData
          });
          setShowSelectedNewsStation(true);
        }}
        onRefreshNews={handleRefreshNews}
      />
    );
  })()
)}
        </div>
      </>
    )}
{/* Companies Live Station Section */}
<h2 style={{ 
  fontSize: '13px', 
  fontWeight: '600', 
  color: '#1f2937', 
  margin: '24px 0 16px 0', 
  display: 'flex', 
  alignItems: 'center', 
  gap: '8px',
  marginTop: '10px',
  marginBottom: '18px'
}}>
  <Radio size={16} color="#ff3535ff" />
  Companies Live Stations
</h2>

{/* Companies Live Station Modal */}
{showCompanyStation && selectedCompany && (
  <CompaniesLiveStation
    company={selectedCompany}
    userName={userName}
    onClose={() => {
      setShowCompanyStation(false);
      setSelectedCompany(null);
    }}
  />
)}

{/* COMPANY CARDS CONTAINER */}
<div style={{ marginLeft: '-16px', marginRight: '-16px', marginBottom: '30px' }}>
  <div style={{
    display: 'flex',
    gap: '12px',
    overflowX: 'auto',
    paddingLeft: '16px',
    paddingRight: '16px',
    paddingTop: '12px',
    paddingBottom: '12px',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none'
  }}
  className="custom-scroll">
    
    {isCheckingLocation ? (
      // ✅ SIMPLE SHIMMER LOADING STATE - Just cards, nothing else
      [...Array(7)].map((_, idx) => (
        <div
          key={`skeleton-${idx}`}
          style={{
            flexShrink: 0,
            width: '200px',
            background: 'rgba(255, 255, 255, 0.31)',
            backdropFilter: 'blur(20px)',
            borderRadius: '16px',
            padding: '5px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
            border: '1px solid rgba(255, 255, 255, 0.93)',
            overflow: 'hidden',
            position: 'relative'
          }}
        >
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '16px',
            border: '1px solid rgba(213, 213, 213, 0.5)',
            minHeight: '200px'
          }}>
            {/* Logo skeleton */}
            <div style={{
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              background: '#f3f4f6',
              margin: '0 auto 16px',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <div style={{
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.8), transparent)',
                animation: 'shimmer 1.5s infinite'
              }} />
            </div>
            
            {/* Text skeleton */}
            <div style={{
              height: '16px',
              background: '#f3f4f6',
              borderRadius: '4px',
              marginBottom: '8px',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <div style={{
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.8), transparent)',
                animation: 'shimmer 1.5s infinite',
                animationDelay: '0.1s'
              }} />
            </div>
            
            {/* Subtitle skeleton */}
            <div style={{
              height: '12px',
              background: '#f3f4f6',
              borderRadius: '4px',
              width: '60%',
              margin: '0 auto 20px',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <div style={{
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.8), transparent)',
                animation: 'shimmer 1.5s infinite',
                animationDelay: '0.2s'
              }} />
            </div>
            
            {/* Button skeleton */}
            <div style={{
              height: '36px',
              background: '#f3f4f6',
              borderRadius: '20px',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <div style={{
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.8), transparent)',
                animation: 'shimmer 1.5s infinite',
                animationDelay: '0.3s'
              }} />
            </div>
          </div>
        </div>
      ))
    ) : (
      // ✅ REAL COMPANY CARDS
      COMPANIES_WATCHLIST
        .filter(company => {
          if (company.isLocal) {
            return showLocalCompany;
          }
          return true;
        })
        .map((company) => (
          <CompanyStationCard
            key={company.ticker}
            company={company}
            onClick={() => handleOpenCompanyStation(company)}
          />
        ))
    )}
  </div>
</div>

<style>{`
  @keyframes shimmer {
    0% { 
      left: -100%; 
    }
    100% { 
      left: 100%; 
    }
  }
  
  .custom-scroll::-webkit-scrollbar {
    display: none;
  }
  
  .custom-scroll {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
  
  .custom-scroll {
    scroll-behavior: smooth;
  }
`}</style>

    {/* Stock Watchlist */}
    {showStocksCard && (
      <>
        <h2 style={{ 
          fontSize: '13px', 
          fontWeight: '600', 
          color: '#1f2937', 
          margin: '24px 0 16px 0', 
          display: 'flex', 
          alignItems: 'center', 
          gap: '8px',
          marginBottom: '24px'
        }}>
          <StockIcon />
          Stock Watchlist
        </h2>

        <div style={{ marginLeft: '-16px', marginRight: '-16px', marginBottom: '30px' }}>
          <div style={customScrollStyle} className="custom-scroll">
            {stockData.length === 0 ? (
              <>
                <SkeletonStockCard />
                <SkeletonStockCard />
                <SkeletonStockCard />
              </>
            ) : (
              stockData
                .filter(stock => selectedStocks.includes(stock.ticker))
                .map((stock) => (
                  <StockCardWithStation
                    key={stock.ticker}
                    stock={stock}
                    userName={userName}
                    firestoreService={firestoreService}
                  />
                ))
            )}
          </div>
        </div>
      </>
    )}

    {/* Live Crypto */}
    {showCryptoCard && (
      <>
        <h2 style={{ 
          fontSize: '13px', 
          fontWeight: '600', 
          color: '#1f2937', 
          margin: '24px 0 16px 0', 
          display: 'flex', 
          alignItems: 'center', 
          gap: '8px',
          marginBottom: '27px' 
        }}>
          <DollarSign size={16} color="#3b82f6" />
          Live Cryptocurrency Prices
        </h2>

        <div style={{ marginLeft: '-16px', marginRight: '-16px', marginBottom: '24px' }}>
          <div style={customScrollStyle} className="custom-scroll">
            {cryptoData.length === 0 ? (
              <>
                <div style={{
                  flexShrink: 0,
                  width: '160px',
                  height: '100px',
                  background: '#f3f4f6',
                  borderRadius: '12px',
                  animation: 'pulse 1.5s infinite'
                }} />
                <div style={{
                  flexShrink: 0,
                  width: '160px',
                  height: '100px',
                  background: '#f3f4f6',
                  borderRadius: '12px',
                  animation: 'pulse 1.5s infinite'
                }} />
              </>
            ) : (
              cryptoData.map((coin) => (
                <div
                  key={coin.id}
                  style={{
                    flexShrink: 0,
                    width: '160px',
                    background: 'white',
                    borderRadius: '12px',
                    padding: '12px',
                    border: '1px solid #e5e7eb',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '8px'
                  }}>
                    <img 
                      src={coin.image} 
                      alt={coin.name}
                      style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%'
                      }}
                    />
                    <div>
                      <div style={{
                        fontSize: '12px',
                        fontWeight: '600',
                        color: '#1f2937'
                      }}>
                        {coin.symbol}
                      </div>
                      <div style={{
                        fontSize: '10px',
                        color: '#9ca3af'
                      }}>
                        {coin.name}
                      </div>
                    </div>
                  </div>
                  
                  <div style={{
                    fontSize: '16px',
                    fontWeight: '700',
                    color: '#1f2937',
                    marginBottom: '4px'
                  }}>
                    ${coin.price >= 1 ? coin.price.toLocaleString() : coin.price.toFixed(4)}
                  </div>
                  
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontSize: '11px',
                    fontWeight: '600',
                    color: parseFloat(coin.percent) >= 0 ? '#10b981' : '#ef4444'
                  }}>
                    {parseFloat(coin.percent) >= 0 ? '▲' : '▼'}
                    {Math.abs(parseFloat(coin.percent))}%
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </>
    )}
  </>
)}
{/* Content - For You Tab */}
{activeTab === 'foryou' && (
  <>
    <LocalNewsCard 
      onSaveArticle={saveArticle}
      isArticleSaved={isArticleSaved}
      onRemoveArticle={removeSavedArticle}
      savedArticles={savedArticles}
    />

    {/* Route Card */}
    <h2 style={{ 
      fontSize: '13px', 
      fontWeight: '600', 
      color: '#1f2937', 
      margin: '24px 0 16px 0', 
      display: 'flex', 
      alignItems: 'center', 
      marginBottom: '18px',
      gap: '8px' 
    }}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
      </svg>
      Your Commute
    </h2>

    <RouteCard 
      savedRoutes={savedRoutes}
      onEditRoute={(route) => {
        setNewRouteName(route.name);
        setNewRouteOrigin(route.origin);
        setNewRouteDestination(route.destination);
        setNewRouteOriginCoords(route.originCoords || '');
        setNewRouteDestinationCoords(route.destCoords || '');
        setEditingRouteId(route.id);
        setShowRouteSetup(true);
      }}
      onViewAllRoutes={() => {
        setShowRouteSetup(true);
      }}
      onAddRoute={() => {
        setNewRouteName('');
        setNewRouteOrigin('');
        setNewRouteDestination('');
        setNewRouteOriginCoords('');
        setNewRouteDestinationCoords('');
        setEditingRouteId(null);
        setShowRouteSetup(true);
      }}
    />
  </>
)}

{/* Content - Saved Tab */}
{activeTab === 'saved' && (
<SavedContent 
  savedArticles={savedArticles}
  savedPodcasts={savedPodcasts}
  savedDailyBriefs={savedDailyBriefs}
  onRemoveArticle={removeSavedArticle}
  onRemovePodcast={deleteSavedPodcast}
  onPlayPodcast={handlePlaySavedPodcast}  // ✅ Add this if missing
  onPlayDailyBrief={handlePlayDailyBrief}
  setInAppBrowserUrl={setInAppBrowserUrl}
  setSelectedNewsSource={setSelectedNewsSource}
  handlePlaySavedPodcast={handlePlaySavedPodcast}  // ✅ Add this line
  removeSavedPodcast={removeSavedPodcast}
/>
)}



{/* News Popup Modal */}
{selectedNewsSource && (
  <div 
    onClick={() => setSelectedNewsSource(null)}
    style={{ 
      position: 'fixed', 
      top: 0, 
      left: 0, 
      right: 0, 
      bottom: 0, 
      background: 'rgba(0, 0, 0, 0.5)', 
      zIndex: 1000, 
      display: 'flex', 
      alignItems: 'flex-end',
      justifyContent: 'center',
      animation: 'fadeIn 0.25s cubic-bezier(0.4, 0, 0.2, 1)'
    }}
  >
    <div 
      onClick={(e) => e.stopPropagation()}
      style={{ 
        background: 'white', 
        borderRadius: '24px 24px 0 0',
        width: '100%',
        height: '92vh',
        overflow: 'hidden',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
      animation: 'fadeIn 0.45s cubic-bezier(0.4, 0, 0.2, 1)',
         display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Header */}
      <div style={{ position: 'sticky', top: 0, background: 'white', zIndex: 10, padding: '20px 24px', borderBottom: '1px solid #e5e7eb', borderRadius: '20px 20px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ 
            background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', 
            padding: '6px 12px', 
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
      <Radio size={14} color="#f68f3bff" />

               <span style={{ fontSize: '11px', fontWeight: '600', color: '#92400e' }}>
              InnerLoop
            </span>
          </div>
          <img 
            src={selectedNewsSource.sourceData.logo} 
            alt={selectedNewsSource.sourceData.name} 
            style={{ height: '20px', objectFit: 'contain' }} 
          />
        </div>
        <button
          onClick={() => setSelectedNewsSource(null)}
          style={{
            background: '#f3f4f6',
            border: '1px solid #e5e7eb',
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <X size={18} color="#1f2937" />
        </button>
      </div>

      {/* Content */}
      <div style={{ 
        flex: 1, 
        overflowY: 'auto', 
        padding: '24px' 
      }}>
        {selectedNewsSource.sourceData.stories.map((story, index) => (
          <div 
            key={index} 
            style={{ 
              marginBottom: index < selectedNewsSource.sourceData.stories.length - 1 ? '32px' : '0', 
              paddingBottom: index < selectedNewsSource.sourceData.stories.length - 1 ? '32px' : '0', 
              borderBottom: index < selectedNewsSource.sourceData.stories.length - 1 ? '1px solid #e5e7eb' : 'none' 
            }}
          >
            <img 
              src={story.image} 
              alt={story.title} 
              style={{ 
                width: '100%', 
                height: '200px', 
                objectFit: 'cover',
                borderRadius: '12px',
                marginBottom: '16px'
              }} 
            />

            {/* Headline */}
            <h3 style={{ 
              fontSize: '18px', 
              fontWeight: '700', 
              color: '#1f2937', 
              marginBottom: '8px',
              lineHeight: '1.3',
              textAlign: 'left'
            }}>
              {story.title}
            </h3>

            {/* Timestamp */}
            <div style={{ 
              fontSize: '12px', 
              color: '#9ca3af', 
              display: 'flex', 
              alignItems: 'center', 
              gap: '6px',
              marginBottom: '16px'
            }}>
              <Clock size={14} />
              {story.time}
            </div>

            {/* Summary Paragraph */}
            <p style={{ 
              fontSize: '14px', 
              lineHeight: '1.7', 
              color: '#4b5563',
              textAlign: 'left',
              marginBottom: '16px'
            }}>
              {story.description}
            </p>

            {/* Read Full Article Button */}
            {story.source && (
              <button
                onClick={() => {
                  const confirmed = window.confirm(
                    'This will open the article in a new tab. Do you want to continue?'
                  );
                  if (confirmed) {
                    window.open(story.source, '_blank', 'noopener,noreferrer');
                  }
                }}
                style={{ 
                  width: '100%',
                  fontSize: '13px', 
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  fontWeight: '600',
                  padding: '12px 20px',
                  background: '#0e0e0eff',
                  borderRadius: '24px',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#1a1a1a';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#272727';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.15)';
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                Read Full Article
              </button>
            )}
          </div>
        ))}
      </div>
    </div>

    {/* Add animations */}
    <style>{`
      @keyframes slideUp {
        from {
          transform: translateY(100%);
          opacity: 0;
        }
        to {
          transform: translateY(0);
          opacity: 1;
        }
      }
      
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
    `}</style>
  </div>
)}
{currentPodcast && currentPodcast.audioUrl && (
  <audio
    ref={audioRef}
    src={currentPodcast.audioUrl || null}
    preload="metadata"
    style={{ display: 'none' }}
    onEnded={() => {
      console.log('🎵 Audio ended');
      setIsPodcastPlaying(false);
    }}
    onError={(e) => {
      console.error('❌ Audio error:', {
        error: e,
        src: audioRef.current?.src,
        podcastUrl: currentPodcast?.audioUrl,
        networkState: audioRef.current?.networkState,
        readyState: audioRef.current?.readyState
      });
      
      if (audioRef.current?.networkState === 3) {
        console.error('❌ Network error - possibly CORS or file not found');
        alert('Unable to load audio. The file may not be available.');
        setShowTranscriptView(false);
        setCurrentPodcast(null);
      }
    }}
  />
)}
{/* Mini Player Bar - Only hides when transcript opens, NOT when paused */}
{currentPodcast && !showTranscriptView && (
<div style={{
  position: 'fixed',
  bottom: '20px',
  left: '50%',
  transform: 'translateX(-50%)',
  background: 'linear-gradient(135deg, #1b1b1bd2 0%, #1a1a1adc 100%)',
  borderRadius: '18px',
  padding: '10px 14px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
  display: 'flex',
  alignItems: 'center',
  gap: '14px',
  zIndex: 999,
  maxWidth: 'calc(100% - 40px)',
  minWidth: '340px',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  transition: 'all 0.3s ease',
  animation: 'fadeIn 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
}}>

    {/* Album Art with Cover Image */}
    <div 
      onClick={() => setShowTranscriptView(true)}
      style={{
        width: '52px',
        height: '52px',
        borderRadius: '10px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        cursor: 'pointer',
        overflow: 'hidden',
        transition: 'transform 0.2s',
        position: 'relative'
      }}
      onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
      onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
    >
      {currentPodcast.coverImage ? (
        <img 
          src={currentPodcast.coverImage}
          alt="Podcast cover"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }}
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.parentElement.style.background = 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)';
            e.target.parentElement.innerHTML = `
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path stroke="#9ca3af" stroke-width="2" stroke-linejoin="round" d="M20 16v-4a8 8 0 1 0-16 0v4m16 0v2a2 2 0 0 1-2 2h-2v-6h2a2 2 0 0 1 2 2ZM4 16v2a2 2 0 0 0 2 2h2v-6H6a2 2 0 0 0-2 2Z"/>
              </svg>
            `;
          }}
        />
      ) : (
        <Radio size={22} color="#9ca3af" />
      )}
    </div>
    
    {/* Title & Info */}
    <div 
      onClick={() => setShowTranscriptView(true)}
      style={{ 
        flex: 1, 
        minWidth: 0,
        cursor: 'pointer'
      }}
    >
      <div style={{
        fontSize: '14px',
        fontWeight: '600',
        color: 'white',
        marginBottom: '4px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      }}>
        {currentPodcast.title || 'InnerLoop Podcast'}
      </div>
      <div style={{
        fontSize: '11px',
        color: '#b3b3b3',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      }}>
        {(() => {
          if (dialogueTimestamps.length === 0) return 'Preptune';

          let left = 0;
          let right = dialogueTimestamps.length - 1;
          let currentIndex = 0;
          
          while (left <= right) {
            const mid = Math.floor((left + right) / 2);
            const line = dialogueTimestamps[mid];
            
            if (currentAudioTime >= line.startTime && currentAudioTime < line.endTime) {
              currentIndex = mid;
              break;
            } else if (currentAudioTime < line.startTime) {
              right = mid - 1;
            } else {
              left = mid + 1;
            }
          }
          
          if (currentAudioTime >= dialogueTimestamps[dialogueTimestamps.length - 1].endTime) {
            currentIndex = dialogueTimestamps.length - 1;
          }

          const currentLine = dialogueTimestamps[currentIndex];
          const hostName = getHostName(currentLine?.speaker);
          return `${hostName} speaking`;
        })()}
      </div>
    </div>
    
    {/* Play/Pause Button */}
    <button
      onClick={(e) => {
        e.stopPropagation();
        if (audioRef.current) {
          if (isPodcastPlaying) {
            audioRef.current.pause();
            setIsPodcastPlaying(false);
          } else {
            audioRef.current.play();
            setIsPodcastPlaying(true);
          }
        }
      }}
      style={{
        width: '38px',
        height: '38px',
        borderRadius: '50%',
        background: 'white',
        border: 'none',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.2s',
        flexShrink: 0
      }}
      onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.08)'}
      onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
    >
      {isPodcastPlaying ? (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="black">
          <rect x="6" y="4" width="4" height="16" rx="1" />
          <rect x="14" y="4" width="4" height="16" rx="1" />
        </svg>
      ) : (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="black" style={{ marginLeft: '2px' }}>
          <path d="M8 5v14l11-7z" />
        </svg>
      )}
    </button>

    {/* ✅ NEW: Close Button */}
    <button
      onClick={(e) => {
        e.stopPropagation();
        // Stop audio
        if (audioRef.current) {
          audioRef.current.pause();
          setIsPodcastPlaying(false);
        }
        // Clear current podcast
        setCurrentPodcast(null);
        setShowTranscriptView(false);
      }}
      style={{
        width: '38px',
        height: '38px',
        borderRadius: '50%',
        background: 'rgba(255, 255, 255, 1)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.2s',
        flexShrink: 0
      }}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2" strokeLinecap="round">
        <path d="M18 6L6 18M6 6l12 12" />
      </svg>
    </button>
  </div>
)}
{/* ✅ COMPLETE TRANSCRIPT VIEW - Daily Brief Slideshow + Regular Podcast */}
{showTranscriptView && currentPodcast && (
  <>
    {currentPodcast.type === 'daily-brief-catchup' ? (
      /* ✅ Daily Brief Slideshow View */
      <DailyBriefTranscriptView
        briefData={currentPodcast}
        isPlaying={isPodcastPlaying}
        onClose={() => setShowTranscriptView(false)}
        audioRef={audioRef}
        currentTime={currentAudioTime}
        audioDuration={audioDuration}
        onTogglePlayPause={() => {
          if (audioRef.current) {
            if (isPodcastPlaying) {
              audioRef.current.pause();
              setIsPodcastPlaying(false);
            } else {
              audioRef.current.play();
              setIsPodcastPlaying(true);
            }
          }
        }}
        playbackSpeed={playbackSpeed}
        onSpeedChange={(speed) => {
          setPlaybackSpeed(speed);
          if (audioRef.current) {
            audioRef.current.playbackRate = speed;
          }
        }}
        onRewind={() => {
          if (audioRef.current) {
            audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime - 15);
          }
        }}
        onFastForward={() => {
          if (audioRef.current) {
            audioRef.current.currentTime = Math.min(audioDuration, audioRef.current.currentTime + 15);
          }
        }}
        isDragging={isDragging}
        onSeekStart={handleMouseDown}
        onSeekMove={handleMouseMove}
        onSeekEnd={handleMouseUp}
        isPodcastSaved={isPodcastSaved}
      />
    ) : (
<div style={{
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0, 0, 0, 0.5)',
  backdropFilter: 'blur(8px)',
  WebkitBackdropFilter: 'blur(8px)', // ✅ Safari support
  zIndex: 2000,
  display: 'flex',
  alignItems: 'flex-end',
  animation: 'fadeIn 0.3s ease-out',
  isolation: 'isolate', // ✅ Prevent blending issues
  willChange: 'opacity' // ✅ GPU acceleration
}}>
<div style={{
  width: '100%',
  height: '92vh',
  background: 'linear-gradient(135deg, #326077ff 0%, #021423ff 100%)',
  borderRadius: '28px 28px 0 0',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  borderTop: '2px solid #8d8d8dff',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
  overflow: 'hidden',
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  transform: 'translateZ(0)', // ✅ Force GPU layer
  WebkitTransform: 'translateZ(0)' // ✅ Safari compatibility
}}>
      {/* Blurred background */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: currentPodcast.coverImage ? `url(${currentPodcast.coverImage})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        filter: 'blur(150px) saturate(1.2)',
        transform: 'scale(1.1)',
        zIndex: 0,
        opacity: 0.3
      }} />
      
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.3))',
        zIndex: 1
      }} />

      {/* Header */}
      <div style={{
        padding: '24px',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'relative',
        zIndex: 2
      }}>
        <button 
          onClick={() => setShowTranscriptView(false)} 
          style={{ 
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.1)',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s'
          }}
        >
          <ChevronDown size={20} color="#fff" />
        </button>

        <div style={{ textAlign: 'center', flex: 1 }}>
          <div style={{ 
            fontSize: '16px', 
            fontWeight: '600', 
            color: '#ffffff', 
            marginBottom: '2px',
            marginLeft: '16px',
            textAlign:'left',
            textShadow: '0 2px 8px rgba(0, 0, 0, 0.2)'
          }}>
            InnerLoop
          </div>
          <div
            style={{
              fontSize: '12px',
              color: 'rgba(255, 255, 255, 0.7)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              textAlign: 'left',
              marginLeft: '16px',
              maxWidth: '180px'
            }}
          >
            {currentPodcast.title}
          </div>
        </div>

        <button
          onClick={async () => {
            if (isSaved) {
              try {
                const podcastToRemove = savedPodcasts.find(p => p.audioUrl === currentPodcast.audioUrl);
                if (podcastToRemove && firestoreService?.isReady()) {
                  await firestoreService.deletePodcast(podcastToRemove.id);
                  setIsSaved(false);
                  console.log('✅ Podcast unsaved');
                }
              } catch (error) {
                console.error('❌ Failed to unsave:', error);
                alert('Failed to remove podcast');
              }
            } else {
              await handleSavePodcast();
            }
          }}
          disabled={isSaving}
          style={{
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            background: isSaved ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.1)',
            backdropFilter: isSaved ? 'blur(10px)' : 'none',
            border: 'none',
            cursor: isSaving ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: isSaved ? '0 2px 8px rgba(0, 0, 0, 0.1)' : 'none',
            transition: 'all 0.2s',
            opacity: isSaving ? 0.6 : 1
          }}
        >
          {isSaving ? (
            <div className="loading-spinner" style={{ width: '16px', height: '16px' }} />
          ) : (
            <svg 
              width="16" 
              height="16" 
              fill={isSaved ? '#0e5092ff' : 'none'} 
              viewBox="0 0 24 24" 
              stroke={isSaved ? '#0e5092ff' : '#ffffff'}
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
            </svg>
          )}
        </button>
      </div>

      {/* ✅ Transcript Content - Fades out when save prompt shows */}
      <div 
        ref={transcriptRef} 
        style={{ 
          flex: 1, 
          overflowY: 'auto', 
          padding: '20px 24px', 
          scrollBehavior: 'smooth',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: currentPodcast.dialogue.length === 0 ? 'center' : 'flex-start',
          alignItems: currentPodcast.dialogue.length === 0 ? 'center' : 'stretch',
          opacity: showSavePrompt ? 0 : 1,
          transition: 'opacity 0.3s ease',
          pointerEvents: showSavePrompt ? 'none' : 'auto',
          position: 'relative',
          zIndex: 2
        }}
      >
        {currentPodcast.dialogue.length === 0 ? (
          // Empty State - Before Playing
          <div style={{
            textAlign: 'center',
            animation: 'fadeInUp 0.6s ease-out'
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              margin: '0 auto 20px',
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="white">
                <path d="M8 5v14l11-7z"/>
              </svg>
            </div>
            <p style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#ffffff',
              marginBottom: '8px'
            }}>
              Tap play to listen
            </p>
            <p style={{
              fontSize: '14px',
              color: 'rgba(255, 255, 255, 0.6)',
              maxWidth: '250px',
              lineHeight: '1.5'
            }}>
              Your audio deep dive is ready to start
            </p>
          </div>
        ) : (
          // Transcript Content
          currentPodcast.dialogue
            .slice(Math.max(0, activeLineIndex - 1), activeLineIndex + 1)
            .map((line, idx, arr) => {
              const actualIndex = Math.max(0, activeLineIndex - 1) + idx;
              const isActive = actualIndex === activeLineIndex;
              const isPast = actualIndex < activeLineIndex;
              const hostName = getHostName(line.speaker);

              return (
                <div 
                  key={actualIndex} 
                  ref={el => lineRefs.current[actualIndex] = el} 
                  style={{ 
                    marginBottom: '24px',
                    textAlign: 'left',
                    opacity: isPast ? 0.5 : 1,
                    transition: 'all 0.3s ease',
                    animation: 'fadeInUp 0.5s ease'
                  }}
                >
                  <div style={{ 
                    fontSize: '11px', 
                    fontWeight: '600', 
                    color: isActive ? '#a7c9ffff' : '#9ca3af',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span>{hostName}</span>
                  </div>

                  <div 
                    style={{ 
                      fontSize: isActive ? '20px' : '18px',
                      lineHeight: '1.6',
                      color: isActive ? '#ffffff' : '#80899aff',
                      fontWeight: isActive ? '600' : '400',
                      transition: 'all 0.3s ease'
                    }}
                    dangerouslySetInnerHTML={{
                      __html: isActive && line.keywords && Math.random() > 0.7 
                        ? highlightImportantWords(line.line, line.keywords)
                        : line.line
                    }}
                  />
                </div>
              );
            })
        )}
        
        {/* Show loading indicator if still generating */}
        {currentPodcast.isGenerating && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '20px',
            color: '#9ca3af'
          }}>
            <div className="loading-spinner" />
            <span>Generating more...</span>
          </div>
        )}
      </div>

      {/* ✅ Save Prompt - Appears centered in modal */}
      {showSavePrompt && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 100,
          animation: 'scaleIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) 0.3s both'
        }}>
          <div style={{
            background: 'rgba(230, 243, 255, 0.08)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            borderRadius: '35px',
            padding: '32px',
            maxWidth: '380px',
            width: '90vw',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            textAlign: 'center'
          }}>
            {/* Icon */}
            <div style={{
              width: '64px',
              height: '64px',
              margin: '0 auto 20px',
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <svg width="28" height="28" fill="white" viewBox="0 0 24 24" stroke="white" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
              </svg>
            </div>

            {/* Title */}
            <h3 style={{
              fontSize: '20px',
              fontWeight: '700',
              color: '#ffffff',
              marginBottom: '12px',
              lineHeight: '1.3'
            }}>
              Save this podcast?
            </h3>

            {/* Description */}
            <p style={{
              fontSize: '14px',
              color: 'rgba(255, 255, 255, 0.8)',
              marginBottom: '28px',
              lineHeight: '1.5'
            }}>
              Keep this in your saved library for easy access later
            </p>

            {/* Buttons */}
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'center'
            }}>
              <button
                onClick={() => {
                  setShowSavePrompt(false);
                  setShowTranscriptView(false);
                  setCurrentPodcast(null);
                }}
                style={{
                  padding: '12px 24px',
                  borderRadius: '28px',
                  background: 'rgba(0, 0, 0, 0.17)',
                  color: '#ffffff',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                   border: '1px solid rgba(255, 255, 255, 0.2)',
                }}
         
              >
                Cancel
              </button>

              <button
                onClick={async () => {
                  await handleSavePodcast();
                  setShowSavePrompt(false);
                  setShowTranscriptView(false);
                }}
                disabled={isSaving}
                style={{
                  padding: '12px 24px',
                  borderRadius: '28px',
                  background: '#ffffff',
                  border: 'none',
                  color: '#000000',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: isSaving ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  opacity: isSaving ? 0.7 : 1,
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                }}
                onMouseEnter={(e) => {
                  if (!isSaving) {
                    e.currentTarget.style.background = '#f0f0f0';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.2)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSaving) {
                    e.currentTarget.style.background = '#ffffff';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                  }
                }}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fixed Bottom Controls */}
      <div style={{ 
        background: 'rgba(244, 251, 255, 0.15)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        padding: '16px 20px 24px',
        borderTop: '1px solid rgba(255, 255, 255, 0.56)',
        borderRadius: '42px 42px 0 0',
        position: 'relative',
        zIndex: 30
      }}>
        <div style={{
          background: '#ffffff',
          borderRadius: '32px',
          padding: '8px 20px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          border: '1px solid rgba(169, 177, 185, 0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '16px'
        }}>
          <button
            onClick={changeSpeed}
            style={{
              padding: '12px 16px',
              borderRadius: '20px',
              background: '#f3f4f6',
              border: 'none',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              color: '#000',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#e5e7eb'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#f3f4f6'}
          >
            {playbackSpeed}x
          </button>

          <button onClick={handleRewind} style={{ 
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            background: '#f3f4f6',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s'
          }}>
            <svg width="20" height="20" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg"
              style={{ transform: "scaleX(-1)" }}>
              <path d="M1.84998 7.49998C1.84998 4.66458 4.05979 1.84998 7.49998 1.84998C10.2783 1.84998 11.6515 3.9064 12.2367 5H10.5C10.2239 5 10 5.22386 10 5.5C10 5.77614 10.2239 6 10.5 6H13.5C13.7761 6 14 5.77614 14 5.5V2.5C14 2.22386 13.7761 2 13.5 2C13.2239 2 13 2.22386 13 2.5V4.31318C12.2955 3.07126 10.6659 0.849976 7.49998 0.849976C3.43716 0.849976 0.849976 4.18537 0.849976 7.49998C0.849976 10.8146 3.43716 14.15 7.49998 14.15C9.44382 14.15 11.0622 13.3808 12.2145 12.2084C12.8315 11.5806 13.3133 10.839 13.6418 10.0407C13.7469 9.78536 13.6251 9.49315 13.3698 9.38806C13.1144 9.28296 12.8222 9.40478 12.7171 9.66014C12.4363 10.3425 12.0251 10.9745 11.5013 11.5074C10.5295 12.4963 9.16504 13.15 7.49998 13.15C4.05979 13.15 1.84998 10.3354 1.84998 7.49998Z" fill="#000" fillRule="evenodd" clipRule="evenodd"/>
            </svg>
          </button>

          <button onClick={() => {
            if (audioRef.current) {
              if (isPodcastPlaying) {
                audioRef.current.pause();
                setIsPodcastPlaying(false);
              } else {
                audioRef.current.play();
                setIsPodcastPlaying(true);
              }
            }
          }} style={{ 
            background: '#000',
            border: 'none', 
            width: '58px', 
            height: '58px', 
            borderRadius: '50%', 
            cursor: 'pointer', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
            transition: 'all 0.2s'
          }}>
            {isPodcastPlaying ? (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                <rect x="6" y="5" width="4" height="14" rx="1.5" />
                <rect x="14" y="5" width="4" height="14" rx="1.5" />
              </svg>
            ) : (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="white" style={{ marginLeft: '2px' }}>
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
          </button>

          <button onClick={handleFastForward} style={{ 
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            background: '#f3f4f6',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s'
          }}>
            <svg width="20" height="20" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1.84998 7.49998C1.84998 4.66458 4.05979 1.84998 7.49998 1.84998C10.2783 1.84998 11.6515 3.9064 12.2367 5H10.5C10.2239 5 10 5.22386 10 5.5C10 5.77614 10.2239 6 10.5 6H13.5C13.7761 6 14 5.77614 14 5.5V2.5C14 2.22386 13.7761 2 13.5 2C13.2239 2 13 2.22386 13 2.5V4.31318C12.2955 3.07126 10.6659 0.849976 7.49998 0.849976C3.43716 0.849976 0.849976 4.18537 0.849976 7.49998C0.849976 10.8146 3.43716 14.15 7.49998 14.15C9.44382 14.15 11.0622 13.3808 12.2145 12.2084C12.8315 11.5806 13.3133 10.839 13.6418 10.0407C13.7469 9.78536 13.6251 9.49315 13.3698 9.38806C13.1144 9.28296 12.8222 9.40478 12.7171 9.66014C12.4363 10.3425 12.0251 10.9745 11.5013 11.5074C10.5295 12.4963 9.16504 13.15 7.49998 13.15C4.05979 13.15 1.84998 10.3354 1.84998 7.49998Z" fill="#000" fillRule="evenodd" clipRule="evenodd"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: translate(-50%, -50%) scale(0.9);
          }
          to {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1);
          }
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  </div>
)}
  </>
)}

{/* Route Setup Modal */}
{(showRouteSetup || showTrafficRouteSetup || showRouteSetupFromDailyBrief) && (
  <div 
    onClick={() => {
      setShowRouteSetup(false);
      setShowTrafficRouteSetup(false);
      setShowRouteSetupFromDailyBrief(false); // ✅ ADD THIS
    }}    
style={{ 
  position: 'fixed', 
  top: 0, 
  left: 0, 
  right: 0, 
  bottom: 0, 
  background: 'rgba(37, 37, 37, 0.05)', 
  backdropFilter: 'blur(2px)',        // ✅ adds background blur
  WebkitBackdropFilter: 'blur(8px)',  // ✅ Safari support
  zIndex: showRouteSetupFromDailyBrief ? 10001 : 1000,
  display: 'flex', 
  alignItems: 'flex-end',
  justifyContent: 'center',
  animation: 'fadeIn 0.25s cubic-bezier(0.4, 0, 0.2, 1)'
}}
>
    <div 
      onClick={(e) => e.stopPropagation()}
      style={{ 
        background: 'white', 
        borderRadius: '24px 24px 0 0',
        width: '100%',
        height: '92vh',
        overflow: 'hidden',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
      animation: 'fadeIn 0.45s cubic-bezier(0.4, 0, 0.2, 1)',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Header */}
      <div style={{ 
        padding: '20px 20px 16px 20px', 
        borderBottom: '1px solid #e5e7eb',
        position: 'relative',
        zIndex: 1,
        background: '#fafafa'
      }}>
        {/* Close Button */}
        <button
          onClick={() => {
            setShowRouteSetup(false);
            setShowTrafficRouteSetup(false);
             setShowRouteSetupFromDailyBrief(false); 
          }}
          style={{
            background: 'white',
            border: '1px solid #e5e7eb',
            width: '30px',
            height: '30px',
            borderRadius: '50%',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            marginBottom: '16px'
          }}
        >
          <X size={17} color="#807f7e" />
        </button>

        {/* Title and Description */}
        <div style={{ textAlign: 'left' }}>
          <h3 style={{ 
            margin: 0, 
            fontSize: '16px', 
            fontWeight: '500', 
            color: '#1f2937',
            marginBottom: '6px'
          }}>
            Live Traffic Setup
          </h3>
          <p style={{ 
            margin: 0, 
            fontSize: '13px', 
            color: '#9ca3af',
            lineHeight: '1.5'
          }}>
            Set up your daily routes to get real-time traffic updates in your podcast.
          </p>
        </div>
      </div>

      {/* Content */}
      <div style={{ 
        flex: 1,
        overflowY: 'auto', 
        overflowX: 'hidden',
        padding: '24px'
      }}>


        {/* Saved Routes Section */}
        {savedRoutes.length > 0 && (
          <>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '14px',
              paddingBottom: '10px',
              borderBottom: '2px solid #f3f4f6'
            }}>
              <h4 style={{ 
                fontSize: '13px', 
                fontWeight: '600', 
                color: '#111827', 
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                textAlign: 'left'
              }}>
                Saved Routes
              </h4>
              <span style={{
                fontSize: '11px',
                fontWeight: '600',
                color: '#6b7280',
                background: '#f3f4f6',
                padding: '4px 10px',
                borderRadius: '12px'
              }}>
                {savedRoutes.length} {savedRoutes.length === 1 ? 'route' : 'routes'}
              </span>
            </div>
            
            <div style={{ marginBottom: '28px' }}>
{savedRoutes.map((route, idx) => {
  const originIsCoords = /^-?\d+\.?\d*,-?\d+\.?\d*$/.test(route.origin.trim());
  const destIsCoords = /^-?\d+\.?\d*,-?\d+\.?\d*$/.test(route.destination.trim());
  
  return (
    <div 
      key={route.id}
      style={{
        background: 'rgba(255, 255, 255, 0.31)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(213, 213, 213, 0.5)',
        borderRadius: '12px',
        padding: '16px',
        marginBottom: '10px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
        position: 'relative'
      }}
    >
      {/* ✅ MAP VIEW BUTTON */}
      <button
        onClick={() => {
          setSelectedRouteForMap(route);
          setShowMapView(true);
        }}
        style={{
          position: 'absolute',
          top: '12px',
          right: '80px',
          background: 'white',
          border: '1px solid #3b82f6',
          borderRadius: '50%',
          width: '28px',
          height: '28px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          transition: 'all 0.2s',
          boxShadow: '0 2px 4px rgba(59, 130, 246, 0.1)'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = '#eff6ff';
          e.currentTarget.style.borderColor = '#2563eb';
          e.currentTarget.style.transform = 'scale(1.1)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'white';
          e.currentTarget.style.borderColor = '#3b82f6';
          e.currentTarget.style.transform = 'scale(1)';
        }}
      >
        <MapPin size={14} color="#3b82f6" />
      </button>

      {/* Edit Button */}
      <button
        onClick={() => {
          setNewRouteName(route.name);
          setNewRouteOrigin(route.origin);
          setNewRouteDestination(route.destination);
          setNewRouteOriginCoords(route.originCoords || '');
          setNewRouteDestinationCoords(route.destCoords || '');
          setEditingRouteId(route.id);
          
          setTimeout(() => {
            document.querySelector('[data-form-section]')?.scrollIntoView({ behavior: 'smooth' });
          }, 100);
        }}
        style={{
          position: 'absolute',
          top: '12px',
          right: '45px',
          background: 'white',
          border: '1px solid #dbeafe',
          borderRadius: '50%',
          width: '28px',
          height: '28px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          transition: 'all 0.2s'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = '#dbeafe';
          e.currentTarget.style.borderColor = '#93c5fd';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'white';
          e.currentTarget.style.borderColor = '#dbeafe';
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      </button>

      {/* Delete Button */}
      <button
        onClick={() => setDeleteConfirm(route)}
        style={{
          position: 'absolute',
          top: '12px',
          right: '12px',
          background: 'white',
          border: '1px solid #cbcbcbff',
          borderRadius: '50%',
          width: '28px',
          height: '28px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          transition: 'all 0.2s'
        }}

      >
        <X size={14} color="#000000ff" />
      </button>

      {/* Route Content */}
      <div style={{ 
        fontSize: '14px', 
        fontWeight: '600', 
        color: '#111827', 
        marginBottom: '10px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        textAlign: 'left',
        paddingRight: '110px'
      }}>
        <span style={{
          background: '#272727',
          color: 'white',
          width: '24px',
          height: '24px',
          borderRadius: '50%',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '11px',
          fontWeight: '700',
          flexShrink: 0
        }}>
          {idx + 1}
        </span>
        {route.name}
      </div>
      
      <div style={{ 
        paddingLeft: '32px',
        borderLeft: '2px solid #e5e7eb',
        marginLeft: '11px'
      }}>
        <div style={{ 
          fontSize: '12px', 
          color: '#6b7280', 
          marginBottom: '6px',
          textAlign: 'left'
        }}>
          <span style={{ fontWeight: '600', color: '#374151', display: 'inline-block', minWidth: '45px' }}>From:</span>
          <span style={{ color: originIsCoords ? '#f59e0b' : '#6b7280' }}>
            {originIsCoords ? '📍 Coordinates' : route.origin}
          </span>
        </div>
        <div style={{ 
          fontSize: '12px', 
          color: '#6b7280',
          textAlign: 'left'
        }}>
          <span style={{ fontWeight: '600', color: '#374151', display: 'inline-block', minWidth: '45px' }}>To:</span>
          <span style={{ color: destIsCoords ? '#f59e0b' : '#6b7280' }}>
            {destIsCoords ? '📍 Coordinates' : route.destination}
          </span>
        </div>
      </div>
    </div>
  );
})}
            </div>
          </>
        )}

        {/* Divider */}
        {savedRoutes.length > 0 && (
          <div style={{
            height: '1px',
            background: 'linear-gradient(90deg, transparent, #e5e7eb, transparent)',
            marginBottom: '24px'
          }} />
        )}

        {/* Add New Route Section */}
        <div>
          <div style={{
            marginBottom: '16px',
            paddingBottom: '10px',
            borderBottom: '2px solid #f3f4f6'
          }}>
            <h4 style={{ 
              fontSize: '13px', 
              fontWeight: '600', 
              color: '#111827', 
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              textAlign: 'left'
            }}>
              <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {editingRouteId ? 'Edit Route' : 'Add New Route'}
            </h4>
          </div>

          <div 
            data-form-section
            style={{
              background: 'rgba(255, 255, 255, 0.31)',
              backdropFilter: 'blur(20px)',
              borderRadius: '16px',
              padding: '20px',
              border: '1px solid rgba(255, 255, 255, 0.93)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
            }}
          >
            
            <div style={{ marginBottom: '14px' }}>
              <label style={{ 
                fontSize: '11px', 
                fontWeight: '600', 
                color: '#374151', 
                display: 'block', 
                marginBottom: '6px',
                textAlign: 'left'
              }}>
                Route Name
              </label>
              <input
                type="text"
                placeholder="e.g., Morning Commute"
                value={newRouteName}
                onChange={(e) => setNewRouteName(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '20px',
                  fontSize: '13px',
                  outline: 'none',
                  background: 'white',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
                }}
              />
            </div>
            
            <div style={{ 
              height: '1px', 
              background: '#f3f4f6', 
              marginBottom: '14px' 
            }} />

            <div style={{ marginBottom: '14px' }}>
              <label style={{ 
                fontSize: '11px', 
                fontWeight: '600', 
                color: '#374151', 
                display: 'block', 
                marginBottom: '6px',
                textAlign: 'left'
              }}>
                Starting Point
              </label>
              <input
                type="text"
                placeholder="Enter address or use location button"
                value={newRouteOrigin}
                onChange={(e) => setNewRouteOrigin(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '20px',
                  fontSize: '13px',
                  outline: 'none',
                  background: 'white',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                  textAlign: 'left'
                }}
              />
            </div>
            
            <button
              onClick={async () => {
                try {
                  const { getCurrentLocation, reverseGeocode } = await import('./trafficHandler');
                  
                  setNewRouteOrigin('✓ Current location set');
                  
                  const coords = await getCurrentLocation();
                  console.log('Got coordinates:', coords);
                  
                  setNewRouteOriginCoords(coords);
                  
                  const address = await reverseGeocode(coords);
                  console.log('Converted to address:', address);
                  
                  setNewRouteOrigin(address);
                  
                } catch (error) {
                  console.error('Location error:', error);
                  setNewRouteOrigin('');
                  setNewRouteOriginCoords('');
                  alert('❌ Could not get your location. Please check permissions.');
                }
              }}
              style={{
                width: '100%',
                padding: '10px',
                background: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '20px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '500',
                color: '#1f2937',
                marginBottom: '18px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#f9fafb';
                e.currentTarget.style.borderColor = '#d1d5db';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.borderColor = '#e5e7eb';
              }}
            >
              <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="#1b76ffff">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Use My Current Location
            </button>

            <div style={{ 
              height: '1px', 
              background: '#f3f4f6', 
              marginBottom: '14px' 
            }} />

            <div style={{ marginBottom: '18px' }}>
              <label style={{ 
                fontSize: '11px', 
                fontWeight: '600', 
                color: '#374151', 
                display: 'block', 
                marginBottom: '6px',
                textAlign: 'left'
              }}>
                Destination
              </label>
              <input
                type="text"
                placeholder="Enter destination address"
                value={newRouteDestination}
                onChange={(e) => setNewRouteDestination(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '20px',
                  fontSize: '13px',
                  outline: 'none',
                  background: 'white',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                  textAlign: 'left'
                }}
              />
            </div>

            {/* Cancel Button (only show when editing) */}
            {editingRouteId && (
              <button
                onClick={() => {
                  setNewRouteName('');
                  setNewRouteOrigin('');
                  setNewRouteDestination('');
                  setNewRouteOriginCoords('');
                  setNewRouteDestinationCoords('');
                  setEditingRouteId(null);
                }}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '20px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#6b7280',
                  marginBottom: '8px',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f9fafb';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'white';
                }}
              >
                Cancel Edit
              </button>
            )}

            {/* Save/Update Button */}
            <button
onClick={async () => {
  if (!firestoreService) {
    alert('Service not ready. Please wait a moment and try again.');
    return;
  }
  if (!newRouteName || !newRouteOrigin || !newRouteDestination) {
    alert('Please fill in all fields');
    return;
  }

  try {
    const button = document.activeElement;
    const originalText = button.textContent;
    button.textContent = 'Validating...';
    button.disabled = true;
    button.style.opacity = '0.7';

    const { geocodeAddress, reverseGeocode } = await import('./trafficHandler');

    let originCoords = newRouteOriginCoords || '';
    let originDisplayAddress = newRouteOrigin;

    if (!originCoords) {
      const coordsPattern = /^-?\d+\.?\d*\s*,\s*-?\d+\.?\d*$/;
      const isCoords = coordsPattern.test(newRouteOrigin.trim());
      
      if (isCoords) {
        originCoords = newRouteOrigin.trim().replace(/\s+/g, '');
        try {
          const address = await reverseGeocode(originCoords);
          originDisplayAddress = address !== originCoords ? address : 'Location at ' + originCoords;
        } catch (err) {
          originDisplayAddress = 'Location at ' + originCoords;
        }
      } else {
        button.textContent = 'Finding location...';
        originCoords = await geocodeAddress(newRouteOrigin);
        
        if (!originCoords) {
          button.textContent = originalText;
          button.disabled = false;
          button.style.opacity = '1';
          alert('Could not find starting location. Please check the address.');
          return;
        }
        originDisplayAddress = newRouteOrigin;
      }
    }

    let destinationCoords = newRouteDestinationCoords || '';
    let destinationDisplayAddress = newRouteDestination;

    if (!destinationCoords) {
      const coordsPattern = /^-?\d+\.?\d*\s*,\s*-?\d+\.?\d*$/;
      const isCoords = coordsPattern.test(newRouteDestination.trim());
      
      if (isCoords) {
        destinationCoords = newRouteDestination.trim().replace(/\s+/g, '');
        try {
          const address = await reverseGeocode(destinationCoords);
          destinationDisplayAddress = address !== destinationCoords ? address : 'Location at ' + destinationCoords;
        } catch (err) {
          destinationDisplayAddress = 'Location at ' + destinationCoords;
        }
      } else {
        button.textContent = 'Finding destination...';
        destinationCoords = await geocodeAddress(newRouteDestination);
        
        if (!destinationCoords) {
          button.textContent = originalText;
          button.disabled = false;
          button.style.opacity = '1';
          alert('Could not find destination. Please check the address.');
          return;
        }
        destinationDisplayAddress = newRouteDestination;
      }
    }

    const validateCoords = (coords, label) => {
      const parts = coords.split(',');
      if (parts.length !== 2) throw new Error('Invalid format');
      
      const [lat, lng] = parts.map(s => parseFloat(s.trim()));
      if (isNaN(lat) || isNaN(lng)) throw new Error('Invalid coordinates');
      if (lat < -90 || lat > 90 || lng < -180 || lng > 180) throw new Error('Out of range');
      
      return true;
    };

    try {
      validateCoords(originCoords, 'Origin');
      validateCoords(destinationCoords, 'Destination');
    } catch (validationError) {
      button.textContent = originalText;
      button.disabled = false;
      button.style.opacity = '1';
      alert('Invalid address format. Please try again.');
      return;
    }

    if (!editingRouteId) {
      const existingRoutes = await firestoreService.getTrafficRoutes();
      const duplicateRoute = existingRoutes.find(r => 
        r.name.toLowerCase() === newRouteName.toLowerCase() ||
        (r.originCoords === originCoords && r.destCoords === destinationCoords)
      );

      if (duplicateRoute) {
        button.textContent = originalText;
        button.disabled = false;
        button.style.opacity = '1';
        
        const overwrite = window.confirm('Route "' + duplicateRoute.name + '" already exists. Save anyway?');
        if (!overwrite) return;
      }
    }

    // ✅ Parse coordinates into separate lat/lon fields
    const [originLat, originLon] = originCoords.split(',').map(s => parseFloat(s.trim()));
    const [destLat, destLon] = destinationCoords.split(',').map(s => parseFloat(s.trim()));

    if (editingRouteId) {
      button.textContent = 'Updating...';
      
      // Update in Firestore with complete coordinate data
      await firestoreService.updateTrafficRoute(editingRouteId, {
        name: newRouteName.trim(),
        origin: originDisplayAddress,
        destination: destinationDisplayAddress,
        originCoords: originCoords,
        destCoords: destinationCoords,
        originLat: originLat,
        originLon: originLon,
        destLat: destLat,
        destLon: destLon
      });
      
      // Create the updated route object for local state
      const updatedRoute = {
        id: editingRouteId,
        name: newRouteName.trim(),
        origin: originDisplayAddress,
        destination: destinationDisplayAddress,
        originCoords: originCoords,
        destCoords: destinationCoords,
        originLat: originLat,
        originLon: originLon,
        destLat: destLat,
        destLon: destLon
      };
      
      // Update local state
      setSavedRoutes(prev => prev.map(r => 
        r.id === editingRouteId ? updatedRoute : r
      ));
      
      alert('Route updated: ' + newRouteName);
      setEditingRouteId(null);
      
    } else {
      button.textContent = 'Saving...';
      
      // Save new route with complete coordinate data
      const savedRoute = await firestoreService.saveTrafficRoute({
        name: newRouteName.trim(),
        origin: originDisplayAddress,
        destination: destinationDisplayAddress,
        originCoords: originCoords,
        destCoords: destinationCoords,
        originLat: originLat,
        originLon: originLon,
        destLat: destLat,
        destLon: destLon
      });
      
      setSavedRoutes(prev => [...prev, savedRoute]);
      
      alert('Route saved: ' + newRouteName);
      
      // Auto-connect traffic module if first route
      if (savedRoutes.length === 0 && !connectedModules.includes('traffic')) {
        setConnectedModules(prev => [...prev, 'traffic']);
      }
    }

    // ✅ Clear form fields
    setNewRouteName('');
    setNewRouteOrigin('');
    setNewRouteDestination('');
    setNewRouteOriginCoords('');
    setNewRouteDestinationCoords('');
    
    // ✅ Close modal
// ✅ Close modal
setShowRouteSetup(false);
setShowTrafficRouteSetup(false);
setShowRouteSetupFromDailyBrief(false); // ✅ ADD THIS LINE
    // ✅ Reset button state
    button.textContent = originalText;
    button.disabled = false;
    button.style.opacity = '1';
    
  } catch (error) {
    console.error('Error saving route:', error);
    
    const button = document.activeElement;
    if (button) {
      button.textContent = editingRouteId ? 'Update Route' : 'Save Route';
      button.disabled = false;
      button.style.opacity = '1';
    }
    
    alert('Failed to save route. Please try again.');
  }
}}              style={{
                width: '100%',
                padding: '12px',
                background: '#0c0c0cff',
                border: 'none',
                borderRadius: '20px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                color: 'white',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                if (!e.currentTarget.disabled) {
                  e.currentTarget.style.background = '#1a1a1a';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.2)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#272727';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
              }}
            >
              {editingRouteId ? 'Update Route' : 'Save Route'}
            </button>
          </div>
        </div>
      </div>
    </div>

    {/* Add animations */}
    <style>{`
      @keyframes slideUp {
        from {
          transform: translateY(100%);
          opacity: 0;
        }
        to {
          transform: translateY(0);
          opacity: 1;
        }
      }
      
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
    `}</style>
  </div>
)}


{showSettings && (
  <div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    zIndex: 9999,
    display: 'flex',
    alignItems: 'flex-end',
  }}>
    <div style={{
      width: '100%',
      height: '85vh',
      background: 'white',
      borderRadius: '24px 24px 0 0',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      zIndex: 10000,
    }}>
      
      {/* Header */}
      <div style={{
        padding: '20px 20px 16px 20px',
        borderBottom: '1px solid #e5e7eb',
        position: 'relative',
        zIndex: 1,
        background: "#fafafa"
      }}>
        <button
          onClick={() => setShowSettings(false)}
          style={{
            background: "white",
            border: "1px solid #e5e7eb",
            borderRadius: "50%",
            width: '30px',
            height: '30px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            marginBottom: '16px'
          }}
        >
          <X size={17} color="#807f7e" />
        </button>

        {/* Title and Description */}
        <div style={{ textAlign: 'left' }}>
          <h2 style={{
            fontSize: '16px',
            fontWeight: '500',
            color: 'black',
            marginBottom: '6px',
            margin: 0,
          }}>
            Settings
          </h2>
          <p style={{
            fontSize: '13px',
            color: '#9ca3af',
            margin: '4px 0 0 0',
            lineHeight: '1.5'
          }}>
            Customize your podcast experience
          </p>
        </div>
      </div>

      {/* Content Area - Scrollable */}
{/* Content Area - Scrollable */}
<div style={{
  flex: 1,
  overflowY: 'auto',
  padding: '24px',
  position: 'relative',
  zIndex: 2
}}>
  


  {/* News Sources Section */}
  <div style={{ marginBottom: "32px" }}>
    <label style={{
      fontSize: "14px",
      fontWeight: "600",
      color: "#1f2937",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      marginBottom: "16px"
    }}>
      <Newspaper size={16} color="#ef4444" />
      News & Media Sources
    </label>
    
    {/* Category Tabs */}
    <div style={{
      display: 'flex',
      gap: '8px',
      marginBottom: '16px',
      overflowX: 'auto',
      paddingBottom: '8px',
      scrollbarWidth: 'none'
    }}>
      {Object.keys(newsSources).map(category => {
        const categoryLabels = {
          news: 'General News',
          sports: 'Sports',
          tech: 'Technology',
          business: 'Business',
          entertainment: 'Entertainment'
        };
        
        const isActive = selectedNewsCategory === category;
        
        const getCategoryIcon = (cat) => {
          switch(cat) {
            case 'news':
              return (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                </svg>
              );
            case 'sports':
              return (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 2a14.5 14.5 0 0 0 0 20m0-20a14.5 14.5 0 0 1 0 20M2 12h20" />
                </svg>
              );
            case 'tech':
              return (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 21h8M12 17v4" />
                </svg>
              );
            case 'business':
              return (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                </svg>
              );
            case 'entertainment':
              return (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="2" y="7" width="20" height="15" rx="2" ry="2" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16 3l-4 4-4-4" />
                </svg>
              );
            default:
              return null;
          }
        };
        
        return (
          <button
            key={category}
            onClick={() => setSelectedNewsCategory(category)}
            style={{
              padding: '10px 18px',
              background: isActive ? '#000000' : 'white',
              border: isActive ? 'none' : '1px solid #e5e7eb',
              borderRadius: '20px',
              fontSize: '11px',
              fontWeight: '500',
              color: isActive ? 'white' : '#6b7280',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              boxShadow: isActive ? '0 2px 8px rgba(0,0,0,0.15)' : '0 1px 3px rgba(0,0,0,0.05)'
            }}
          >
            {getCategoryIcon(category)}
            <span>{categoryLabels[category]}</span>
            {selectedCategories[category]?.length > 0 && (
              <span style={{
                background: isActive ? 'rgba(255, 255, 255, 0.2)' : '#f3f4f6',
                padding: '2px 6px',
                borderRadius: '10px',
                fontSize: '10px',
                fontWeight: '600'
              }}>
                {selectedCategories[category].length}
              </span>
            )}
          </button>
        );
      })}
    </div>
    
    {/* Sources Grid for Selected Category */}
    <div style={{
      display: "grid",
      gridTemplateColumns: "repeat(3, 1fr)",
      gap: "16px",
      padding: "4px",
      minHeight: '180px',
      transition: 'all 0.3s'
    }}>
{newsSources[selectedNewsCategory]?.map(source => {
  const isSelected = selectedCategories[selectedNewsCategory]?.includes(source.id);
  const isClicked = clickedSource === source.id;
  
  return (
    <button
      key={source.id}
      onClick={() => handleToggleNewsSource(selectedNewsCategory, source.id)}
      style={{
        padding: "0",
        background: "transparent",
        border: "none",
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "8px",
        position: "relative",
        // ✅ Animation when clicked
        transform: isClicked ? 'scale(0.9)' : 'scale(1)',
        transition: 'transform 0.2s ease-out'
      }}
    >
            <div style={{
              width: "56px",
              height: "56px",
              borderRadius: "50%",
              background: "#f9fafb",
              border: isSelected ? "2px solid #000000" : "1px solid #e5e7eb",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.2s",
              position: "relative",
              boxShadow: isSelected ? '0 4px 12px rgba(0,0,0,0.1)' : '0 2px 4px rgba(0,0,0,0.05)'
            }}>
              <img 
                src={source.favicon} 
                alt={source.name}
                style={{ width: "30px", height: "30px", objectFit: "contain" }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = '<div style="width:30px;height:30px;background:#e5e7eb;border-radius:8px;display:flex;align-items:center;justify-content:center;font-size:16px;">📰</div>';
                }}
              />
            </div>
            
            {isSelected && (
              <div style={{
                position: "absolute",
                top: "-4px",
                right: "12px",
                width: "22px",
                height: "22px",
                borderRadius: "50%",
                background: "#000000",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "2px solid white",
                zIndex: 10,
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
              }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                  <polyline points="20 6 9 17 4 12" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            )}
            
            <div style={{ 
              fontSize: "11px", 
              fontWeight: "500", 
              color: "#6b7280",
              maxWidth: '70px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              textAlign: 'center'
            }}>
              {source.name}
            </div>
          </button>
        );
      })}
    </div>
    
    {/* Selection Summary */}
    <div style={{
      marginTop: '20px',
      padding: '12px 18px',
      background: 'rgba(185, 185, 185, 0.05)',
      border: '1px dashed rgba(156, 156, 156, 0.15)',
      borderRadius: '20px',
      fontSize: '12px',
      color: '#4b5563',
      textAlign: 'left'
    }}>
      <strong>Total selected:</strong> {Object.values(selectedCategories).flat().length} sources across {Object.values(selectedCategories).filter(arr => arr.length > 0).length} categories
    </div>
  </div>

  {/* Stock Selection */}
  <div style={{ marginBottom: "24px" }}>
    <label style={{
      fontSize: "14px",
      fontWeight: "600",
      color: "#1f2937",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      marginBottom: "16px"
    }}>
      <TrendingUp size={16} color="#10b981" />
      Stock Tickers
    </label>
<div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "16px", padding: "4px" }}>
  {[
    { 
      ticker: 'AAPL', 
      logos: [
        'https://logo.clearbit.com/apple.com',
        'https://upload.wikimedia.org/wikipedia/commons/f/fa/Apple_logo_black.svg',
        'https://companiesmarketcap.com/img/company-logos/64/AAPL.webp'
      ]
    },
    { 
      ticker: 'SPX', 
      logos: [
        'https://upload.wikimedia.org/wikipedia/commons/thumb/4/44/Standard_%26_Poor%27s_logo.svg/512px-Standard_%26_Poor%27s_logo.svg.png',
        'https://companiesmarketcap.com/img/company-logos/64/SPY.webp',
        'https://assets.parqet.com/logos/symbol/SPY'
      ]
    },
    { 
      ticker: 'MSFT', 
      logos: [
        'https://logo.clearbit.com/microsoft.com',
        'https://upload.wikimedia.org/wikipedia/commons/4/44/Microsoft_logo.svg',
        'https://companiesmarketcap.com/img/company-logos/64/MSFT.webp'
      ]
    },
    { 
      ticker: 'GOOGL', 
      logos: [
        'https://logo.clearbit.com/google.com',
        'https://upload.wikimedia.org/wikipedia/commons/2/2f/Google_2015_logo.svg',
        'https://companiesmarketcap.com/img/company-logos/64/GOOGL.webp'
      ]
    },
    { 
      ticker: 'AMZN', 
      logos: [
        'https://logo.clearbit.com/amazon.com',
        'https://upload.wikimedia.org/wikipedia/commons/a/a9/Amazon_logo.svg',
        'https://companiesmarketcap.com/img/company-logos/64/AMZN.webp'
      ]
    },
    { 
      ticker: 'TSLA', 
      logos: [
        'https://logo.clearbit.com/tesla.com',
        'https://upload.wikimedia.org/wikipedia/commons/b/bd/Tesla_Motors.svg',
        'https://companiesmarketcap.com/img/company-logos/64/TSLA.webp'
      ]
    },
    { 
      ticker: 'META', 
      logos: [
        'https://logo.clearbit.com/meta.com',
        'https://upload.wikimedia.org/wikipedia/commons/7/7b/Meta_Platforms_Inc._logo.svg',
        'https://companiesmarketcap.com/img/company-logos/64/META.webp'
      ]
    },
    { 
      ticker: 'NVDA', 
      logos: [
        'https://logo.clearbit.com/nvidia.com',
        'https://upload.wikimedia.org/wikipedia/sco/2/21/Nvidia_logo.svg',
        'https://companiesmarketcap.com/img/company-logos/64/NVDA.webp'
      ]
    },
    { 
      ticker: 'JPM', 
      logos: [
        'https://logo.clearbit.com/jpmorganchase.com',
        'https://upload.wikimedia.org/wikipedia/commons/4/44/JP_Morgan_Chase_Logo_2008.svg',
        'https://companiesmarketcap.com/img/company-logos/64/JPM.webp'
      ]
    },
    { 
      ticker: 'V', 
      logos: [
        'https://logo.clearbit.com/visa.com',
        'https://upload.wikimedia.org/wikipedia/commons/0/04/Visa.svg',
        'https://companiesmarketcap.com/img/company-logos/64/V.webp'
      ]
    },
    { 
      ticker: 'WMT', 
      logos: [
        'https://logo.clearbit.com/walmart.com',
        'https://upload.wikimedia.org/wikipedia/commons/c/ca/Walmart_logo.svg',
        'https://companiesmarketcap.com/img/company-logos/64/WMT.webp'
      ]
    }
  ].map(stock => {
    const isSelected = selectedStocks.includes(stock.ticker);
    
    return (
      <button
        key={stock.ticker}
        onClick={() => {
          if (isSelected && selectedStocks.length <= 2) {
            alert('You must keep at least 2 stocks selected');
            return;
          }
          setSelectedStocks(prev =>
            isSelected
              ? prev.filter(t => t !== stock.ticker)
              : [...prev, stock.ticker]
          );
        }}
        style={{
          padding: "0",
          background: "transparent",
          border: "none",
          cursor: "pointer",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "8px",
          position: "relative"
        }}
      >
        <div style={{
          width: "56px",
          height: "56px",
          borderRadius: "50%",
          background: "#f9fafb",
          border: isSelected ? "2px solid #000000" : "1px solid #e5e7eb",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "all 0.2s",
          position: "relative",
          boxShadow: isSelected ? '0 4px 12px rgba(0,0,0,0.1)' : '0 2px 4px rgba(0,0,0,0.05)'
        }}>
          <img 
            src={stock.logos[0]} 
            alt={stock.ticker}
            style={{ width: "30px", height: "30px", objectFit: "contain" }}
            data-fallback-index="0"
            data-ticker={stock.ticker}
            onError={(e) => {
              const currentIndex = parseInt(e.target.getAttribute('data-fallback-index') || '0');
              const ticker = e.target.getAttribute('data-ticker');
              
              // Find stock data from the parent array
              const allStocks = [
                { ticker: 'AAPL', logos: ['https://logo.clearbit.com/apple.com', 'https://upload.wikimedia.org/wikipedia/commons/f/fa/Apple_logo_black.svg', 'https://companiesmarketcap.com/img/company-logos/64/AAPL.webp'] },
                { ticker: 'SPX', logos: ['https://upload.wikimedia.org/wikipedia/commons/thumb/4/44/Standard_%26_Poor%27s_logo.svg/512px-Standard_%26_Poor%27s_logo.svg.png', 'https://companiesmarketcap.com/img/company-logos/64/SPY.webp', 'https://assets.parqet.com/logos/symbol/SPY'] },
                { ticker: 'MSFT', logos: ['https://logo.clearbit.com/microsoft.com', 'https://upload.wikimedia.org/wikipedia/commons/4/44/Microsoft_logo.svg', 'https://companiesmarketcap.com/img/company-logos/64/MSFT.webp'] },
                { ticker: 'GOOGL', logos: ['https://logo.clearbit.com/google.com', 'https://upload.wikimedia.org/wikipedia/commons/2/2f/Google_2015_logo.svg', 'https://companiesmarketcap.com/img/company-logos/64/GOOGL.webp'] },
                { ticker: 'AMZN', logos: ['https://logo.clearbit.com/amazon.com', 'https://upload.wikimedia.org/wikipedia/commons/a/a9/Amazon_logo.svg', 'https://companiesmarketcap.com/img/company-logos/64/AMZN.webp'] },
                { ticker: 'TSLA', logos: ['https://logo.clearbit.com/tesla.com', 'https://upload.wikimedia.org/wikipedia/commons/b/bd/Tesla_Motors.svg', 'https://companiesmarketcap.com/img/company-logos/64/TSLA.webp'] },
                { ticker: 'META', logos: ['https://logo.clearbit.com/meta.com', 'https://upload.wikimedia.org/wikipedia/commons/7/7b/Meta_Platforms_Inc._logo.svg', 'https://companiesmarketcap.com/img/company-logos/64/META.webp'] },
                { ticker: 'NVDA', logos: ['https://logo.clearbit.com/nvidia.com', 'https://upload.wikimedia.org/wikipedia/sco/2/21/Nvidia_logo.svg', 'https://companiesmarketcap.com/img/company-logos/64/NVDA.webp'] },
                { ticker: 'JPM', logos: ['https://logo.clearbit.com/jpmorganchase.com', 'https://upload.wikimedia.org/wikipedia/commons/4/44/JP_Morgan_Chase_Logo_2008.svg', 'https://companiesmarketcap.com/img/company-logos/64/JPM.webp'] },
                { ticker: 'V', logos: ['https://logo.clearbit.com/visa.com', 'https://upload.wikimedia.org/wikipedia/commons/0/04/Visa.svg', 'https://companiesmarketcap.com/img/company-logos/64/V.webp'] },
                { ticker: 'WMT', logos: ['https://logo.clearbit.com/walmart.com', 'https://upload.wikimedia.org/wikipedia/commons/c/ca/Walmart_logo.svg', 'https://companiesmarketcap.com/img/company-logos/64/WMT.webp'] }
              ];
              
              const stockData = allStocks.find(s => s.ticker === ticker);
              if (!stockData) return;
              
              const nextIndex = currentIndex + 1;
              
              // Try next fallback
              if (nextIndex < stockData.logos.length) {
                console.log(`${ticker}: Trying fallback ${nextIndex + 1}/${stockData.logos.length}`);
                e.target.src = stockData.logos[nextIndex];
                e.target.setAttribute('data-fallback-index', nextIndex.toString());
              } else {
                // All fallbacks failed - show ticker letter
                console.log(`${ticker}: All logos failed, showing letter badge`);
                e.target.style.display = 'none';
                e.target.parentElement.innerHTML = `<div style="width:30px;height:30px;background:#10b981;border-radius:8px;display:flex;align-items:center;justify-content:center;font-size:14px;font-weight:700;color:white;">${ticker.charAt(0)}</div>`;
              }
            }}
          />
        </div>
        
        {isSelected && (
          <div style={{
            position: "absolute",
            top: "-4px",
            right: "4px",
            width: "22px",
            height: "22px",
            borderRadius: "50%",
            background: "#000000",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "2px solid white",
            zIndex: 10,
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
          }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
              <polyline points="20 6 9 17 4 12" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        )}
        
        <div style={{ 
          fontSize: "11px", 
          fontWeight: "500", 
          color: "#6b7280",
          textAlign: 'center'
        }}>
          {stock.ticker}
        </div>
      </button>
    );
  })}
</div>  
     <div style={{
   background: 'rgba(185, 185, 185, 0.05)',
      border: '1px dashed rgba(156, 156, 156, 0.15)',
      borderRadius: '20px',
      padding: '12px',
      marginBottom: '16px',
      marginTop: '16px',
      textAlign: 'left'
    }}>
      <div style={{
        fontSize: '11px',
        color: '#6b7280',
        lineHeight: '1.4'
      }}>
        Select at least 2 stocks to track
      </div>
    </div>
  </div>

{/* Crypto Selection Section */}
{showCryptoCard && (
  <div style={{ marginBottom: "24px" }}>
    <label style={{
      fontSize: "14px",
      fontWeight: "600",
      color: "#1f2937",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      marginBottom: "16px"
    }}>
      <DollarSign size={16} color="#1f2937" />
      Cryptocurrency Selection
    </label>
    
    <div style={{
      background: 'rgba(0, 0, 0, 0.03)',
      border: '1px dashed #d1d5db',
      borderRadius: '16px',
      padding: '12px',
      marginBottom: '16px',
      textAlign: 'center'
    }}>
      <div style={{
        fontSize: '11px',
        color: '#6b7280',
        lineHeight: '1.4'
      }}>
        Select at least 2 cryptocurrencies to track
      </div>
    </div>
    
    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "16px", padding: "4px" }}>
      {[
        { id: 'bitcoin', name: 'BTC', logo: 'https://assets.coingecko.com/coins/images/1/small/bitcoin.png' },
        { id: 'ethereum', name: 'ETH', logo: 'https://assets.coingecko.com/coins/images/279/small/ethereum.png' },
        { id: 'binancecoin', name: 'BNB', logo: 'https://assets.coingecko.com/coins/images/825/small/bnb-icon2_2x.png' },
        { id: 'cardano', name: 'ADA', logo: 'https://assets.coingecko.com/coins/images/975/small/cardano.png' },
        { id: 'solana', name: 'SOL', logo: 'https://assets.coingecko.com/coins/images/4128/small/solana.png' },
        { id: 'ripple', name: 'XRP', logo: 'https://assets.coingecko.com/coins/images/44/small/xrp-symbol-white-128.png' },
        { id: 'polkadot', name: 'DOT', logo: 'https://assets.coingecko.com/coins/images/12171/small/polkadot.png' },
        { id: 'dogecoin', name: 'DOGE', logo: 'https://assets.coingecko.com/coins/images/5/small/dogecoin.png' }
      ].map(crypto => {
        const isSelected = selectedCryptos.includes(crypto.id);
        
        return (
          <button
            key={crypto.id}
            onClick={() => {
              if (isSelected && selectedCryptos.length <= 2) {
                alert('You must keep at least 2 cryptocurrencies selected');
                return;
              }
              setSelectedCryptos(prev =>
                isSelected
                  ? prev.filter(c => c !== crypto.id)
                  : [...prev, crypto.id]
              );
            }}
            style={{
              padding: "0",
              background: "transparent",
              border: "none",
              cursor: "pointer",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "8px",
              position: "relative"
            }}
          >
            <div style={{
              width: "56px",
              height: "56px",
              borderRadius: "50%",
              background: "#f9fafb",
              border: isSelected ? "2px solid #000000" : "1px solid #e5e7eb",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.2s",
              position: "relative",
              boxShadow: isSelected ? '0 4px 12px rgba(0,0,0,0.1)' : '0 2px 4px rgba(0,0,0,0.05)'
            }}>
              <img 
                src={crypto.logo} 
                alt={crypto.name}
                style={{ width: "30px", height: "30px", objectFit: "contain" }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = '<div style="width:30px;height:30px;background:#1f2937;border-radius:8px;"></div>';
                }}
              />
            </div>
            
            {isSelected && (
              <div style={{
                position: "absolute",
                top: "-4px",
                right: "4px",
                width: "22px",
                height: "22px",
                borderRadius: "50%",
                background: "#000000",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "2px solid white",
                zIndex: 10,
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
              }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                  <polyline points="20 6 9 17 4 12" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            )}
            
            <div style={{ 
              fontSize: "11px", 
              fontWeight: "500", 
              color: "#6b7280",
              textAlign: 'center'
            }}>
              {crypto.name}
            </div>
          </button>
        );
      })}
    </div>
  </div>
)}


  {/* Divider */}
  <div style={{
    height: '1px',
    background: 'linear-gradient(90deg, transparent, #e5e7eb, transparent)',
    marginBottom: '32px'
  }} />
        
        {/* Setup Routes Button */}
        <div style={{ marginBottom: "24px" }}>
              <label style={{
      fontSize: "14px",
      fontWeight: "600",
      color: "#1f2937",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      marginBottom: "16px"
    }}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
      </svg>
      Your Commute
    </label>
          <button
            onClick={() => {
              setShowRouteSetup(true);
              setShowSettings(false);
            }}
            style={{
              width: "100%",
              padding: "16px",
              background: "white",
              border: "1px solid #e5e7eb",
              borderRadius: "20px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "12px",
              transition: "all 0.2s",
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = "#f9fafb"}
            onMouseLeave={(e) => e.currentTarget.style.background = "white"}
          >
            <div style={{
              width: "40px",
              height: "40px",
              borderRadius: "20px",
              background: "#fef3c7",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0
            }}>
              <Car size={15} color="#f59e0b" />
            </div>
            
            <div style={{ flex: 1, textAlign: "left" }}>
              <div style={{ fontSize: "14px", fontWeight: "600", color: "#1f2937", marginBottom: "2px" }}>
                Traffic Routes
              </div>
              <div style={{ fontSize: "12px", color: "#9ca3af" }}>
                Set up your commute routes
              </div>
            </div>
            
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

  {/* ✅ Card Visibility Section */}
  <div style={{ marginBottom: "32px" }}>
    <h3 style={{
      fontSize: "14px",
      fontWeight: "600",
      color: "#1f2937",
      marginBottom: "16px",
      display: "flex",
      alignItems: "center",
      gap: "8px"
    }}>
  
      Card Visibility
    </h3>


{/* News Card Toggle */}
<div style={{
  background: 'white',
  border: '1px solid #e5e7eb',
  borderRadius: '16px',
  padding: '16px',
  marginBottom: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between'
}}>
  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
    <div style={{
      width: '40px',
      height: '40px',
      borderRadius: '20px',
      background: showNewsCard ? '#ffe7e0ff' : '#f3f4f6',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <Newspaper size={15} color={showNewsCard ? '#ef4444' : '#9ca3af'} />
    </div>
    <div style={{ textAlign: 'left' }}>
      <div style={{ fontSize: '13px', fontWeight: '600', color: '#1f2937' }}>
        News Card
      </div>
      <div style={{ fontSize: '11px', color: '#9ca3af' }}>
        Show news stories from your sources
      </div>
    </div>
  </div>
  
  {/* SINGLE TOGGLE - DO NOT DUPLICATE */}
  <button
    onClick={() => {
      const activeCards = [showNewsCard, showStocksCard, showCryptoCard].filter(Boolean).length;
      if (showNewsCard && activeCards === 1) {
        alert('At least one card must remain visible');
        return;
      }
      setShowNewsCard(!showNewsCard);
    }}
    style={{
      width: '48px',
      height: '26px',
      borderRadius: '13px',
      background: showNewsCard ? '#000000' : '#d1d5db',
      border: 'none',
      cursor: 'pointer',
      position: 'relative',
      transition: 'all 0.2s',
      padding: 0
    }}
  >
    <div style={{
      width: '20px',
      height: '20px',
      borderRadius: '50%',
      background: 'white',
      position: 'absolute',
      top: '3px',
      left: showNewsCard ? '25px' : '3px',
      transition: 'all 0.2s',
      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
    }} />
  </button>
</div>


    {/* Stocks Card Toggle */}
    <div style={{
      background: 'white',
      border: '1px solid #e5e7eb',
      borderRadius: '16px',
      padding: '16px',
      marginBottom: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{
          width: '40px',
          height: '40px',
          borderRadius: '20px',
          background: showStocksCard ? '#dcfce7' : '#f3f4f6',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <TrendingUp size={15} color={showStocksCard ? '#10b981' : '#9ca3af'} />
        </div>
        <div style={{ textAlign: 'left' }}>
          <div style={{ fontSize: '13px', fontWeight: '600', color: '#1f2937' }}>
            Stocks Card
          </div>
          <div style={{ fontSize: '11px', color: '#9ca3af' }}>
            Show stock market updates
          </div>
        </div>
      </div>
      
      <button
        onClick={() => setShowStocksCard(!showStocksCard)}
        style={{
          width: '48px',
          height: '26px',
          borderRadius: '13px',
          background: showStocksCard ? '#000000' : '#d1d5db',
          border: 'none',
          cursor: 'pointer',
          position: 'relative',
          transition: 'all 0.2s',
          padding: 0
        }}
      >
        <div style={{
          width: '20px',
          height: '20px',
          borderRadius: '50%',
          background: 'white',
          position: 'absolute',
          top: '3px',
          left: showStocksCard ? '25px' : '3px',
          transition: 'all 0.2s',
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
        }} />
      </button>
    </div>

    {/* Crypto Card Toggle */}
    <div style={{
      background: 'white',
      border: '1px solid #e5e7eb',
      borderRadius: '16px',
      padding: '16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{
          width: '40px',
          height: '40px',
          borderRadius: '20px',
          background: showCryptoCard ? '#dbeafe' : '#f3f4f6',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={showCryptoCard ? '#3b82f6' : '#9ca3af'} strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <path d="M12 6v12M15 9H9.5a2.5 2.5 0 0 0 0 5h5a2.5 2.5 0 0 1 0 5H9"/>
          </svg>
        </div>
        <div style={{ textAlign: 'left' }}>
          <div style={{ fontSize: '13px', fontWeight: '600', color: '#1f2937' }}>
            Crypto Card
          </div>
          <div style={{ fontSize: '11px', color: '#9ca3af' }}>
            Show cryptocurrency prices
          </div>
        </div>
      </div>
      
      <button
        onClick={() => setShowCryptoCard(!showCryptoCard)}
        style={{
          width: '48px',
          height: '26px',
          borderRadius: '13px',
          background: showCryptoCard ? '#000000' : '#d1d5db',
          border: 'none',
          cursor: 'pointer',
          position: 'relative',
          transition: 'all 0.2s',
          padding: 0
        }}
      >
        <div style={{
          width: '20px',
          height: '20px',
          borderRadius: '50%',
          background: 'white',
          position: 'absolute',
          top: '3px',
          left: showCryptoCard ? '25px' : '3px',
          transition: 'all 0.2s',
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
        }} />
      </button>
    </div>
  </div>

</div>

      {/* Footer - Fixed at Bottom */}
      <div style={{
        padding: "20px 24px",
        borderTop: "1px solid #e5e7eb",
        background: "white"
      }}>
        <button
          onClick={handleSaveSettings}
          style={{
            width: "100%",
            background: "#000000",
            color: "white",
            border: "none",
            borderRadius: "24px",
            padding: "14px",
            fontSize: "14px",
            fontWeight: "600",
            cursor: "pointer",
            transition: "all 0.2s",
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
          }}
        >
          Save Changes
        </button>
      </div>
    </div>
  </div>
)}
{/* Delete Confirmation Dialog */}
{deleteConfirm && (
  <div
    onClick={() => setDeleteConfirm(null)}
    style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      backdropFilter: 'blur(4px)',
      zIndex: 2001,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      animation: 'fadeIn 0.2s ease-out'
    }}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        background: 'white',
        borderRadius: '20px',
        padding: '24px',
        maxWidth: '400px',
        width: '100%',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        animation: 'slideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
      }}
    >

      <h3 style={{
        fontSize: '16px',
        fontWeight: '500',
        color: '#111827',
        textAlign: 'center',
        marginBottom: '8px'
      }}>
        Delete Route?
      </h3>

      <p style={{
        fontSize: '12px',
        color: '#6b7280',
        textAlign: 'center',
        marginBottom: '24px',
        lineHeight: '1.5'
      }}>
        Are you sure you want to delete "<strong>{deleteConfirm.name}</strong>"? This action cannot be undone.
      </p>

      <div style={{
        display: 'flex',
        gap: '12px'
      }}>
        <button
          onClick={() => setDeleteConfirm(null)}
          style={{
            flex: 1,
            padding: '10px',
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '20px',
            fontSize: '12px',
            fontWeight: '500',
            color: '#374151',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#f9fafb';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'white';
          }}
        >
          Cancel
        </button>
<button
  onClick={async () => {
    try {
      // ✅ Delete from Firestore
      await firestoreService.deleteTrafficRoute(deleteConfirm.id);
      
      // Update local state
      setSavedRoutes(prev => prev.filter(r => r.id !== deleteConfirm.id));
      
      // If no routes left, remove traffic module
      if (savedRoutes.length === 1) { // Will be 0 after this delete
        setConnectedModules(prev => prev.filter(id => id !== 'traffic'));
      }
      
      setDeleteConfirm(null);
      
      console.log('✅ Route deleted from Firestore');
    } catch (error) {
      console.error('❌ Failed to delete route:', error);
      alert('Failed to delete route. Please try again.');
    }
  }}
  style={{
    flex: 1,
    padding: '10px',
    background: '#ef4444',
    border: 'none',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '500',
    color: 'white',
    cursor: 'pointer',
    transition: 'all 0.2s'
  }}
>
  Delete Route
</button>
      </div>
    </div>
  </div>
)}
{/* Route Selector Modal - NEW */}
{showRouteSelector && (
  <div
    onClick={() => {
      setShowRouteSelector(false);
      setSelectedRouteForPodcast(null);
    }}
    style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.6)',
      backdropFilter: 'blur(4px)',
      zIndex: 10000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      animation: 'fadeIn 0.2s ease-out' /* ← ADD THIS */
    }}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        background: 'white',
        borderRadius: '20px',
        width: '100%',
        maxWidth: '450px',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        animation: 'scaleIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)', /* ← ADD THIS */
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <div style={{
        padding: '20px 24px',
        borderBottom: '1px solid #e5e7eb',
        background: '#fafafa'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '12px',
            background: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
          }}>
            <Car size={22} color="#f59e0b" />
          </div>
          <div style={{ textAlign: 'left', flex: 1 }}>
            <h3 style={{ fontSize: '16px', margin: 0, color: '#111', fontWeight: '600' }}>
              Select Traffic Route
            </h3>
            <p style={{ fontSize: '12px', color: '#9ca3af', margin: '4px 0 0 0' }}>
              Choose which route to include in your podcast
            </p>
          </div>
          <button
            onClick={() => {
              setShowRouteSelector(false);
              setSelectedRouteForPodcast(null);
            }}
            style={{
              background: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X size={18} color="#6b7280" />
          </button>
        </div>
      </div>

      {/* Routes List */}
      <div style={{ padding: '24px', maxHeight: '400px', overflowY: 'auto' }}>
        {savedRoutes.map((route, idx) => {
          const isSelected = selectedRouteForPodcast?.id === route.id;
          
          return (
            <button
              key={route.id}
              onClick={() => setSelectedRouteForPodcast(route)}
              style={{
                width: '100%',
                padding: '16px',
                background: isSelected ? '#fafafa' : 'white',
                border: isSelected ? '2px solid #f59e0b' : '1px solid #e5e7eb',
                borderRadius: '16px',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '12px'
              }}
              onMouseEnter={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.background = '#f9fafb';
                  e.currentTarget.style.borderColor = '#d1d5db';
                }
              }}
              onMouseLeave={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.background = 'white';
                  e.currentTarget.style.borderColor = '#e5e7eb';
                }
              }}
            >
              {/* Radio Button */}
              <div style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                border: isSelected ? '2px solid #f59e0b' : '2px solid #d1d5db',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                background: 'white',
                transition: 'all 0.2s'
              }}>
                {isSelected && (
                  <div style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    background: '#f59e0b'
                  }} />
                )}
              </div>
              
              {/* Route Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#1f2937',
                  marginBottom: '6px'
                }}>
                  {route.name}
                </div>
                
                <div style={{
                  fontSize: '11px',
                  color: '#6b7280',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  overflow: 'hidden'
                }}>
                  <span style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {route.origin}
                  </span>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                  <span style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {route.destination}
                  </span>
                </div>
              </div>
              
              {/* Checkmark */}
              {isSelected && (
                <div style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  background: '#f59e0b',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                    <polyline points="20 6 9 17 4 12" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Footer */}
      <div style={{
        padding: '16px 24px',
        borderTop: '1px solid #e5e7eb',
        background: 'white'
      }}>
        <button
          onClick={() => {
            if (!selectedRouteForPodcast) {
              alert('Please select a route');
              return;
            }
            setShowRouteSelector(false);
            // Trigger generation with selected route
            handleGenerate();
          }}
          disabled={!selectedRouteForPodcast}
          style={{
            width: '100%',
            background: selectedRouteForPodcast ? 'rgba(0, 0, 0, 0.9)' : '#9ca3af',
            color: 'white',
            border: 'none',
            borderRadius: '20px',
            padding: '12px',
            fontSize: '14px',
            fontWeight: '500',
            cursor: selectedRouteForPodcast ? 'pointer' : 'not-allowed',
            transition: 'all 0.2s'
          }}
        >
          {selectedRouteForPodcast ? 'Continue with this Route' : 'Select a Route'}
        </button>
      </div>
    </div>
  </div>
)}
{/* News Type Selector Modal - NEW */}
{showNewsTypeSelector && (
  <div
    onClick={() => {
      setShowNewsTypeSelector(false);
      setSelectedNewsType('general');
    }}
    style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.6)',
      backdropFilter: 'blur(4px)',
      zIndex: 10000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      animation: 'fadeIn 0.2s ease-out' /* ← ADD THIS */
    }}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        background: 'white',
        borderRadius: '20px',
        width: '100%',
        maxWidth: '450px',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        animation: 'scaleIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)', /* ← ADD THIS */
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <div style={{
        padding: '20px 24px',
        borderBottom: '1px solid #e5e7eb',
        background: '#fafafa'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '12px',
            background: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
          }}>
            <Newspaper size={22} color="#ef4444" />
          </div>
          <div style={{ textAlign: 'left', flex: 1 }}>
            <h3 style={{ fontSize: '16px', margin: 0, color: '#111', fontWeight: '600' }}>
              Select News Type
            </h3>
            <p style={{ fontSize: '12px', color: '#9ca3af', margin: '4px 0 0 0' }}>
              Choose between general or local news coverage
            </p>
          </div>
          <button
            onClick={() => {
              setShowNewsTypeSelector(false);
              setSelectedNewsType('general');
            }}
            style={{
              background: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X size={18} color="#6b7280" />
          </button>
        </div>
      </div>

      {/* Options */}
      <div style={{ padding: '24px' }}>
        {/* General News Option */}
        <button
          onClick={() => setSelectedNewsType('general')}
          style={{
            width: '100%',
            padding: '18px',
            background: selectedNewsType === 'general' ? '#fafafa' : 'white',
            border: selectedNewsType === 'general' ? '2px solid #ef4444' : '1px solid #e5e7eb',
            borderRadius: '16px',
            cursor: 'pointer',
            textAlign: 'left',
            transition: 'all 0.2s',
            display: 'flex',
            gap: '14px',
            marginBottom: '14px'
          }}
          onMouseEnter={(e) => {
            if (selectedNewsType !== 'general') {
              e.currentTarget.style.background = '#f9fafb';
              e.currentTarget.style.borderColor = '#d1d5db';
            }
          }}
          onMouseLeave={(e) => {
            if (selectedNewsType !== 'general') {
              e.currentTarget.style.background = 'white';
              e.currentTarget.style.borderColor = '#e5e7eb';
            }
          }}
        >
          {/* Radio Button */}
          <div style={{
            width: '18px',
            height: '18px',
            borderRadius: '50%',
            border: selectedNewsType === 'general' ? '2px solid #ef4444' : '2px solid #d1d5db',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            background: 'white',
            marginTop: '2px'
          }}>
            {selectedNewsType === 'general' && (
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: '#ef4444'
              }} />
            )}
          </div>
          
          {/* Content */}
          <div style={{ flex: 1 }}>
            <div style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#1f2937',
              marginBottom: '6px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
            <TrendingUp size={16} color="#ef4444" />
              General News
            </div>
            <p style={{
              fontSize: '12px',
              color: '#6b7280',
              lineHeight: '1.5',
              margin: 0
            }}>
Catch up on the latest news from CNN, NBC, ABC, etc. all gathered for you in one place.            </p>
          </div>
        </button>

        {/* Local News Option */}
        <button
          onClick={() => setSelectedNewsType('local')}
          style={{
            width: '100%',
            padding: '18px',
            background: selectedNewsType === 'local' ? '#fafafa' : 'white',
            border: selectedNewsType === 'local' ? '2px solid #ef4444' : '1px solid #e5e7eb',
            borderRadius: '16px',
            cursor: 'pointer',
            textAlign: 'left',
            transition: 'all 0.2s',
            display: 'flex',
            gap: '14px'
          }}
          onMouseEnter={(e) => {
            if (selectedNewsType !== 'local') {
              e.currentTarget.style.background = '#f9fafb';
              e.currentTarget.style.borderColor = '#d1d5db';
            }
          }}
          onMouseLeave={(e) => {
            if (selectedNewsType !== 'local') {
              e.currentTarget.style.background = 'white';
              e.currentTarget.style.borderColor = '#e5e7eb';
            }
          }}
        >
          {/* Radio Button */}
          <div style={{
            width: '18px',
            height: '18px',
            borderRadius: '50%',
            border: selectedNewsType === 'local' ? '2px solid #ef4444' : '2px solid #d1d5db',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            background: 'white',
            marginTop: '2px'
          }}>
            {selectedNewsType === 'local' && (
              <div style={{
               width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: '#ef4444'
              }} />
            )}
          </div>
          
          {/* Content */}
          <div style={{ flex: 1 }}>
            <div style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#1f2937',
              marginBottom: '6px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Local News Update
            </div>
            <p style={{
              fontSize: '12px',
              color: '#6b7280',
              lineHeight: '1.5',
              margin: 0
            }}>
Stay in the loop with news happening in your city and nearby areas.            </p>
          </div>
        </button>
      </div>

      {/* Footer */}
      <div style={{
        padding: '16px 24px',
        borderTop: '1px solid #e5e7eb',
        background: 'white'
      }}>
        <button
          onClick={() => {
            // Add news module with selected type
            setConnectedModules(prev => [...prev, 'news']);
            setShowNewsTypeSelector(false);
            console.log('📰 Selected news type:', selectedNewsType);
          }}
          style={{
            width: '100%',
            background: '#000000',
            color: 'white',
            border: 'none',
            borderRadius: '20px',
            padding: '12px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
        >
          Add {selectedNewsType === 'general' ? 'General' : 'Local'} News
        </button>
      </div>
    </div>
  </div>
)}
{/* Create Podcast Popup - WITH STYLE SELECTOR */}
{showCreatePodcastModal && (
  <div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    zIndex: 9999,
    display: 'flex',
    alignItems: 'flex-end',
  }}>
    <div style={{
      width: '100%',
      height: '82vh',
      background: 'white',
      borderRadius: '24px 24px 0 0',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      zIndex: 10000,
    }}>
      
      {/* Header */}
      <div style={{
        padding: '20px 20px 16px 20px',
        borderBottom: '1px solid #e5e7eb',
        position: 'relative',
        zIndex: 1,
        background: "#fafafa",
        marginBottom: '0px'
      }}>
        {/* Close Button */}
        <button
          onClick={() => setShowCreatePodcastModal(false)}
          style={{
            background: "white",
            border: "1px solid #e5e7eb",
            borderRadius: "50%",
            width: '30px',
            height: '30px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            marginBottom: '16px'
          }}
        >
          <X size={17} color="#807f7e" />
        </button>

        {/* Title and Description */}
        <div style={{ textAlign: 'left' }}>
          <h2 style={{
            fontSize: '16px',
            fontWeight: '500',
            color: '#1f2937',
            marginBottom: '6px',
            margin: 0,
          }}>
            Create Your Podcast
          </h2>
          <p style={{
            fontSize: '13px',
            color: '#9ca3af',
            margin: '4px 0 0 0',
            lineHeight: '1.5'
          }}>
            Turn any topic or document into an engaging audio.
          </p>
        </div>
      </div>

      {/* Content Area */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        overflowX: 'visible',
        padding: '24px',
        position: 'relative',
        zIndex: 2
      }}>

        {/* Input Container with SVG */}
        <div style={{
          border: '1px solid #d1d5db',
          borderRadius: '24px',
          background: 'white',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
          marginBottom: '16px',
          position: 'relative',
          zIndex: 10
        }}>
          {/* SVG for connection lines - INSIDE CONTAINER */}
          <svg
            ref={svgRef}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
              zIndex: 5,
              overflow: 'visible'
            }}
          >
            {connectedModules.length > 0 && connectedModules.map((modId) => {
              const moduleElement = moduleRefs.current[modId];
              const buttonElement = generateButtonRef.current;
              
              if (!moduleElement || !buttonElement || !svgRef.current) {
                return null;
              }

              const svgRect = svgRef.current.getBoundingClientRect();
              const moduleRect = moduleElement.getBoundingClientRect();
              const buttonRect = buttonElement.getBoundingClientRect();
              const isLastModule = modId === connectedModules[connectedModules.length - 1];

              if (!isLastModule) return null;

              const startX = moduleRect.right - svgRect.left;
              const startY = moduleRect.top + moduleRect.height / 2 - svgRect.top;
              const endX = buttonRect.left + buttonRect.width / 2 - svgRect.left;
              const endY = buttonRect.top + buttonRect.height / 2 - svgRect.top;
              const midX = (startX + endX) / 2;
              const midY = (startY + endY) / 2;

              return (
                <g key={modId}>
                  <path
                    d={`M ${startX} ${startY} Q ${midX} ${midY + 15}, ${endX} ${endY}`}
                    stroke="#9ca3af"
                    strokeWidth="2"
                    fill="none"
                  />
                  <circle cx={endX} cy={endY} r="3" fill="#272727" />
                </g>
              );
            })}
          </svg>

          {/* Input Box */}
          <div style={{
            padding: '12px 16px',
            position: 'relative',
            zIndex: 20
          }}>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {/* Plus Button */}
              <div style={{ position: 'relative' }}>
                <button
  onClick={(e) => {
  const rect = e.currentTarget.getBoundingClientRect();
  setDropdownPosition({
    top: rect.top - 380, // Adjust based on dropdown height
    left: rect.left
  });
  setShowModuleDropdown(!showModuleDropdown);
  setAnimatePlus(true);
  setTimeout(() => setAnimatePlus(false), 400);
}}
                  style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    background: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s',
                    flexShrink: 0,
                    position: 'relative',
                    border: 'none'
                  }}
                >
                  <svg 
                    aria-hidden="true" 
                    xmlns="http://www.w3.org/2000/svg" 
                    width="20" 
                    height="20" 
                    fill="none" 
                    viewBox="0 0 24 24"
                    style={{
                      transform: animatePlus ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55)'
                    }}
                  >
                    <path stroke="#383838ff" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.4" d="M5 12h14m-7 7V5"/>
                  </svg>

                </button>
                
                  {showModuleDropdown && (
                    <div style={{
  position: 'fixed',
  bottom: 'auto',
  top: `85px`,
  left: `30px`,
                      background: 'white',
                      borderRadius: '12px',
                      boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.05), 0 4px 20px rgba(0, 0, 0, 0.1)',
                      border: '1px solid #e5e7eb',
                      padding: '8px',
                      minWidth: '280px',
                      zIndex: 99999,
                      animation: 'fadeIn 0.2s ease-out'
                    }}>
                      {/* First 4 modules in a 2x2 grid */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        gap: '4px',
                        marginBottom: '4px'
                      }}>
                        {modules.slice(0, 4).map(module => {
                          const Icon = module.icon;
                          const isSelected = connectedModules.includes(module.id);
                          return (
                            <button
                              key={module.id}
                              onClick={() => toggleModule(module.id)}
                              style={{
                                padding: '8px 10px',
                                background: isSelected ? '#f3f4f6' : 'transparent',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                transition: 'all 0.2s'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                              onMouseLeave={(e) => e.currentTarget.style.background = isSelected ? '#f3f4f6' : 'transparent'}
                            >
                              <div style={{
                                width: '24px',
                                height: '24px',
                                background: isSelected ? 'white' : '#f9fafb',
                                borderRadius: '5px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: `1px solid ${isSelected ? '#d1d5db' : '#e5e7eb'}`
                              }}>
                                <Icon size={13} color={module.color} />
                              </div>
                              <span style={{
                                fontSize: '12px',
                                fontWeight: '500',
                                color: '#1f2937'
                              }}>
                                {module.name}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                      
                      {/* Divider */}
                      <div style={{
                        height: '1px',
                        background: '#e5e7eb8e',
                        margin: '4px 0'
                      }} />
                      
                      {/* Last 2 modules (Traffic + Upload) in a single row */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        gap: '4px'
                      }}>
                        {modules.slice(4, 6).map(module => {
                          const Icon = module.icon;
                          const isSelected = connectedModules.includes(module.id);
                          return (
                            <button
                              key={module.id}
                              onClick={() => toggleModule(module.id)}
                              style={{
                                padding: '8px 10px',
                                background: isSelected ? '#f3f4f6' : 'transparent',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                transition: 'all 0.2s'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                              onMouseLeave={(e) => e.currentTarget.style.background = isSelected ? '#f3f4f6' : 'transparent'}
                            >
                              <div style={{
                                width: '24px',
                                height: '24px',
                                background: isSelected ? 'white' : '#f9fafb',
                                borderRadius: '5px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: `1px solid ${isSelected ? '#d1d5db' : '#e5e7eb'}`
                              }}>
                                <Icon size={13} color={module.color} />
                              </div>
                              <span style={{
                                fontSize: '12px',
                                fontWeight: '500',
                                color: '#1f2937'
                              }}>
                                {module.name}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
              </div>

              {/* Text Input */}
              <input
                type="text"
                value={promptText}
                onChange={(e) => setPromptText(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    setShowCreatePodcastModal(false);
                    handleGenerate();
                  }
                }}
                placeholder="Create podcasts on any topic..."
                style={{
                  flex: 1,
                  border: 'none',
                  outline: 'none',
                  fontSize: '14px',
                  color: '#1f2937',
                  background: 'transparent',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
                }}
              />

              {/* Send Button */}
              <button
                ref={generateButtonRef}
                onClick={() => {
                  setShowCreatePodcastModal(false);
                  handleGenerate();
                }}
                disabled={connectedModules.length === 0 && !promptText.trim()}
                style={{
                  width: '26px',
                  height: '26px',
                  borderRadius: '50%',
                  background: (connectedModules.length === 0 && !promptText.trim()) ? '#9ca3af' : '#272727',
                  border: 'none',
                  cursor: (connectedModules.length === 0 && !promptText.trim()) ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s',
                  flexShrink: 0,
                  position: 'relative',
                  zIndex: 30
                }}
              >
                <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24">
                  <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m9 5 7 7-7 7"/>
                </svg>
              </button>
            </div>
          </div>

          {/* Connected Modules Display */}
          {connectedModules.length > 0 && (
            <div style={{ 
              padding: '0px 16px 12px', 
              display: 'flex', 
              justifyContent: 'flex-start', 
              alignItems: 'center',
              gap: '0px',
              animation: 'fadeIn 0.3s ease-out',
              position: 'relative',
              zIndex: 15
            }}>
              {connectedModules.map((modId, index) => {
                const module = modules.find(m => m.id === modId);
                if (!module) return null;
                const Icon = module.icon;
                
                const showText = !shouldShowOnlyIcons && index === 0;
                const isUpload = modId === 'upload';
                const isYouTube = modId === 'youtube';

                const getFileIcon = () => {
                  if (!isUpload || !uploadedFileType) return null;
                  
                  if (uploadedFileType === 'PDF') {
                    return (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ef4444">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6" />
                      </svg>
                    );
                  } else if (uploadedFileType === 'DOCX') {
                    return (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2563eb">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9h1m-1 4h6m-6 4h6m2-10V7" />
                      </svg>
                    );
                  } else {
                    return (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b7280">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    );
                  }
                };
                
                return (
                  <React.Fragment key={modId}>
                    <div
                      ref={el => moduleRefs.current[modId] = el}
                      style={{
                        background: 'white',
                        border: `1.5px solid ${isYouTube ? '#FF0000' : '#e5e7eb'}`,
                        borderRadius: '22px',
                        padding: showText && !isYouTube ? '6px 14px' : '8px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        cursor: 'pointer',
                        position: 'relative',
                        touchAction: 'none',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
                      }}
                    >
                      {isYouTube && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setYoutubeVideoId(null);
                            setYoutubeVideoTitle(null);
                            setYoutubeVideoAnalysis(null);
                            setConnectedModules(prev => prev.filter(id => id !== 'youtube'));
                            setModules(prev => prev.filter(m => m.id !== 'youtube'));
                          }}
                          style={{
                            position: 'absolute',
                            top: '-6px',
                            right: '-6px',
                            width: '16px',
                            height: '16px',
                            borderRadius: '50%',
                            background: '#FF0000',
                            border: '1.5px solid white',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: 0,
                            transition: 'all 0.2s',
                            zIndex: 10
                          }}
                        >
                          <X size={10} color="white" strokeWidth={3} />
                        </button>
                      )}
                      
                      <div style={{
                        width: '14px',
                        height: '14px',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {isUpload && uploadedFileType ? (
                          getFileIcon()
                        ) : isYouTube ? (
                          <YouTubeIcon />
                        ) : (
                          <Icon size={14} color={module.color} />
                        )}
                      </div>
                      
                      {showText && !isYouTube && (
                        <span style={{ 
                          fontSize: '13px', 
                          fontWeight: '500', 
                          color: '#374151'
                        }}>
                          {isUpload && uploadedFileType ? uploadedFileType : module.name}
                        </span>
                      )}
                    </div>
                    
                    {index < connectedModules.length - 1 && (
                      <div style={{
                        width: '24px',
                        height: '2px',
                        background: '#d1d5db',
                        margin: '0 -1px'
                      }} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </div>
        {/* Host Selection Section */}
        <div style={{
          background: 'rgba(255, 255, 255, 1)',
          backdropFilter: 'blur(20px)',
          borderRadius: '20px',
          padding: '4px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(255, 255, 255, 1)',
          overflow: 'hidden',
          marginBottom: '20px',
          position: 'relative'
        }}>
          <div style={{
            background: `linear-gradient(${gradientAngle}deg, rgba(211, 238, 255, 1) 0%, rgba(255, 206, 215, 1) 53%, rgba(236, 241, 251, 0.3) 60%, #e9f9ffff 60%, #eaf2ffff 100%)`,
            border: '1px solid rgba(196, 210, 253, 0.5)',
            borderRadius: '16px',
            padding: '20px',
            position: 'relative',
            zIndex: 5,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px',
            transition: 'background 3s cubic-bezier(0.4, 0, 0.2, 1)',
            overflow: 'hidden'
          }}>

            {/* Content wrapper */}
            <div style={{ position: 'relative', zIndex: 10, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
              {/* Intro Music Toggle */}
              <div style={{
                position: 'absolute',
                top: '-8px',
                right: '-10px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: 'white',
                padding: '4px 10px',
                borderRadius: '20px',
                boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                zIndex: 20
              }}>
                <Music size={13} color="#4286cf" />
                <span style={{ fontSize: '12px', fontWeight: '500', color: '#1f2937' }}>
                  Podcast Intro music
                </span>
                <button
                  onClick={() => setIntroMusicEnabled(!introMusicEnabled)}
                  style={{
                    width: '36px',
                    height: '18px',
                    borderRadius: '10px',
                    background: introMusicEnabled ? '#363636' : '#d1d5db',
                    border: 'none',
                    cursor: 'pointer',
                    position: 'relative',
                    transition: 'all 0.2s',
                    padding: 0
                  }}
                >
                  <div style={{
                    width: '13px',
                    height: '13px',
                    borderRadius: '50%',
                    background: 'white',
                    position: 'absolute',
                    top: '2px',
                    left: introMusicEnabled ? '18px' : '2px',
                    transition: 'all 0.2s',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                  }} />
                </button>
              </div>

              {/* Host Display (when not editing) */}
              {!showHostEditor && (
                <>
                  <div style={{
                    background: 'white',
                    borderRadius: '20px',
                    padding: '8px 20px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    fontSize: '13px',
                    fontWeight: '500',
                    color: '#1f2937',
                    textAlign: 'center',
                    marginBottom: '12px',
                    marginTop: '65px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Radio size={16} color="#71b1f5" />
                      <span>{selectedGuest ? 'Hosts + Guest' : 'Default Hosts'}</span>
                    </div>
                    <div style={{
                      width: '1px',
                      height: '15px',
                      background: '#8a8a8a',
                      borderRadius: '2px'
                    }} />
                    <span style={{ fontWeight: '400' }}>
                      {selectedHosts.map(h => getHostName(h)).join(' & ')}
                      {selectedGuest && ` + ${getHostName(selectedGuest)}`}
                    </span>
                  </div>

                  {/* ✅ NEW: Style Display Container */}
                  <div style={{
background: 'rgba(255, 255, 255, 0.5)',
                    borderRadius: '20px',
                    padding: '8px 16px',
                    fontSize: '12px',
                    fontWeight: '500',
                    color: '#1f2937',
                    textAlign: 'center',
                    backdropFilter: 'blur(10px)',
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    justifyContent: 'center'
                  }}>
                    <span style={{ color: '##1c1c1c' }}>Style</span>
         -
                    <span style={{ 
                      color: '#3b82f6',
                      textTransform: 'capitalize'
                    }}>
                      {talkingStyle === 'casual' ? 'Casual' : 'Semi-Formal'}
                    </span>
                  </div>

                  <button
                    onClick={() => setShowHostEditor(true)}
                    style={{
                      width: '100%',
                      padding: '10px',
                      background: '#212121',
                      color: 'white',
                      border: 'none',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      maxWidth: '130px'
                    }}
                  >
                    Edit Hosts
                  </button>
                </>
              )}

              {/* Host Editor */}
              {showHostEditor && (
                <>
                  {/* Main Hosts Section */}
                  <div style={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    marginTop: '10px',
                  }}>
                                    <div style={{
                    width: '100%',
                    borderTop: '1px solid #e5e7eb',
                    paddingTop: '1px',
                    marginTop: '20px'
                  }}></div>
                    <div style={{
                      fontSize: '11px',
                      fontWeight: '600',
                      color: '#6b7280',
                      marginBottom: '4px',
                      textAlign: 'left',
                     marginTop: '20px',
                    }}>
                      Main Hosts (Select 2)
                    </div>
                    {[
                      { id: 'Host 1', name: 'Emma', desc: 'Warm and friendly voice' },
                      { id: 'Host 2', name: 'James', desc: 'Professional and clear' },
                      { id: 'Host 3', name: 'Jackie', desc: 'Energetic and upbeat' },
                      { id: 'Host 4', name: 'Kenny', desc: 'Calm and soothing' }
                    ].map(host => {
                      const isSelected = selectedHosts.includes(host.id);
                      const isGuest = selectedGuest === host.id;
                      const isDisabled = isGuest;
                      
                      return (
                        <button
                          key={host.id}
                          onClick={() => {
                            if (isDisabled) return;
                            
                            if (isSelected) {
                              setSelectedHosts(prev => prev.filter(h => h !== host.id));
                            } else {
                              if (selectedHosts.length >= 2) {
                                return;
                              }
                              setSelectedHosts(prev => [...prev, host.id]);
                            }
                          }}
                          disabled={isDisabled}
                          style={{
                            background: isSelected ? 'white' : isDisabled ? '#f3f4f6' : 'white',
                            border: isSelected ? '1.5px solid #4286cf' : '1px solid #e5e7eb',
                            borderRadius: '20px',
                            padding: '10px 16px',
                            marginTop: '8px',
                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                            textAlign: 'left',
                            transition: 'all 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                            opacity: isDisabled ? 0.5 : 1
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <Radio size={16} color="#71b1f5" />
                            <div>
                              <div style={{ fontSize: '13px', fontWeight: '600', color: '#1f2937' }}>
                                {host.name}
                              </div>
                              <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                                {host.desc}
                              </div>
                            </div>
                          </div>
                          {isSelected && (
                            <div style={{
                              width: '18px',
                              height: '18px',
                              borderRadius: '50%',
                              background: '#000000',
                              color: 'white',
                              fontSize: '10px',
                              fontWeight: '700',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              {selectedHosts.indexOf(host.id) + 1}
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>

                  {/* ✅ NEW: Talking Style Section */}
                  <div style={{
                    width: '100%',
                    borderTop: '1px solid #e5e7eb',
                    paddingTop: '16px',
                    marginTop: '8px'
                  }}>
                    <div style={{
                      fontSize: '11px',
                      fontWeight: '600',
                      color: '#6b7280',
                      marginBottom: '12px',
                      textAlign: 'left'
                    }}>
                      Talking Style
                    </div>

                    {/* Casual Style Option */}
                    <button
                      onClick={() => setTalkingStyle('casual')}
                      style={{
                        background: talkingStyle === 'casual' ? 'white' : 'white',
                        border: talkingStyle === 'casual' ? '1.5px solid #3b82f6' : '1px solid #e5e7eb',
                        borderRadius: '16px',
                        padding: '12px 16px',
                        cursor: 'pointer',
                        textAlign: 'left',
                        transition: 'all 0.2s',
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                        width: '100%',
                        marginBottom: '8px'
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{ 
                          fontSize: '13px', 
                          fontWeight: '600', 
                          color: '#1f2937',
                          marginBottom: '4px'
                        }}>
                          Casual
                        </div>
                        <div style={{ 
                          fontSize: '11px', 
                          color: '#6b7280',
                          lineHeight: '1.4'
                        }}>
                          Natural fillers like "um" and "like", friendly tone
                        </div>
                      </div>
                      {talkingStyle === 'casual' && (
                        <div style={{
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          background: '#3b82f6',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          marginLeft: '12px'
                        }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      )}
                    </button>

                    {/* Semi-Formal Style Option */}
                    <button
                      onClick={() => setTalkingStyle('semi-formal')}
                      style={{
                        background: talkingStyle === 'semi-formal' ? 'white' : 'white',
                        border: talkingStyle === 'semi-formal' ? '1.5px solid #3b82f6' : '1px solid #e5e7eb',
                        borderRadius: '16px',
                        padding: '12px 16px',
                        cursor: 'pointer',
                        textAlign: 'left',
                        transition: 'all 0.2s',
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                        width: '100%'
                      }}
                    >
                      <div style={{ flex: 1 }}>
              <div style={{ 
                          fontSize: '13px', 
                          fontWeight: '600', 
                          color: '#1f2937',
                          marginBottom: '4px'
                        }}>
                          Semi-Formal
                        </div>
                        <div style={{ 
                          fontSize: '11px', 
                          color: '#6b7280',
                          lineHeight: '1.4'
                        }}>
Crisp and polished, great for informational content
                        </div>
                      </div>
                      {talkingStyle === 'semi-formal' && (
                        <div style={{
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          background: '#3b82f6',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          marginLeft: '12px'
                        }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      )}
                    </button>
                  </div>
                  {/* Guest Section */}
                  <div style={{
                    width: '100%',
                    borderTop: '1px solid #e5e7eb',
                    paddingTop: '16px',
                    marginTop: '8px'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '12px'
                    }}>
                      <div style={{
                        fontSize: '11px',
                        fontWeight: '600',
                        color: '#6b7280'
                      }}>
                        Guest Speaker (Optional)
                      </div>
                      {selectedGuest && (
                        <button
                          onClick={() => setSelectedGuest(null)}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: '#ef4444',
                            fontSize: '11px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            padding: '4px 8px'
                          }}
                        >
                          Remove Guest
                        </button>
                      )}
                    </div>

                    {!selectedGuest && (
                      <div style={{
                        background: 'rgba(59, 130, 246, 0.05)',
                        border: '1px dashed #93c5fd',
                        borderRadius: '12px',
                        padding: '12px',
                        marginBottom: '12px',
                        textAlign: 'center'
                      }}>
                        <div style={{
                          fontSize: '11px',
                          color: '#6b7280',
                          lineHeight: '1.4'
                        }}>
                          Add a third voice to join mid-conversation
                        </div>
                      </div>
                    )}

                    {[
                      { id: 'Host 1', name: 'Emma', desc: 'Warm and friendly voice' },
                      { id: 'Host 2', name: 'James', desc: 'Professional and clear' },
                      { id: 'Host 3', name: 'Jackie', desc: 'Energetic and upbeat' },
                      { id: 'Host 4', name: 'Kenny', desc: 'Calm and soothing' }
                    ]
                      .filter(host => !selectedHosts.includes(host.id))
                      .map(host => {
                        const isSelected = selectedGuest === host.id;
                        
                        return (
                          <button
                            key={host.id}
                            onClick={() => {
                              setSelectedGuest(isSelected ? null : host.id);
                            }}
                            style={{
                              background: isSelected ? 'white' : 'white',
                              border: isSelected ? '1.5px solid #8b5cf6' : '1px solid #e5e7eb',
                              borderRadius: '20px',
                              padding: '10px 16px',
                              marginTop: '8px',
                              cursor: 'pointer',
                              textAlign: 'left',
                              transition: 'all 0.2s',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                              width: '100%'
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <div style={{
                                width: '32px',
                                height: '32px',
                                borderRadius: '50%',
                                background: isSelected ? '#f3e8ff' : '#f9fafb',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                <Radio size={14} color="#8b5cf6" />
                              </div>
                              <div>
                                <div style={{ fontSize: '13px', fontWeight: '600', color: '#1f2937' }}>
                                  {host.name}
                                </div>
                                <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                                  {host.desc}
                                </div>
                              </div>
                            </div>
                            {isSelected && (
                              <div style={{
                                background: '#8b5cf6',
                                color: 'white',
                                fontSize: '9px',
                                fontWeight: '600',
                                padding: '4px 8px',
                                borderRadius: '6px'
                              }}>
                                GUEST
                              </div>
                            )}
                          </button>
                        );
                      })}
                  </div>


                  {/* Confirm Button */}
                  <button
                    onClick={() => setShowHostEditor(false)}
                    disabled={selectedHosts.length !== 2}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: selectedHosts.length === 2 ? '#1a1a1a' : '#9ca3af',
                      color: 'white',
                      border: 'none',
                      borderRadius: '20px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: selectedHosts.length === 2 ? 'pointer' : 'not-allowed',
                      transition: 'all 0.2s',
                      maxWidth: '200px',
                      marginTop: '12px'
                    }}
                  >
                    Confirm Settings
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
)}

{/* ✅ MAP VIEW MODAL */}
{showMapView && selectedRouteForMap && (
  <div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 10000,
    background: 'rgba(0, 0, 0, 0.95)',
    display: 'flex',
    flexDirection: 'column'
  }}>
    {/* Header */}
    <div style={{
      padding: '20px',
      background: 'linear-gradient(180deg, rgba(0,0,0,0.9) 0%, transparent 100%)',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 10,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    }}>
      <div>
        <h2 style={{ 
          fontSize: '18px', 
          fontWeight: '600', 
          color: 'white', 
          margin: 0,
          marginBottom: '4px'
        }}>
          {selectedRouteForMap.name}
        </h2>
        <div style={{
          fontSize: '12px',
          color: '#9ca3af',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <MapPin size={14} />
          <span>Live Route Preview</span>
        </div>
      </div>

      <button
        onClick={closeMapView}
        style={{
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: '50%',
          width: '40px',
          height: '40px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.2s'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
        }}
      >
        <X size={20} color="white" />
      </button>
    </div>

    {/* Map Container */}
    <div 
      ref={mapContainer} 
      style={{ 
        flex: 1,
        width: '100%',
        height: '100%'
      }} 
    />

    {/* Route Info Footer */}
    <div style={{
      padding: '20px',
      background: 'linear-gradient(0deg, rgba(0,0,0,0.9) 0%, transparent 100%)',
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      zIndex: 10
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '16px'
      }}>
        {/* Origin */}
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '10px', color: '#10b981', fontWeight: '600', marginBottom: '4px' }}>
            START
          </div>
          <div style={{ fontSize: '12px', color: 'white', lineHeight: '1.4' }}>
            {selectedRouteForMap.origin}
          </div>
        </div>

        {/* Arrow */}
        <div style={{
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          background: 'rgba(59, 130, 246, 0.2)',
          border: '2px solid #3b82f6',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
          </svg>
        </div>

        {/* Destination */}
        <div style={{ flex: 1, textAlign: 'right' }}>
          <div style={{ fontSize: '10px', color: '#ef4444', fontWeight: '600', marginBottom: '4px' }}>
            DESTINATION
          </div>
          <div style={{ fontSize: '12px', color: 'white', lineHeight: '1.4' }}>
            {selectedRouteForMap.destination}
          </div>
        </div>
      </div>

      {/* Replay Button */}
      {!isMapAnimating && (
        <button
          onClick={() => animateMapRoute(selectedRouteForMap)}
          style={{
            width: '100%',
            marginTop: '16px',
            padding: '12px',
            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            border: 'none',
            borderRadius: '22px',
            color: 'white',
            fontSize: '13px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 8px 20px rgba(59, 130, 246, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          Replay Animation
        </button>
      )}
    </div>
  </div>
)}
{showSelectedNewsStation && selectedNewsStationData && (
  <SelectedNewsStation
    userName={userName}
    selectedSources={selectedNewsStationData.selectedSources}
    newsData={selectedNewsStationData.newsData}
    onClose={() => {
      setShowSelectedNewsStation(false);
      setSelectedNewsStationData(null);
    }}
  />
)}

      </div>
    </div>
      </>
  );
}